import { useRef, useEffect, useCallback, useState } from 'react';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { CAPABILITIES, CAMERA_WAYPOINTS, PHASE_INFO, TIMELINE, AUDIO_OFFSET, TRANSITIONS } from './BrainCinematic';

// ═══════════════════════════════════════════════════════════
// BRAIN GEOMETRY
// ═══════════════════════════════════════════════════════════
function createBrainGeometry() {
  const geo = new THREE.SphereGeometry(1.3, 48, 36);
  const pos = geo.attributes.position;
  for (let i = 0; i < pos.count; i++) {
    let x = pos.getX(i), y = pos.getY(i), z = pos.getZ(i);
    x *= 1.05; y *= 0.82; z *= 1.12;
    const topness = Math.max(0, y) / 1.1;
    y -= 0.35 * topness * topness * Math.exp(-(x * x) / 0.06);
    const lowness = Math.max(0, -y - 0.1);
    x += Math.sign(x) * lowness * (Math.abs(x) / 1.4) * 0.25;
    z += Math.max(0, z / 1.5) * 0.08 * Math.max(0, y + 0.3);
    z -= Math.max(0, -z / 1.5) * 0.06 * Math.exp(-y * y / 0.3);
    if (y < -0.45) y = -0.45 - (y + 0.45) * 0.3;
    const noise = Math.sin(x * 9 + z * 6) * 0.025 + Math.cos(y * 11 + x * 7) * 0.02 + Math.sin(z * 13 + y * 9 + x * 5) * 0.015;
    pos.setXYZ(i, x + noise * 0.8, y + noise * 0.4, z + noise * 0.8);
  }
  pos.needsUpdate = true;
  geo.computeVertexNormals();
  return geo;
}

// ═══════════════════════════════════════════════════════════
// LAYER 1 — BRAIN SHELL
// ═══════════════════════════════════════════════════════════
function createBrainShell(scene) {
  const group = new THREE.Group();
  const g1 = createBrainGeometry();
  const wireframe = new THREE.Mesh(g1, new THREE.MeshBasicMaterial({ color: 0x4a7aff, wireframe: true, transparent: true, opacity: 0.06 }));
  group.add(wireframe);
  const g2 = createBrainGeometry();
  const glow = new THREE.Mesh(g2, new THREE.MeshBasicMaterial({ color: 0x1e3a8a, transparent: true, opacity: 0.02, side: THREE.BackSide, blending: THREE.AdditiveBlending }));
  glow.scale.setScalar(1.02);
  group.add(glow);
  scene.add(group);

  let flashIntensity = 0;
  let flashColor = null;

  return {
    group,
    flash(color) { flashIntensity = 1.0; flashColor = color ? new THREE.Color(color) : null; },
    update(t, intensity) {
      group.scale.setScalar(1 + Math.sin(t * 0.7) * 0.015 * (1 + intensity));
      flashIntensity *= 0.88; // Fast decay
      if (flashIntensity < 0.02) flashIntensity = 0;
      wireframe.material.opacity = 0.04 + intensity * 0.02 + Math.sin(t * 1.2) * 0.01 + flashIntensity * 0.15;
      if (flashColor && flashIntensity > 0.05) {
        const mixed = new THREE.Color(0x4a7aff).lerp(flashColor, flashIntensity * 0.35);
        wireframe.material.color.copy(mixed);
      } else {
        wireframe.material.color.setHex(0x4a7aff);
      }
      glow.material.opacity = 0.01 + intensity * 0.01 + flashIntensity * 0.04;
    },
    dispose() { scene.remove(group); g1.dispose(); g2.dispose(); },
  };
}

// ═══════════════════════════════════════════════════════════
// LAYER 1b — INTERNAL SYNAPSES
// ═══════════════════════════════════════════════════════════
function createSynapses(scene) {
  const group = new THREE.Group();
  const synapses = [];

  function rndBrainPt() {
    for (let t = 0; t < 20; t++) {
      const x = (Math.random() - 0.5) * 2, y = (Math.random() - 0.5) * 1.5, z = (Math.random() - 0.5) * 2.2;
      if (Math.sqrt(x * x / 1.2 + y * y / 0.75 + z * z / 1.4) < 1) return new THREE.Vector3(x, y, z);
    }
    return new THREE.Vector3();
  }

  for (let i = 0; i < 0; i++) {
    const start = rndBrainPt(), end = rndBrainPt();
    const m1 = start.clone().lerp(end, 0.33).add(new THREE.Vector3((Math.random() - 0.5) * 0.4, (Math.random() - 0.5) * 0.3, (Math.random() - 0.5) * 0.4));
    const m2 = start.clone().lerp(end, 0.66).add(new THREE.Vector3((Math.random() - 0.5) * 0.4, (Math.random() - 0.5) * 0.3, (Math.random() - 0.5) * 0.4));
    const curve = new THREE.CatmullRomCurve3([start, m1, m2, end]);
    const hue = 0.55 + Math.random() * 0.15;
    const color = new THREE.Color().setHSL(hue, 0.7, 0.6);
    const line = new THREE.Line(new THREE.BufferGeometry().setFromPoints(curve.getPoints(40)), new THREE.LineBasicMaterial({ color, transparent: true, opacity: 0, blending: THREE.AdditiveBlending }));
    group.add(line);
    const pGeo = new THREE.BufferGeometry();
    pGeo.setAttribute('position', new THREE.BufferAttribute(new Float32Array(3), 3));
    const particle = new THREE.Points(pGeo, new THREE.PointsMaterial({ color, size: 0.06, transparent: true, opacity: 0, blending: THREE.AdditiveBlending, sizeAttenuation: true }));
    group.add(particle);
    synapses.push({ line, particle, curve, phase: Math.random() * Math.PI * 2, speed: 0.3 + Math.random() * 0.5, fireTime: 0, firing: false });
  }
  scene.add(group);

  return {
    group,
    update(t, intensity) {
      group.scale.setScalar(1 + Math.sin(t * 0.7) * 0.015 * (1 + intensity));
      synapses.forEach(s => {
        if (Math.sin(t * s.speed + s.phase) > 0.7 && !s.firing) { s.firing = true; s.fireTime = t; }
        if (s.firing) {
          const el = t - s.fireTime, dur = 1.5 / s.speed;
          if (el < dur) {
            const prog = el / dur;
            const op = Math.min(Math.min(1, prog * 4), Math.max(0, 1 - (prog - 0.5) * 2));
            s.line.material.opacity = op * (0.15 + intensity * 0.2);
            const pt = s.curve.getPointAt(Math.min(prog, 1));
            const pa = s.particle.geometry.attributes.position;
            pa.array[0] = pt.x; pa.array[1] = pt.y; pa.array[2] = pt.z;
            pa.needsUpdate = true;
            s.particle.material.opacity = op * 0.9;
          } else { s.firing = false; s.line.material.opacity = 0; s.particle.material.opacity = 0; }
        }
      });
    },
    dispose() { scene.remove(group); },
  };
}

// ═══════════════════════════════════════════════════════════
// LAYER 2 — SOURCE ORBITS
// ═══════════════════════════════════════════════════════════
function createSourceOrbits(scene, sourceList) {
  const group = new THREE.Group();
  const PC = 0;
  const count = sourceList.length || 1;
  const GRAY = 0x555566;

  const sourceObjs = sourceList.map((s, idx) => {
    const orbit = 3.2 + idx * (3.0 / Math.max(count - 1, 1));
    const isActive = s.active !== false;
    const speed = isActive ? 0.08 + idx * 0.02 : 0.025 + idx * 0.006;
    const tiltDeg = ((idx % 5) - 2) * 0.08;
    const hex = isActive ? new THREE.Color(s.color).getHex() : GRAY;

    const sub = new THREE.Group();
    sub.rotation.y = (idx / count) * Math.PI * 2;

    const ringPts = [];
    for (let i = 0; i <= 200; i++) {
      const a = (i / 200) * Math.PI * 2;
      ringPts.push(new THREE.Vector3(Math.cos(a) * orbit, Math.sin(a) * tiltDeg * orbit * 0.15, Math.sin(a) * orbit));
    }
    sub.add(new THREE.Line(new THREE.BufferGeometry().setFromPoints(ringPts), new THREE.LineBasicMaterial({ color: hex, transparent: true, opacity: isActive ? 0.2 : 0.06, blending: THREE.AdditiveBlending })));

    const iconR = isActive ? 0.16 : 0.09;
    const icon = new THREE.Mesh(new THREE.SphereGeometry(iconR, 16, 16), new THREE.MeshBasicMaterial({ color: hex, transparent: true, opacity: isActive ? 0.9 : 0.25, blending: THREE.AdditiveBlending }));
    sub.add(icon);
    const halo = new THREE.Mesh(new THREE.SphereGeometry(iconR * 2, 12, 12), new THREE.MeshBasicMaterial({ color: hex, transparent: true, opacity: isActive ? 0.1 : 0.03, blending: THREE.AdditiveBlending }));
    sub.add(halo);

    const pPos = new Float32Array(PC * 3);
    const pGeo = new THREE.BufferGeometry();
    pGeo.setAttribute('position', new THREE.BufferAttribute(pPos, 3));
    const particles = new THREE.Points(pGeo, new THREE.PointsMaterial({ color: hex, size: 0.04, transparent: true, opacity: 0.7, blending: THREE.AdditiveBlending, sizeAttenuation: true }));
    sub.add(particles);
    group.add(sub);

    const offsets = Array.from({ length: PC }, (_, i) => ({ off: (i / PC) * Math.PI * 2, spd: 0.2 + Math.random() * 0.3 }));
    return { icon, halo, particles, offsets, orbit, speed, tilt: tiltDeg, active: isActive, key: s.key };
  });

  scene.add(group);
  group.position.y = 3.0;

  return {
    group, sourceObjs,
    update(t, intensity) {
      group.rotation.y = 0;
      sourceObjs.forEach(so => {
        const a = t * so.speed;
        so.icon.position.set(Math.cos(a) * so.orbit, Math.sin(a) * so.tilt * so.orbit * 0.15, Math.sin(a) * so.orbit);
        so.halo.position.copy(so.icon.position);
        if (so.active) {
          so.halo.material.opacity = 0.08 + Math.sin(t * 2.5) * 0.03;
        } else {
          so.halo.material.opacity = 0.02;
        }
        so.particles.visible = so.active;
        if (so.active) {
          const attr = so.particles.geometry.attributes.position;
          for (let i = 0; i < PC; i++) {
            const p = so.offsets[i];
            const angle = t * p.spd + p.off;
            const prog = (t * 0.08 * p.spd + p.off / (Math.PI * 2)) % 1;
            const r = so.orbit * (1 - prog * 0.75);
            attr.array[i * 3] = Math.cos(angle) * r;
            attr.array[i * 3 + 1] = Math.sin(angle) * so.tilt * r * 0.15;
            attr.array[i * 3 + 2] = Math.sin(angle) * r;
          }
          attr.needsUpdate = true;
        }
      });
    },
    dispose() { scene.remove(group); },
  };
}

// ═══════════════════════════════════════════════════════════
// LAYER 4 — LIVING VEINS (1 per source, double tube)
// Inner tube (body) + Outer glow tube (halo)
// Breathes, swells on pulse, thickness ∝ data volume
// ═══════════════════════════════════════════════════════════
function createSourceVeins(scene, sourceList) {
  const group = new THREE.Group();
  const FLOW_PC = 0;
  const TUBE_SEGS = 24;
  const RADIAL_SEGS = 6;
  const activeSources = sourceList.filter(s => s.active !== false);
  const maxCount = Math.max(1, ...activeSources.map(s => s.count || 1));

  let frameCount = 0;
  const REBUILD_EVERY = 6;

  const veins = activeSources.map((s) => {
    const color = new THREE.Color(s.color);
    const veinIntensity = Math.sqrt((s.count || 1) / maxCount);
    const baseRadius = 0.012 + veinIntensity * 0.03; // 0.012 min → 0.042 max
    const glowRadius = baseRadius * 2.5;

    // Inner tube mesh (geometry swapped each rebuild)
    const innerMat = new THREE.MeshBasicMaterial({
      color, transparent: true, opacity: 0.25, blending: THREE.AdditiveBlending,
    });
    const innerTube = new THREE.Mesh(new THREE.BufferGeometry(), innerMat);
    group.add(innerTube);

    // Outer glow tube
    const outerMat = new THREE.MeshBasicMaterial({
      color, transparent: true, opacity: 0.06, blending: THREE.AdditiveBlending,
    });
    const outerTube = new THREE.Mesh(new THREE.BufferGeometry(), outerMat);
    group.add(outerTube);

    // Flow particles
    const fpPos = new Float32Array(FLOW_PC * 3);
    const fpGeo = new THREE.BufferGeometry();
    fpGeo.setAttribute('position', new THREE.BufferAttribute(fpPos, 3));
    const flow = new THREE.Points(fpGeo, new THREE.PointsMaterial({
      color, size: 0.05, transparent: true, opacity: 0.7,
      blending: THREE.AdditiveBlending, sizeAttenuation: true,
    }));
    group.add(flow);

    // Pulse sphere (travels along vein on pulse)
    const pulseSphere = new THREE.Mesh(
      new THREE.SphereGeometry(0.1, 10, 10),
      new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0, blending: THREE.AdditiveBlending }),
    );
    pulseSphere.visible = false;
    group.add(pulseSphere);

    // Pulse glow (larger, softer halo around pulse sphere)
    const pulseGlow = new THREE.Mesh(
      new THREE.SphereGeometry(0.25, 8, 8),
      new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0, blending: THREE.AdditiveBlending }),
    );
    pulseGlow.visible = false;
    group.add(pulseGlow);

    return {
      innerTube, outerTube, innerMat, outerMat,
      flow, fpPos,
      pulseSphere, pulseGlow,
      color, veinIntensity, baseRadius, glowRadius,
      curve: null,
      pulseActive: false, pulseStart: 0,
      key: s.key,
    };
  });

  scene.add(group);

  const PULSE_DURATION = 1.8;

  return {
    group, veins,

    triggerPulse(sourceKey) {
      const v = veins.find(v => v.key === sourceKey);
      if (!v) return;
      v.pulseActive = true;
      v.pulseStart = -1;
    },

    update(t, intensity, sourceObjs) {
      frameCount++;
      const shouldRebuild = frameCount % REBUILD_EVERY === 0;

      veins.forEach((v) => {
        const so = sourceObjs.find(o => o.key === v.key);
        if (!so) return;

        // Source world position
        const wp = new THREE.Vector3();
        so.icon.getWorldPosition(wp);
        const target = new THREE.Vector3(0, 1.5, 0);

        // Curved path: source → arc midpoint → Workers
        const mid = wp.clone().lerp(target, 0.5);
        const perp = new THREE.Vector3(-wp.z, 0, wp.x).normalize();
        mid.addScaledVector(perp, wp.distanceTo(target) * 0.1);
        v.curve = new THREE.CatmullRomCurve3([wp, mid, target]);

        // ── Rebuild tube geometry every N frames ──
        if (shouldRebuild && v.curve) {
          // Breathing oscillation
          const breathe = 1 + Math.sin(t * 1.5) * 0.12;
          // Pulse swell (entire vein thickens during pulse)
          const swell = v.pulseActive ? 1.8 : 1.0;
          const innerR = v.baseRadius * breathe * swell;
          const outerR = v.glowRadius * breathe * swell;

          // Swap inner tube geometry
          v.innerTube.geometry.dispose();
          v.innerTube.geometry = new THREE.TubeGeometry(v.curve, TUBE_SEGS, innerR, RADIAL_SEGS, false);

          // Swap outer glow geometry
          v.outerTube.geometry.dispose();
          v.outerTube.geometry = new THREE.TubeGeometry(v.curve, TUBE_SEGS, outerR, RADIAL_SEGS, false);
        }

        // ── Opacity based on volume + pulse ──
        const baseInnerOp = 0.18 + v.veinIntensity * 0.15;
        const baseOuterOp = 0.04 + v.veinIntensity * 0.04;
        const pulseOp = v.pulseActive ? 0.25 : 0;
        v.innerMat.opacity = baseInnerOp + intensity * 0.05 + pulseOp;
        v.outerMat.opacity = baseOuterOp + intensity * 0.02 + pulseOp * 0.3;

        // ── Flow particles along curve ──
        for (let i = 0; i < FLOW_PC; i++) {
          const prog = ((t * (0.12 + v.veinIntensity * 0.08) + i / FLOW_PC) % 1);
          if (v.curve) {
            const pt = v.curve.getPointAt(prog);
            v.fpPos[i * 3] = pt.x; v.fpPos[i * 3 + 1] = pt.y; v.fpPos[i * 3 + 2] = pt.z;
          }
        }
        v.flow.geometry.attributes.position.needsUpdate = true;

        // ── Pulse travel animation ──
        if (v.pulseActive) {
          if (v.pulseStart < 0) v.pulseStart = t;
          const elapsed = t - v.pulseStart;
          const prog = elapsed / PULSE_DURATION;

          if (prog > 1) {
            v.pulseActive = false;
            v.pulseSphere.visible = false;
            v.pulseGlow.visible = false;
          } else if (v.curve) {
            const pt = v.curve.getPointAt(Math.min(prog, 1));

            // Pulse sphere — shrinks slightly as it approaches core
            v.pulseSphere.position.copy(pt);
            v.pulseSphere.material.opacity = 0.95 * (1 - prog * 0.3);
            v.pulseSphere.scale.setScalar(1.2 + (1 - prog) * 0.8);
            v.pulseSphere.visible = true;

            // Glow around pulse sphere
            v.pulseGlow.position.copy(pt);
            v.pulseGlow.material.opacity = 0.25 * (1 - prog * 0.5);
            v.pulseGlow.scale.setScalar(1 + (1 - prog) * 1.2);
            v.pulseGlow.visible = true;
          }
        }
      });
    },

    dispose() {
      veins.forEach(v => {
        v.innerTube.geometry.dispose();
        v.outerTube.geometry.dispose();
      });
      scene.remove(group);
    },
  };
}

// ═══════════════════════════════════════════════════════════
// LAYER 3 — CONSTELLATION (far background)
// ═══════════════════════════════════════════════════════════
function createConstellation(scene) {
  const group = new THREE.Group();
  scene.add(group);
  return { group, update() {}, dispose() { scene.remove(group); } };
}


// ═══════════════════════════════════════════════════════════
// HEMISPHERE LEFT — MÉMOIRE & SAVOIR
// ═══════════════════════════════════════════════════════════
function createLeftHemisphere(scene) {
  const group = new THREE.Group();

  // Background sphere — visible translucent blue zone
  const bgSphere = new THREE.Mesh(
    new THREE.SphereGeometry(0.6, 24, 24),
    new THREE.MeshBasicMaterial({ color: 0x3b82f6, transparent: true, opacity: 0.06, blending: THREE.AdditiveBlending, side: THREE.DoubleSide, depthWrite: false })
  );
  bgSphere.position.set(-0.8, 0.1, 0);
  bgSphere.renderOrder = 0;
  group.add(bgSphere);

  // Outer ring to delineate the zone
  const ringPts = [];
  for (let i = 0; i <= 80; i++) {
    const a = (i / 80) * Math.PI * 2;
    ringPts.push(new THREE.Vector3(-0.8 + Math.cos(a) * 0.6, 0.1 + Math.sin(a) * 0.55, Math.sin(a * 0.5) * 0.15));
  }
  group.add(new THREE.Line(new THREE.BufferGeometry().setFromPoints(ringPts), new THREE.LineBasicMaterial({ color: 0x60a5fa, transparent: true, opacity: 0.08, blending: THREE.AdditiveBlending })));

  const kTypes = [
    { label: 'PROMESSE', color: 0xfbbf24, r: 0.45, speed: 3.0 },
    { label: 'BLOCAGE', color: 0xef4444, r: 0.45, speed: 2.5 },
    { label: 'FAIT', color: 0x22d3ee, r: 0.45, speed: 1.8 },
    { label: 'DECISION', color: 0xc084fc, r: 0.45, speed: 2.0 },
  ];
  const CENTER = { x: -0.8, y: 0.1, z: 0 };
  const clusters = kTypes.map(k => {
    const wire = new THREE.Mesh(
      new THREE.IcosahedronGeometry(k.r, 2),
      new THREE.MeshBasicMaterial({ color: k.color, wireframe: true, transparent: true, opacity: 0 })
    );
    wire.position.set(CENTER.x, CENTER.y, CENTER.z);
    wire.renderOrder = 2;
    group.add(wire);
    const core = new THREE.Mesh(
      new THREE.IcosahedronGeometry(k.r * 0.92, 2),
      new THREE.MeshLambertMaterial({ color: k.color, transparent: true, opacity: 0, side: THREE.DoubleSide, emissive: k.color, emissiveIntensity: 0.3 })
    );
    core.position.set(CENTER.x, CENTER.y, CENTER.z);
    core.renderOrder = 1;
    group.add(core);
    const glow = new THREE.Mesh(
      new THREE.IcosahedronGeometry(k.r * 1.25, 1),
      new THREE.MeshBasicMaterial({ color: k.color, wireframe: true, transparent: true, opacity: 0, blending: THREE.AdditiveBlending })
    );
    glow.position.set(CENTER.x, CENTER.y, CENTER.z);
    glow.renderOrder = 3;
    group.add(glow);
    return { wire, core, glow, ...k };
  });

  scene.add(group);
  let flashAmount = 0;
  return {
    group, clusters,
    flash() { flashAmount = 1.0; },
    update(t, intensity) {
      // Flash decay
      if (flashAmount > 0) {
        flashAmount *= 0.94;
        if (flashAmount < 0.01) flashAmount = 0;
      }
      group.scale.setScalar(1 + Math.sin(t * 0.7) * 0.015 * (1 + intensity));
      bgSphere.material.opacity = 0.04 + Math.sin(t * 0.8) * 0.02 + flashAmount * 0.15;
      const CYCLE = 3, FADE = 0.6, n = clusters.length;
      const ct = t % (CYCLE * n), ai = Math.floor(ct / CYCLE), pp = ct % CYCLE;
      const prev = (ai - 1 + n) % n;
      clusters.forEach((c, i) => {
        const active = i === ai;
        let wO = active ? 1.0 : 0, cO = active ? 0.55 : 0, gO = active ? 0.25 : 0, sc = active ? 1.0 : 0;
        if (pp < FADE) {
          const f = pp / FADE;
          if (active) { wO = 1.0 * f; cO = 0.55 * f; gO = 0.25 * f; sc = 0.3 + f * 0.7; }
          else if (i === prev) { wO = 1.0 * (1 - f); cO = 0.55 * (1 - f); gO = 0.25 * (1 - f); sc = 0.3 + (1 - f) * 0.7; }
        }
        c.wire.material.opacity = wO; c.core.material.opacity = cO;
        c.glow.material.opacity = gO;
        const s = Math.max(sc, 0.001);
        c.wire.scale.setScalar(s); c.core.scale.setScalar(s); c.glow.scale.setScalar(s);
        c.wire.rotation.y = t * (0.3 + i * 0.12); c.wire.rotation.x = t * 0.15;
        c.glow.rotation.y = -t * 0.1; c.glow.rotation.z = t * 0.08;
      });
    },
    dispose() { scene.remove(group); },
  };
}

// ═══════════════════════════════════════════════════════════
// HEMISPHERE RIGHT — INTELLIGENCE & COMPORTEMENT
// ═══════════════════════════════════════════════════════════
function createRightHemisphere(scene) {
  const group = new THREE.Group();
  const CX = 0.8, CY = 0.15, CZ = 0;

  // Background sphere — visible translucent violet zone
  const bgSphere = new THREE.Mesh(
    new THREE.SphereGeometry(0.55, 24, 24),
    new THREE.MeshBasicMaterial({ color: 0x8b5cf6, transparent: true, opacity: 0.06, blending: THREE.AdditiveBlending, side: THREE.DoubleSide, depthWrite: false })
  );
  bgSphere.position.set(CX, CY, CZ);
  bgSphere.renderOrder = 0;
  group.add(bgSphere);

  // Central GPT core + halo
  const coreMat = new THREE.MeshBasicMaterial({ color: 0xc084fc, transparent: true, opacity: 0.3, blending: THREE.AdditiveBlending });
  const core = new THREE.Mesh(new THREE.SphereGeometry(0.12, 14, 14), coreMat);
  core.position.set(CX, CY, CZ);
  group.add(core);

  const hubHalo = new THREE.Mesh(
    new THREE.SphereGeometry(0.18, 10, 10),
    new THREE.MeshBasicMaterial({ color: 0x8b5cf6, transparent: true, opacity: 0.06, blending: THREE.AdditiveBlending })
  );
  hubHalo.position.set(CX, CY, CZ);
  group.add(hubHalo);

  // 4 Intelligence clusters — all at zone center, cycle one at a time
  const iTypes = [
    { label: 'COMPREHENSION', color: 0xf472b6, r: 0.42, speed: 2.2 },
    { label: 'RAISONNEMENT', color: 0x67e8f9, r: 0.42, speed: 1.8 },
    { label: 'RESOLUTION', color: 0xfb923c, r: 0.42, speed: 2.5 },
    { label: 'ADAPTATION', color: 0xfde68a, r: 0.42, speed: 2.0 },
  ];
  const clusters = iTypes.map(k => {
    const wire = new THREE.Mesh(
      new THREE.IcosahedronGeometry(k.r, 2),
      new THREE.MeshBasicMaterial({ color: k.color, wireframe: true, transparent: true, opacity: 0 })
    );
    wire.position.set(CX, CY, CZ);
    wire.renderOrder = 2;
    group.add(wire);
    const coreN = new THREE.Mesh(
      new THREE.IcosahedronGeometry(k.r * 0.92, 2),
      new THREE.MeshLambertMaterial({ color: k.color, transparent: true, opacity: 0, side: THREE.DoubleSide, emissive: k.color, emissiveIntensity: 0.3 })
    );
    coreN.position.set(CX, CY, CZ);
    coreN.renderOrder = 1;
    group.add(coreN);
    const glow = new THREE.Mesh(
      new THREE.IcosahedronGeometry(k.r * 1.25, 1),
      new THREE.MeshBasicMaterial({ color: k.color, wireframe: true, transparent: true, opacity: 0, blending: THREE.AdditiveBlending })
    );
    glow.position.set(CX, CY, CZ);
    glow.renderOrder = 3;
    group.add(glow);
    return { wire, core: coreN, glow, ...k };
  });

  scene.add(group);
  let flashAmount = 0;
  return {
    group, clusters,
    flash() { flashAmount = 1.0; },
    update(t, intensity) {
      // Flash decay
      if (flashAmount > 0) {
        flashAmount *= 0.94;
        if (flashAmount < 0.01) flashAmount = 0;
      }
      group.scale.setScalar(1 + Math.sin(t * 0.7) * 0.015 * (1 + intensity));
      coreMat.opacity = 0.22 + Math.sin(t * 1.8) * 0.08 + intensity * 0.07 + flashAmount * 0.2;
      hubHalo.material.opacity = 0.04 + Math.sin(t * 1.2) * 0.02 + flashAmount * 0.1;
      bgSphere.material.opacity = 0.04 + Math.sin(t * 0.8) * 0.02 + flashAmount * 0.12;
      const CYCLE = 3, FADE = 0.6, n = clusters.length;
      const ct = (t + 1.5) % (CYCLE * n), ai = Math.floor(ct / CYCLE), pp = ct % CYCLE;
      const prev = (ai - 1 + n) % n;
      clusters.forEach((c, i) => {
        const active = i === ai;
        let wO = active ? 1.0 : 0, cO = active ? 0.55 : 0, gO = active ? 0.25 : 0, sc = active ? 1.0 : 0;
        if (pp < FADE) {
          const f = pp / FADE;
          if (active) { wO = 1.0 * f; cO = 0.55 * f; gO = 0.25 * f; sc = 0.3 + f * 0.7; }
          else if (i === prev) { wO = 1.0 * (1 - f); cO = 0.55 * (1 - f); gO = 0.25 * (1 - f); sc = 0.3 + (1 - f) * 0.7; }
        }
        c.wire.material.opacity = wO; c.core.material.opacity = cO;
        c.glow.material.opacity = gO;
        const s = Math.max(sc, 0.001);
        c.wire.scale.setScalar(s); c.core.scale.setScalar(s); c.glow.scale.setScalar(s);
        c.wire.rotation.y = t * (0.3 + i * 0.12); c.wire.rotation.x = t * 0.15;
        c.glow.rotation.y = -t * 0.1; c.glow.rotation.z = t * 0.08;
      });
    },
    dispose() { scene.remove(group); },
  };
}

// ═══════════════════════════════════════════════════════════
// HEMISPHERE BAS — WORKERS (3 clusters: Ingestion, Traitement, Orchestration)
// ═══════════════════════════════════════════════════════════
function createBottomHemisphere(scene) {
  const group = new THREE.Group();
  const Y = 1.5;

  // Background sphere — visible translucent green zone
  const bgSphere = new THREE.Mesh(
    new THREE.SphereGeometry(0.5, 24, 24),
    new THREE.MeshBasicMaterial({ color: 0x22c55e, transparent: true, opacity: 0.05, blending: THREE.AdditiveBlending, side: THREE.DoubleSide, depthWrite: false })
  );
  bgSphere.position.set(0, Y, 0);
  bgSphere.renderOrder = 0;
  group.add(bgSphere);

  // Hub central
  const hub = new THREE.Mesh(new THREE.SphereGeometry(0.1, 14, 14), new THREE.MeshBasicMaterial({ color: 0x22c55e, transparent: true, opacity: 0.35, blending: THREE.AdditiveBlending }));
  hub.position.set(0, Y, 0);
  group.add(hub);
  const hubHalo = new THREE.Mesh(new THREE.SphereGeometry(0.16, 10, 10), new THREE.MeshBasicMaterial({ color: 0x22c55e, transparent: true, opacity: 0.06, blending: THREE.AdditiveBlending }));
  hubHalo.position.set(0, Y, 0);
  group.add(hubHalo);

  const wTypes = [
    { label: 'INGESTION', color: 0x22d3ee, r: 0.40, speed: 2.2 },
    { label: 'TRAITEMENT', color: 0xf59e0b, r: 0.40, speed: 1.8 },
    { label: 'ORCHESTRATION', color: 0x10b981, r: 0.40, speed: 2.5 },
  ];
  const WCENTER = { x: 0, y: Y, z: 0 };
  const clusters = wTypes.map(k => {
    const wire = new THREE.Mesh(
      new THREE.IcosahedronGeometry(k.r, 2),
      new THREE.MeshBasicMaterial({ color: k.color, wireframe: true, transparent: true, opacity: 0 })
    );
    wire.position.set(WCENTER.x, WCENTER.y, WCENTER.z);
    wire.renderOrder = 2;
    group.add(wire);
    const core = new THREE.Mesh(
      new THREE.IcosahedronGeometry(k.r * 0.92, 2),
      new THREE.MeshLambertMaterial({ color: k.color, transparent: true, opacity: 0, side: THREE.DoubleSide, emissive: k.color, emissiveIntensity: 0.3 })
    );
    core.position.set(WCENTER.x, WCENTER.y, WCENTER.z);
    core.renderOrder = 1;
    group.add(core);
    const glow = new THREE.Mesh(
      new THREE.IcosahedronGeometry(k.r * 1.25, 1),
      new THREE.MeshBasicMaterial({ color: k.color, wireframe: true, transparent: true, opacity: 0, blending: THREE.AdditiveBlending })
    );
    glow.position.set(WCENTER.x, WCENTER.y, WCENTER.z);
    glow.renderOrder = 3;
    group.add(glow);
    return { wire, core, glow, ...k };
  });

  scene.add(group);
  let flashAmount = 0;
  return {
    group, clusters,
    flash() { flashAmount = 1.0; },
    update(t, intensity) {
      // Flash decay
      if (flashAmount > 0) {
        flashAmount *= 0.94;
        if (flashAmount < 0.01) flashAmount = 0;
      }
      group.scale.setScalar(1 + Math.sin(t * 0.7) * 0.015 * (1 + intensity));
      bgSphere.material.opacity = 0.035 + Math.sin(t * 0.8) * 0.015 + flashAmount * 0.12;
      hub.material.opacity = 0.28 + Math.sin(t * 2) * 0.12 + flashAmount * 0.2;
      hubHalo.material.opacity = 0.04 + Math.sin(t * 1.5) * 0.02 + flashAmount * 0.1;
      const CYCLE = 3, FADE = 0.6, n = clusters.length;
      const ct = (t + 3) % (CYCLE * n), ai = Math.floor(ct / CYCLE), pp = ct % CYCLE;
      const prev = (ai - 1 + n) % n;
      clusters.forEach((c, i) => {
        const active = i === ai;
        let wO = active ? 1.0 : 0, cO = active ? 0.55 : 0, gO = active ? 0.25 : 0, sc = active ? 1.0 : 0;
        if (pp < FADE) {
          const f = pp / FADE;
          if (active) { wO = 1.0 * f; cO = 0.55 * f; gO = 0.25 * f; sc = 0.3 + f * 0.7; }
          else if (i === prev) { wO = 1.0 * (1 - f); cO = 0.55 * (1 - f); gO = 0.25 * (1 - f); sc = 0.3 + (1 - f) * 0.7; }
        }
        c.wire.material.opacity = wO; c.core.material.opacity = cO;
        c.glow.material.opacity = gO;
        const s = Math.max(sc, 0.001);
        c.wire.scale.setScalar(s); c.core.scale.setScalar(s); c.glow.scale.setScalar(s);
        c.wire.rotation.y = t * (0.3 + i * 0.12); c.wire.rotation.x = t * 0.15;
        c.glow.rotation.y = -t * 0.1; c.glow.rotation.z = t * 0.08;
      });
    },
    dispose() { scene.remove(group); },
  };
}

// ═══════════════════════════════════════════════════════════
// CONNEXIONS INTER-HÉMISPHÈRES (Axones & Synapses)
// ═══════════════════════════════════════════════════════════
function createHemisphereConnections(scene) {
  const group = new THREE.Group();
  const defs = [
    // Workers -> Memoire (green→purple arcs, descending)
    { from: [0, 1.5, 0.15], to: [-0.8, 0.2, 0.12], color: 0x34d399, phase: 'workers_to_memory' },
    { from: [0.12, 1.5, -0.1], to: [-0.9, 0.0, -0.12], color: 0x34d399, phase: 'workers_to_memory' },
    // Memoire -> Intelligence (indigo arcs)
    { from: [-0.65, 0.2, 0], to: [0.65, 0.18, 0], color: 0x818cf8, phase: 'memory_to_intel' },
    { from: [-0.7, 0.0, 0.12], to: [0.68, 0.08, 0.08], color: 0x818cf8, phase: 'memory_to_intel' },
    // Workers -> Intelligence (teal arc, descending)
    { from: [0.1, 1.5, 0.1], to: [0.7, 0.0, 0.1], color: 0x5eead4, phase: 'workers_to_intel' },
  ];

  const TUBE_SEGS = 16, RADIAL_SEGS = 4, FLOW_PC = 6;

  const conns = defs.map(d => {
    const from = new THREE.Vector3(...d.from), to = new THREE.Vector3(...d.to);
    const mid = from.clone().lerp(to, 0.5);
    mid.z += 0.18 + Math.random() * 0.08;
    const curve = new THREE.CatmullRomCurve3([from, mid, to]);

    // Inner tube (main visible vein)
    const innerMat = new THREE.MeshBasicMaterial({
      color: d.color, transparent: true, opacity: 0.13,
      blending: THREE.AdditiveBlending, depthWrite: false,
    });
    const innerTube = new THREE.Mesh(new THREE.TubeGeometry(curve, TUBE_SEGS, 0.012, RADIAL_SEGS, false), innerMat);
    group.add(innerTube);

    // Outer glow tube
    const outerMat = new THREE.MeshBasicMaterial({
      color: d.color, transparent: true, opacity: 0.04,
      blending: THREE.AdditiveBlending, depthWrite: false,
    });
    const outerTube = new THREE.Mesh(new THREE.TubeGeometry(curve, TUBE_SEGS, 0.035, RADIAL_SEGS, false), outerMat);
    group.add(outerTube);

    // Flow particles
    const fpPos = new Float32Array(FLOW_PC * 3);
    const fpGeo = new THREE.BufferGeometry();
    fpGeo.setAttribute('position', new THREE.BufferAttribute(fpPos, 3));
    const flow = new THREE.Points(fpGeo, new THREE.PointsMaterial({
      color: d.color, size: 0.018, transparent: true, opacity: 0.3,
      blending: THREE.AdditiveBlending, depthWrite: false,
    }));
    group.add(flow);

    // Pulse sphere + glow (cascade animation)
    const pulseSphere = new THREE.Mesh(
      new THREE.SphereGeometry(0.04, 8, 8),
      new THREE.MeshBasicMaterial({ color: d.color, transparent: true, opacity: 0, blending: THREE.AdditiveBlending })
    );
    pulseSphere.visible = false;
    group.add(pulseSphere);
    const pulseGlow = new THREE.Mesh(
      new THREE.SphereGeometry(0.08, 6, 6),
      new THREE.MeshBasicMaterial({ color: d.color, transparent: true, opacity: 0, blending: THREE.AdditiveBlending })
    );
    pulseGlow.visible = false;
    group.add(pulseGlow);

    return { innerTube, outerTube, innerMat, outerMat, curve, flow, fpPos, pulseSphere, pulseGlow, phase: d.phase, pulseActive: false, pulseStart: -1 };
  });

  scene.add(group);

  const CASCADE_PHASE1_DUR = 1.4; // Workers → Mémoire / Intelligence
  const CASCADE_PHASE2_DUR = 1.2; // Mémoire → Intelligence

  return {
    group,

    // Trigger cascade: source vein arrives at Workers → propagate through hemispheres
    triggerCascade(t) {
      conns.forEach(c => {
        if (c.phase === 'workers_to_memory' || c.phase === 'workers_to_intel') {
          c.pulseActive = true;
          c.pulseStart = t;
        }
      });
      // Delayed phase 2: Mémoire → Intelligence
      conns.forEach(c => {
        if (c.phase === 'memory_to_intel') {
          c.pulseActive = true;
          c.pulseStart = t + CASCADE_PHASE1_DUR * 0.7; // slight overlap for fluid look
        }
      });
    },

    update(t, intensity) {
      group.scale.setScalar(1 + Math.sin(t * 0.7) * 0.015 * (1 + intensity));
      conns.forEach((c, i) => {
        // Base breathing opacity (much more visible than before)
        const breathe = Math.sin(t * 0.8 + i * 1.1);
        const baseInner = 0.10 + breathe * 0.03 + intensity * 0.04;
        const baseOuter = 0.03 + breathe * 0.01 + intensity * 0.02;

        // Pulse travel animation
        let pulseOp = 0;
        if (c.pulseActive && t >= c.pulseStart) {
          const dur = c.phase === 'memory_to_intel' ? CASCADE_PHASE2_DUR : CASCADE_PHASE1_DUR;
          const elapsed = t - c.pulseStart;
          const prog = elapsed / dur;

          if (prog > 1) {
            c.pulseActive = false;
            c.pulseSphere.visible = false;
            c.pulseGlow.visible = false;
          } else {
            pulseOp = 0.2 * (1 - prog * 0.5);
            const pt = c.curve.getPointAt(Math.min(prog, 1));
            c.pulseSphere.position.copy(pt);
            c.pulseSphere.material.opacity = 0.85 * (1 - prog * 0.4);
            c.pulseSphere.scale.setScalar(0.8 + (1 - prog) * 0.5);
            c.pulseSphere.visible = true;
            c.pulseGlow.position.copy(pt);
            c.pulseGlow.material.opacity = 0.25 * (1 - prog * 0.6);
            c.pulseGlow.scale.setScalar(0.6 + (1 - prog) * 0.8);
            c.pulseGlow.visible = true;
          }
        }

        c.innerMat.opacity = baseInner + pulseOp;
        c.outerMat.opacity = baseOuter + pulseOp * 0.4;

        // Flow particles along curve
        for (let j = 0; j < FLOW_PC; j++) {
          const prog = ((t * 0.15 + j / FLOW_PC) % 1);
          const pt = c.curve.getPointAt(prog);
          c.fpPos[j * 3] = pt.x; c.fpPos[j * 3 + 1] = pt.y; c.fpPos[j * 3 + 2] = pt.z;
        }
        c.flow.geometry.attributes.position.needsUpdate = true;
      });
    },
    dispose() {
      conns.forEach(c => {
        c.innerTube.geometry.dispose();
        c.outerTube.geometry.dispose();
      });
      scene.remove(group);
    },
  };
}

// ═══════════════════════════════════════════════════════════
// PORTAIL VOCAL — Holographic sound ring
// ═══════════════════════════════════════════════════════════
const MOUTH_PHRASES = [
  { flag: '🇫🇷', text: 'Je comprends tout ce que vous dites' },
  { flag: '🇬🇧', text: 'I understand everything you say' },
  { flag: '🇪🇸', text: 'Entiendo todo lo que dices' },
  { flag: '🇩🇪', text: 'Ich verstehe alles, was Sie sagen' },
  { flag: '🇷🇸', text: 'Razumem sve što kažete' },
  { flag: '🇸🇦', text: 'أنا أفهم كل ما تقوله' },
  { flag: '🇯🇵', text: 'あなたの言うことは全て理解しています' },
  { flag: '🇨🇳', text: '我能理解你说的一切' },
  { flag: '🇧🇷', text: 'Eu entendo tudo o que você diz' },
  { flag: '🇷🇺', text: 'Я понимаю всё, что вы говорите' },
  { flag: '🇮🇹', text: 'Capisco tutto quello che dici' },
  { flag: '🇰🇷', text: '당신이 말하는 모든 것을 이해합니다' },
];

function createMouth(scene) {
  const group = new THREE.Group();
  const MY = -1.8;
  const RING_SEGS = 96;
  const RING_INNER = 0.28;
  const RING_OUTER = 0.42;

  // === MAIN RING (deformable) ===
  const ringGeo = new THREE.RingGeometry(RING_INNER, RING_OUTER, RING_SEGS, 1);
  const ringMat = new THREE.MeshBasicMaterial({
    color: 0xc084fc, transparent: true, opacity: 0.5,
    side: THREE.DoubleSide, blending: THREE.AdditiveBlending,
  });
  const ring = new THREE.Mesh(ringGeo, ringMat);
  ring.rotation.x = -Math.PI / 2;
  ring.position.y = MY;
  group.add(ring);

  // Store original Y positions (all 0 since ring is in XZ plane after rotation)
  const posAttr = ringGeo.attributes.position;
  const origY = new Float32Array(posAttr.count);
  for (let i = 0; i < posAttr.count; i++) origY[i] = posAttr.getY(i);

  // === OUTER GLOW RING ===
  const glowGeo = new THREE.RingGeometry(RING_OUTER - 0.02, RING_OUTER + 0.12, RING_SEGS, 1);
  const glowRing = new THREE.Mesh(glowGeo, new THREE.MeshBasicMaterial({
    color: 0x8b5cf6, transparent: true, opacity: 0.12,
    side: THREE.DoubleSide, blending: THREE.AdditiveBlending,
  }));
  glowRing.rotation.x = -Math.PI / 2;
  glowRing.position.y = MY;
  group.add(glowRing);

  // === INNER CORE GLOW ===
  const coreGeo = new THREE.CircleGeometry(RING_INNER - 0.02, RING_SEGS);
  const coreMesh = new THREE.Mesh(coreGeo, new THREE.MeshBasicMaterial({
    color: 0x7c3aed, transparent: true, opacity: 0.0,
    side: THREE.DoubleSide, blending: THREE.AdditiveBlending,
  }));
  coreMesh.rotation.x = -Math.PI / 2;
  coreMesh.position.y = MY;
  group.add(coreMesh);

  // === EDGE HIGHLIGHT CIRCLE (thin bright line) ===
  const edgePts = [];
  for (let i = 0; i <= RING_SEGS; i++) {
    const a = (i / RING_SEGS) * Math.PI * 2;
    edgePts.push(new THREE.Vector3(Math.cos(a) * (RING_OUTER + 0.01), MY, Math.sin(a) * (RING_OUTER + 0.01)));
  }
  const edgeLine = new THREE.Line(
    new THREE.BufferGeometry().setFromPoints(edgePts),
    new THREE.LineBasicMaterial({ color: 0xd8b4fe, transparent: true, opacity: 0.4, blending: THREE.AdditiveBlending })
  );
  group.add(edgeLine);

  // === RISING PARTICLES (sound waves going up) ===
  const PCOUNT = 40;
  const pPositions = new Float32Array(PCOUNT * 3);
  const pGeo = new THREE.BufferGeometry();
  pGeo.setAttribute('position', new THREE.BufferAttribute(pPositions, 3));
  const particles = new THREE.Points(pGeo, new THREE.PointsMaterial({
    color: 0xd8b4fe, size: 0.04, transparent: true, opacity: 0,
    blending: THREE.AdditiveBlending, sizeAttenuation: true,
  }));
  group.add(particles);

  // Particle seeds
  const pSeeds = Array.from({ length: PCOUNT }, () => ({
    angle: Math.random() * Math.PI * 2,
    radius: RING_INNER + Math.random() * (RING_OUTER - RING_INNER),
    speed: 0.3 + Math.random() * 0.5,
    phase: Math.random() * Math.PI * 2,
  }));

  scene.add(group);

  let openAmount = 0;
  let targetOpen = 0;
  let speaking = false;
  let speakStart = 0;
  let phraseIdx = 0;
  let onPhraseChange = null;
  let waveIntensity = 0;

  return {
    group,
    get speaking() { return speaking; },
    get phraseIdx() { return phraseIdx; },
    get openAmount() { return openAmount; },
    startSpeaking(callback) {
      speaking = true;
      speakStart = -1;
      phraseIdx = 0;
      onPhraseChange = callback;
      if (callback) callback(0);
    },
    stopSpeaking() {
      speaking = false;
      targetOpen = 0;
      if (onPhraseChange) onPhraseChange(-1);
    },
    worldPos() { return new THREE.Vector3(0, MY, 0); },
    update(t) {
      if (speaking && speakStart < 0) speakStart = t;

      // Speaking cycle
      if (speaking) {
        const elapsed = t - speakStart;
        const PHRASE_DUR = 3.0;
        const currentPhraseIdx = Math.floor(elapsed / PHRASE_DUR) % MOUTH_PHRASES.length;
        if (currentPhraseIdx !== phraseIdx) {
          phraseIdx = currentPhraseIdx;
          if (onPhraseChange) onPhraseChange(phraseIdx);
        }
        const inPhrase = elapsed % PHRASE_DUR;
        if (inPhrase < 0.3) targetOpen = inPhrase / 0.3;
        else if (inPhrase < 2.2) targetOpen = 0.8 + Math.sin(t * 6) * 0.15;
        else targetOpen = Math.max(0, (PHRASE_DUR - inPhrase) / 0.6);
        if (elapsed > PHRASE_DUR * MOUTH_PHRASES.length) {
          speaking = false;
          targetOpen = 0;
          if (onPhraseChange) onPhraseChange(-1);
        }
      }

      openAmount += (targetOpen - openAmount) * 0.12;
      waveIntensity += ((speaking ? openAmount : 0) - waveIntensity) * 0.08;

      // === DEFORM RING VERTICES (audio visualizer wave) ===
      const breath = Math.sin(t * 1.2) * 0.005;
      for (let i = 0; i < posAttr.count; i++) {
        const x = posAttr.getX(i);
        const z = posAttr.getZ(i);
        const angle = Math.atan2(z, x);
        // Multi-frequency wave
        const w1 = Math.sin(angle * 3 + t * 5) * 0.12;
        const w2 = Math.sin(angle * 7 - t * 3.5) * 0.06;
        const w3 = Math.sin(angle * 11 + t * 8) * 0.03;
        const w4 = Math.sin(angle * 5 + t * 2) * 0.04;
        const wave = (w1 + w2 + w3 + w4) * waveIntensity;
        posAttr.setY(i, origY[i] + wave + breath);
      }
      posAttr.needsUpdate = true;

      // Ring material pulsing
      ringMat.opacity = 0.35 + waveIntensity * 0.45 + Math.sin(t * 2) * 0.05;

      // Outer glow pulse
      glowRing.material.opacity = 0.06 + waveIntensity * 0.15 + Math.sin(t * 1.5) * 0.03;
      glowRing.rotation.z = t * 0.1;

      // Inner core glow (appears when speaking)
      coreMesh.material.opacity = waveIntensity * 0.2;

      // Edge line pulse
      const edgeArr = edgeLine.geometry.attributes.position.array;
      for (let i = 0; i <= RING_SEGS; i++) {
        const a = (i / RING_SEGS) * Math.PI * 2;
        const r = RING_OUTER + 0.01;
        const ew = (Math.sin(a * 3 + t * 5) * 0.04 + Math.sin(a * 7 - t * 3) * 0.02) * waveIntensity;
        edgeArr[i * 3] = Math.cos(a) * (r + ew);
        edgeArr[i * 3 + 1] = MY + ew * 0.5;
        edgeArr[i * 3 + 2] = Math.sin(a) * (r + ew);
      }
      edgeLine.geometry.attributes.position.needsUpdate = true;
      edgeLine.material.opacity = 0.25 + waveIntensity * 0.5;

      // === RISING PARTICLES ===
      particles.material.opacity = waveIntensity * 0.7;
      const pArr = particles.geometry.attributes.position.array;
      for (let i = 0; i < PCOUNT; i++) {
        const s = pSeeds[i];
        if (waveIntensity > 0.05) {
          const age = ((t * s.speed + s.phase) % 2.5) / 2.5;
          const r = s.radius * (1 - age * 0.3);
          pArr[i * 3] = Math.cos(s.angle + t * 0.3) * r;
          pArr[i * 3 + 1] = MY + age * 1.5;
          pArr[i * 3 + 2] = Math.sin(s.angle + t * 0.3) * r;
        } else {
          pArr[i * 3 + 1] = -100;
        }
      }
      particles.geometry.attributes.position.needsUpdate = true;

      // Ring scale breathing
      const breathScale = 1 + Math.sin(t * 1.5) * 0.02;
      ring.scale.set(breathScale, breathScale, 1);
    },
    dispose() { scene.remove(group); },
  };
}

// ═══════════════════════════════════════════════════════════
// CAPABILITY NODES (37 competences, illuminated during cinematic)
// ═══════════════════════════════════════════════════════════
function createCapabilityNodes(leftGroup, rightGroup, bottomGroup) {
  const geo = new THREE.OctahedronGeometry(0.032, 0);
  function makeNodes(caps, color, parent) {
    return caps.map(cap => {
      const mat = new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0, blending: THREE.AdditiveBlending });
      const mesh = new THREE.Mesh(geo, mat);
      mesh.position.set(cap.x, cap.y, cap.z);
      mesh.scale.setScalar(0);
      parent.add(mesh);
      return { mesh, mat, name: cap.name, activated: false, activateTime: 0 };
    });
  }
  const leftNodes = makeNodes(CAPABILITIES.left, 0x60a5fa, leftGroup);
  const rightNodes = makeNodes(CAPABILITIES.right, 0xc084fc, rightGroup);
  const bottomNodes = makeNodes(CAPABILITIES.bottom, 0x22c55e, bottomGroup);
  return {
    leftNodes, rightNodes, bottomNodes,
    update(t) {
      [leftNodes, rightNodes, bottomNodes].forEach(list => list.forEach(n => {
        if (!n.activated) return;
        const age = t - n.activateTime;
        n.mesh.scale.setScalar(age < 0.15 ? (age / 0.15) * 1.6 : 1 + Math.sin(t * 2.5) * 0.1);
        n.mesh.rotation.y = t * 2;
        n.mat.opacity = age < 0.25 ? Math.min(0.9, age * 4) : Math.max(0.2, 0.9 - (age - 0.25) * 0.3);
      }));
    },
    reset() {
      [leftNodes, rightNodes, bottomNodes].forEach(list => list.forEach(n => {
        n.activated = false; n.mat.opacity = 0; n.mesh.scale.setScalar(0);
      }));
    },
    dispose() { geo.dispose(); },
  };
}

// ═══════════════════════════════════════════════════════════
// CINEMATIC ENGINE
// ═══════════════════════════════════════════════════════════
function runCinematic(ct, t, sr, camera, canvas, refs) {
  const TL = TIMELINE;
  const easeOut = x => 1 - (1 - Math.min(1, x)) ** 3;

  // Determine phase
  let phase, localT;
  if (ct < TL.ACCROCHE.end) { phase = 'accroche'; localT = ct - TL.ACCROCHE.start; }
  else if (ct < TL.TRANS_BOTTOM.end) { phase = 'trans_bottom'; localT = ct - TL.TRANS_BOTTOM.start; }
  else if (ct < TL.BOTTOM.end) { phase = 'bottom'; localT = ct - TL.BOTTOM.start; }
  else if (ct < TL.TRANS_LEFT.end) { phase = 'trans_left'; localT = ct - TL.TRANS_LEFT.start; }
  else if (ct < TL.LEFT.end) { phase = 'left'; localT = ct - TL.LEFT.start; }
  else if (ct < TL.TRANS_RIGHT.end) { phase = 'trans_right'; localT = ct - TL.TRANS_RIGHT.start; }
  else if (ct < TL.RIGHT.end) { phase = 'right'; localT = ct - TL.RIGHT.start; }
  else if (ct < TL.RECON.end) { phase = 'recon'; localT = ct - TL.RECON.start; }
  else if (ct < TL.FINALE.end) { phase = 'finale'; localT = ct - TL.FINALE.start; }
  else if (ct < TL.TRANS_VOCAL.end) { phase = 'trans_vocal'; localT = ct - TL.TRANS_VOCAL.start; }
  else if (ct < TL.VOCAL.end) { phase = 'vocal'; localT = ct - TL.VOCAL.start; }
  else if (ct < TL.CLOTURE.end) { phase = 'cloture'; localT = ct - TL.CLOTURE.start; }
  else if (ct < TL.SILENCE.end) { phase = 'silence'; localT = ct - TL.SILENCE.start; }
  else return true; // ended

  // Camera target mapping
  var cameraKey;
  switch (phase) {
    case 'accroche':     cameraKey = 'intro'; break;
    case 'trans_bottom': cameraKey = 'bottom'; break;
    case 'bottom':       cameraKey = 'bottom'; break;
    case 'trans_left':   cameraKey = 'left'; break;
    case 'left':         cameraKey = 'left'; break;
    case 'trans_right':  cameraKey = 'right'; break;
    case 'right':        cameraKey = 'right'; break;
    case 'recon':        cameraKey = 'finale'; break;
    case 'finale':       cameraKey = 'finale'; break;
    case 'trans_vocal':  cameraKey = 'vocal'; break;
    case 'vocal':        cameraKey = 'vocal'; break;
    case 'cloture':      cameraKey = 'vocal'; break;
    case 'silence':      cameraKey = 'idle'; break;
    default:             cameraKey = 'intro';
  }
  var wp = CAMERA_WAYPOINTS[cameraKey];
  var lerpSpeed = phase === 'accroche' ? 0.02 : 0.025;
  camera.position.lerp(new THREE.Vector3(wp.pos[0], wp.pos[1], wp.pos[2]), lerpSpeed);
  sr.lookAtTarget.lerp(new THREE.Vector3(wp.target[0], wp.target[1], wp.target[2]), lerpSpeed);
  camera.lookAt(sr.lookAtTarget);

  // Explosion offset
  var ex;
  if (phase === 'accroche') ex = easeOut(localT / 3.0) * 0.8;
  else if (phase === 'recon') ex = (1 - easeOut(localT / 1.5)) * 0.8;
  else if (phase === 'finale' || phase === 'trans_vocal' || phase === 'vocal' || phase === 'cloture' || phase === 'silence') ex = 0;
  else ex = 0.8;

  sr.leftHemi.group.position.x = -ex;
  sr.rightHemi.group.position.x = ex;
  sr.bottomHemi.group.position.y = ex * 0.65;
  sr.mouth.group.position.x = 0;
  sr.hemiConns.group.visible = ex < 0.15;

  // Start mouth speaking at vocal phase start
  if (phase === 'vocal' && sr.mouth && !sr.mouth.speaking && !sr._vocalStarted) {
    sr._vocalStarted = true;
    sr.mouth.startSpeaking(sr._mouthCallback);
  }

  // Narration audio (accroche, transitions, cloture)
  var narrationMap = {
    accroche: 'accroche', trans_bottom: 'trans_00',
    trans_left: 'trans_01', trans_right: 'trans_02',
    trans_vocal: 'trans_03', cloture: 'cloture',
  };
  if (narrationMap[phase] && !sr._narrationsPlayed[phase]) {
    sr._narrationsPlayed[phase] = true;
    if (sr._audioEl) {
      sr._audioEl.src = '/audio/' + narrationMap[phase] + '.mp3';
      sr._audioEl.play().catch(function() {});
    }
  }

  // Narrator text overlay (shown during accroche, transitions, cloture)
  var narratorTextMap = {
    accroche: TRANSITIONS.accroche, trans_bottom: TRANSITIONS.workers,
    trans_left: TRANSITIONS.memoire, trans_right: TRANSITIONS.intelligence,
    trans_vocal: TRANSITIONS.vocal, cloture: TRANSITIONS.cloture,
  };
  if (refs.narrator && refs.narrator.current) {
    var narText = narratorTextMap[phase];
    if (narText) {
      refs.narrator.current.textContent = narText;
      refs.narrator.current.style.opacity = String(easeOut(localT / 0.8));
    } else {
      refs.narrator.current.style.opacity = '0';
    }
  }

  // Cascade activation for hemisphere phases
  var cascadeMap = { left: sr.capNodes.leftNodes, right: sr.capNodes.rightNodes, bottom: sr.capNodes.bottomNodes };
  var nodeList = cascadeMap[phase];
  if (nodeList) {
    var capIdx = Math.floor(localT / TL.CASCADE_INTERVAL);
    for (var i = 0; i <= Math.min(capIdx, nodeList.length - 1); i++) {
      if (!nodeList[i].activated) { nodeList[i].activated = true; nodeList[i].activateTime = t; }
    }
    // Capability label overlay
    var activeIdx = Math.min(capIdx, nodeList.length - 1);
    if (activeIdx >= 0 && activeIdx < nodeList.length && refs.capLabel.current) {
      var wp3 = new THREE.Vector3();
      nodeList[activeIdx].mesh.getWorldPosition(wp3);
      var proj = wp3.project(camera);
      if (proj.z < 1) {
        var sx = (proj.x * 0.5 + 0.5) * canvas.clientWidth;
        var sy = (-proj.y * 0.5 + 0.5) * canvas.clientHeight;
        refs.capLabel.current.textContent = '\u2726 ' + nodeList[activeIdx].name;
        refs.capLabel.current.style.left = (sx + 20) + 'px';
        refs.capLabel.current.style.top = (sy - 8) + 'px';
        refs.capLabel.current.style.opacity = '1';
        refs.capLabel.current.style.color = PHASE_INFO[phase].color;
      }
    }
    // Phase title overlay
    if (refs.phaseTitle.current) {
      var info = PHASE_INFO[phase];
      refs.phaseTitle.current.innerHTML =
        '<div style="font-size:28px;font-weight:700;color:' + info.color + ';letter-spacing:3px">' +
        info.title + ' \u2014 ' + info.count + ' capacit\u00e9s</div>' +
        '<div style="font-size:14px;color:rgba(255,255,255,0.5);margin-top:6px">' + info.subtitle + '</div>';
      refs.phaseTitle.current.style.opacity = '1';
    }
    // Audio playback for capability
    if (activeIdx >= 0) {
      var audioIdx = (AUDIO_OFFSET[phase] || 0) + activeIdx;
      if (audioIdx !== sr._lastAudioIdx) {
        sr._lastAudioIdx = audioIdx;
        if (sr._audioEl) {
          sr._audioEl.src = '/audio/cap_' + String(audioIdx).padStart(2, '0') + '.mp3';
          sr._audioEl.play().catch(function() {});
        }
      }
    }
  } else {
    if (refs.capLabel.current) refs.capLabel.current.style.opacity = '0';
    if (refs.phaseTitle.current && (phase !== 'trans_bottom' && phase !== 'trans_left' && phase !== 'trans_right')) {
      refs.phaseTitle.current.style.opacity = '0';
    }
  }

  // Intro text (hidden — accroche uses narrator overlay instead)
  if (refs.intro.current) refs.intro.current.style.opacity = '0';
  // Finale text
  if (refs.finale.current) refs.finale.current.style.opacity = (phase === 'finale' || phase === 'trans_vocal' || phase === 'vocal') ? String(easeOut(localT / 1.5)) : '0';
  // Counter overlay
  if (refs.counter.current) {
    if (nodeList) {
      var activated = Math.min(Math.floor(localT / TL.CASCADE_INTERVAL) + 1, nodeList.length);
      var total = CAPABILITIES.left.length + CAPABILITIES.right.length + CAPABILITIES.bottom.length;
      var offset = phase === 'bottom' ? 0 : phase === 'left' ? CAPABILITIES.bottom.length : CAPABILITIES.bottom.length + CAPABILITIES.left.length;
      refs.counter.current.textContent = (offset + activated) + ' / ' + total;
      refs.counter.current.style.opacity = '1';
    } else if (phase === 'finale' || phase === 'trans_vocal' || phase === 'vocal') {
      refs.counter.current.style.opacity = '1';
      refs.counter.current.textContent = '37 / 37';
    } else {
      refs.counter.current.style.opacity = '0';
    }
  }

  // Audio playback for vocal languages
  if (phase === 'vocal' && sr._audioEl) {
    var langIdx = Math.floor(localT / 3.0);
    if (langIdx !== sr._lastLangIdx && langIdx < 12) {
      sr._lastLangIdx = langIdx;
      sr._audioEl.src = '/audio/lang_' + String(langIdx).padStart(2, '0') + '.mp3';
      sr._audioEl.play().catch(function() {});
    }
  }

  // Silence phase: fade out everything
  if (phase === 'silence') {
    if (refs.finale.current) refs.finale.current.style.opacity = String(1 - easeOut(localT / 1.5));
    if (refs.counter.current) refs.counter.current.style.opacity = String(1 - easeOut(localT / 1.5));
  }

  return false;
}


// ── Galaxy Type Colors ──
const GALAXY_TYPE_COLORS = {
  call: new THREE.Color(0x60a5fa),       // blue
  email: new THREE.Color(0x34d399),      // green
  crm_note: new THREE.Color(0xfbbf24),   // amber
  sms: new THREE.Color(0x22d3ee),        // cyan
  whatsapp: new THREE.Color(0xa78bfa),   // purple
  document: new THREE.Color(0xf97316),   // orange
  unknown: new THREE.Color(0x6b7280),    // gray
};
const GALAXY_DEFAULT_COLOR = new THREE.Color(0x6b7280);

function createGalaxy(scene, galaxyData) {
  const group = new THREE.Group();
  const clusters = galaxyData.clusters || [];
  const topContacts = galaxyData.top_contacts || [];

  // Build contact position map (deterministic hash → spherical coordinates)
  const contactPositions = {};
  const maxR = 3.5;
  function hashToPos(str) {
    let h = 0;
    for (let i = 0; i < str.length; i++) h = ((h << 5) - h + str.charCodeAt(i)) | 0;
    const phi = (Math.abs(h % 1000) / 1000) * Math.PI * 2;
    const theta = (Math.abs((h >> 10) % 1000) / 1000) * Math.PI;
    const r = 0.4 + (Math.abs((h >> 20) % 1000) / 1000) * (maxR - 0.4);
    return new THREE.Vector3(
      r * Math.sin(theta) * Math.cos(phi),
      r * Math.sin(theta) * Math.sin(phi) - 0.3,
      r * Math.cos(theta),
    );
  }

  // Count total memories
  let totalMemories = 0;
  clusters.forEach(c => { totalMemories += c.count; });

  // Cap at 30k particles for performance
  const MAX_PARTICLES = 30000;
  const scale = totalMemories > MAX_PARTICLES ? MAX_PARTICLES / totalMemories : 1;

  // Generate all particle positions + colors + sizes
  const positions = [];
  const colors = [];
  const sizes = [];
  const now = Date.now();

  clusters.forEach(cluster => {
    const cid = cluster.contact_id || 'unknown';
    if (!contactPositions[cid]) contactPositions[cid] = hashToPos(cid);
    const center = contactPositions[cid];
    const typeColor = GALAXY_TYPE_COLORS[cluster.type] || GALAXY_DEFAULT_COLOR;
    const particleCount = Math.max(1, Math.round(cluster.count * scale));

    // Recency factor (0-1, 1 = today)
    let recency = 0.3;
    if (cluster.last_at) {
      const age = (now - new Date(cluster.last_at).getTime()) / (1000 * 60 * 60 * 24);
      recency = Math.max(0.2, 1 - age / 180); // 6 months decay
    }

    for (let i = 0; i < particleCount; i++) {
      // Scatter around center with gaussian-like distribution
      const spread = 0.15 + cluster.count * 0.002;
      const dx = (Math.random() - 0.5) * spread * 2;
      const dy = (Math.random() - 0.5) * spread * 2;
      const dz = (Math.random() - 0.5) * spread * 2;
      positions.push(center.x + dx, center.y + dy, center.z + dz);
      colors.push(typeColor.r, typeColor.g, typeColor.b);
      sizes.push(0.02 + recency * 0.04);
    }
  });

  // Create instanced points
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geometry.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));
  geometry.setAttribute('size', new THREE.Float32BufferAttribute(sizes, 1));

  const material = new THREE.PointsMaterial({
    vertexColors: true, size: 0.04, transparent: true, opacity: 0.85,
    blending: THREE.AdditiveBlending, depthWrite: false, sizeAttenuation: true,
  });
  const points = new THREE.Points(geometry, material);
  group.add(points);

  // Add "star" labels for top contacts (larger glowing spheres)
  const starData = [];
  topContacts.slice(0, 20).forEach(tc => {
    const pos = contactPositions[tc.id] || hashToPos(tc.id);
    const starSize = 0.03 + Math.min(tc.count / 200, 0.12);
    const star = new THREE.Mesh(
      new THREE.SphereGeometry(starSize, 8, 8),
      new THREE.MeshBasicMaterial({
        color: 0xffffff, transparent: true, opacity: 0.6,
        blending: THREE.AdditiveBlending,
      })
    );
    star.position.copy(pos);
    group.add(star);

    // Halo
    const halo = new THREE.Mesh(
      new THREE.SphereGeometry(starSize * 2.5, 6, 6),
      new THREE.MeshBasicMaterial({
        color: 0x60a5fa, transparent: true, opacity: 0.08,
        blending: THREE.AdditiveBlending,
      })
    );
    halo.position.copy(pos);
    group.add(halo);

    starData.push({ name: tc.name, count: tc.count, position: pos.clone(), star, halo });
  });

  // Ambient nebula ring
  const nebulaGeo = new THREE.RingGeometry(2.5, 3.8, 64);
  const nebulaMat = new THREE.MeshBasicMaterial({
    color: 0x818cf8, transparent: true, opacity: 0.02, side: THREE.DoubleSide,
    blending: THREE.AdditiveBlending,
  });
  const nebula = new THREE.Mesh(nebulaGeo, nebulaMat);
  nebula.rotation.x = Math.PI * 0.5;
  nebula.position.y = -0.3;
  group.add(nebula);

  scene.add(group);

  return {
    group, points, starData, totalMemories: positions.length / 3,
    update(t) {
      // Slow rotation
      group.rotation.y = t * 0.03;
      // Twinkle stars
      starData.forEach((s, i) => {
        s.star.material.opacity = 0.5 + Math.sin(t * 2 + i * 1.7) * 0.2;
        s.halo.material.opacity = 0.06 + Math.sin(t * 1.5 + i * 2.1) * 0.03;
      });
      // Subtle size pulse
      material.size = 0.035 + Math.sin(t * 0.5) * 0.005;
    },
    dispose() {
      geometry.dispose();
      material.dispose();
      scene.remove(group);
    },
  };
}

// ── Solar System (contact zoom) — "Isolation Solaire" ────
const SOLAR_BANDS = [
  { maxAge: 7, radius: 0.6, label: 'Cette semaine' },
  { maxAge: 30, radius: 1.0, label: 'Ce mois' },
  { maxAge: 90, radius: 1.4, label: '3 derniers mois' },
  { maxAge: 180, radius: 1.85, label: '6 derniers mois' },
  { maxAge: Infinity, radius: 2.3, label: 'Plus ancien' },
];
const SOLAR_BAND_COLORS = [
  new THREE.Color(0xfbbf24), // warm yellow (recent)
  new THREE.Color(0xf59e0b), // amber
  new THREE.Color(0x8b5cf6), // violet
  new THREE.Color(0x3b82f6), // blue
  new THREE.Color(0x1e3a5f), // cold dark blue (old)
];

function createSolarSystem(scene, contactData, center = new THREE.Vector3(0, 0, 0)) {
  const group = new THREE.Group();
  group.position.copy(center);
  const memories = contactData.memories || [];

  // ── Ambient dust (150 tiny round particles) ──
  const dustCount = 150;
  const dustPositions = new Float32Array(dustCount * 3);
  for (let i = 0; i < dustCount; i++) {
    dustPositions[i * 3] = (Math.random() - 0.5) * 12;
    dustPositions[i * 3 + 1] = (Math.random() - 0.5) * 8;
    dustPositions[i * 3 + 2] = (Math.random() - 0.5) * 12;
  }
  const dustGeo = new THREE.BufferGeometry();
  dustGeo.setAttribute('position', new THREE.Float32BufferAttribute(dustPositions, 3));
  const dustMat = new THREE.PointsMaterial({
    color: 0x6b7db8, size: 0.008, transparent: true, opacity: 0.12,
    blending: THREE.AdditiveBlending, depthWrite: false, sizeAttenuation: true,
  });
  const dustParticles = new THREE.Points(dustGeo, dustMat);
  group.add(dustParticles);

  // ── Background starfield (100 fixed stars on a distant sphere) ──
  const starCount = 100;
  const starPositions = new Float32Array(starCount * 3);
  const starOpacities = new Float32Array(starCount);
  const starSizes = new Float32Array(starCount);
  for (let i = 0; i < starCount; i++) {
    // Distribute on a large sphere (radius 18)
    const phi = Math.random() * Math.PI * 2;
    const theta = Math.acos(2 * Math.random() - 1);
    const r = 16 + Math.random() * 4;
    starPositions[i * 3] = r * Math.sin(theta) * Math.cos(phi);
    starPositions[i * 3 + 1] = r * Math.sin(theta) * Math.sin(phi);
    starPositions[i * 3 + 2] = r * Math.cos(theta);
    starOpacities[i] = 0.08 + Math.random() * 0.17; // 0.08 to 0.25
    starSizes[i] = 0.012 + Math.random() * 0.018; // small variation
  }
  const starfieldGeo = new THREE.BufferGeometry();
  starfieldGeo.setAttribute('position', new THREE.Float32BufferAttribute(starPositions, 3));
  starfieldGeo.setAttribute('size', new THREE.Float32BufferAttribute(starSizes, 1));
  const starfieldMat = new THREE.PointsMaterial({
    color: 0xffffff, size: 1.8, transparent: true, opacity: 0.35,
    blending: THREE.AdditiveBlending, depthWrite: false, sizeAttenuation: false,
  });
  const starfield = new THREE.Points(starfieldGeo, starfieldMat);
  group.add(starfield);
  // Track which stars twinkle (15%)
  const twinkleIndices = [];
  for (let i = 0; i < starCount; i++) {
    if (Math.random() < 0.15) twinkleIndices.push(i);
  }

  // ── Nebula glow (radial gradient via layered sprites, color based on dominant type) ──
  const typeCounts = {};
  memories.forEach(m => { typeCounts[m.type] = (typeCounts[m.type] || 0) + 1; });
  const dominantType = Object.entries(typeCounts).sort((a, b) => b[1] - a[1])[0]?.[0] || 'unknown';
  const nebulaColorMap = { call: 0xf97316, email: 0x34d399, crm_note: 0xef4444, sms: 0x22d3ee, whatsapp: 0xa78bfa, document: 0xf97316 };
  const nebulaColor = nebulaColorMap[dominantType] || 0x6366f1;

  // Create radial nebula using 3 concentric transparent spheres (no hard edges)
  const nebulaLayers = [];
  [
    { scale: 1.8, opacity: 0.045 },
    { scale: 3.0, opacity: 0.025 },
    { scale: 4.5, opacity: 0.012 },
  ].forEach(layer => {
    const neb = new THREE.Mesh(
      new THREE.SphereGeometry(layer.scale, 16, 16),
      new THREE.MeshBasicMaterial({
        color: nebulaColor, transparent: true, opacity: layer.opacity,
        blending: THREE.AdditiveBlending, depthWrite: false,
      })
    );
    group.add(neb);
    nebulaLayers.push(neb);
  });

  // ── Central sun (smaller, warm gradient, corona) ──
  const sunSize = 0.08;
  const sun = new THREE.Mesh(
    new THREE.SphereGeometry(sunSize, 20, 20),
    new THREE.MeshBasicMaterial({ color: 0xfffdf0, transparent: true, opacity: 0.95 })
  );
  group.add(sun);

  // Sun warm inner glow
  const sunInner = new THREE.Mesh(
    new THREE.SphereGeometry(sunSize * 1.6, 14, 14),
    new THREE.MeshBasicMaterial({ color: 0xfcd34d, transparent: true, opacity: 0.2, blending: THREE.AdditiveBlending })
  );
  group.add(sunInner);

  // Corona rings (3 layers)
  const coronaLayers = [];
  [2.5, 4.0, 6.0].forEach((scale, i) => {
    const corona = new THREE.Mesh(
      new THREE.SphereGeometry(sunSize * scale, 12, 12),
      new THREE.MeshBasicMaterial({
        color: [0xfbbf24, 0xf59e0b, 0xea580c][i],
        transparent: true, opacity: [0.08, 0.04, 0.02][i],
        blending: THREE.AdditiveBlending, depthWrite: false,
      })
    );
    group.add(corona);
    coronaLayers.push(corona);
  });

  // ── Distribute memories into bands ──
  const now = Date.now();
  const bandGroups = SOLAR_BANDS.map(() => []);
  memories.forEach(mem => {
    const age = mem.timestamp ? (now - new Date(mem.timestamp).getTime()) / (1000 * 60 * 60 * 24) : 999;
    const idx = SOLAR_BANDS.findIndex(b => age <= b.maxAge);
    bandGroups[idx >= 0 ? idx : SOLAR_BANDS.length - 1].push(mem);
  });

  // ── Orbit rings (dashed, opacity based on content, warm→cold color) ──
  const orbitRings = [];
  SOLAR_BANDS.forEach((band, bandIdx) => {
    const bandCount = bandGroups[bandIdx].length;
    const baseOpacity = bandCount > 0 ? 0.08 + Math.min(bandCount / 20, 0.15) : 0.03;
    const bandColor = SOLAR_BAND_COLORS[bandIdx];
    const segments = 128;
    const points = [];
    for (let i = 0; i <= segments; i++) {
      const theta = (i / segments) * Math.PI * 2;
      points.push(new THREE.Vector3(Math.cos(theta) * band.radius, 0, Math.sin(theta) * band.radius));
    }
    const lineGeo = new THREE.BufferGeometry().setFromPoints(points);
    const lineMat = new THREE.LineDashedMaterial({
      color: bandColor, transparent: true, opacity: baseOpacity,
      dashSize: 0.06, gapSize: 0.04, blending: THREE.AdditiveBlending,
    });
    const ring = new THREE.Line(lineGeo, lineMat);
    ring.computeLineDistances(); // required for dashed material - must be called on Line, not geometry
    group.add(ring);
    orbitRings.push({ ring, baseOpacity, bandIdx, bandCount });
  });

  // ── Place memory spheres (larger, comet trails, variable size) ──
  const memoryMeshes = [];
  bandGroups.forEach((bandMems, bandIdx) => {
    const radius = SOLAR_BANDS[bandIdx].radius;
    const orbitSpeed = 0.12 - bandIdx * 0.018;
    bandMems.forEach((mem, i) => {
      const angle = (i / Math.max(bandMems.length, 1)) * Math.PI * 2;
      const typeColor = GALAXY_TYPE_COLORS[mem.type] || GALAXY_DEFAULT_COLOR;

      // Variable size: calls with duration are bigger, emails medium, sms small
      let memSize = 0.025; // base 50% larger
      if (mem.duration && mem.duration > 60) memSize = 0.025 + Math.min(mem.duration / 600, 0.035);
      else if (mem.type === 'email') memSize = 0.028;
      else if (mem.type === 'sms') memSize = 0.018;
      if (mem.importance === 'high') memSize *= 1.3;

      const sphere = new THREE.Mesh(
        new THREE.SphereGeometry(memSize, 8, 8),
        new THREE.MeshBasicMaterial({
          color: new THREE.Color(typeColor.r, typeColor.g, typeColor.b),
          transparent: true, opacity: 0.9,
        })
      );
      const yOffset = (Math.random() - 0.5) * 0.05;
      sphere.position.set(Math.cos(angle) * radius, yOffset, Math.sin(angle) * radius);
      group.add(sphere);

      // Glow halo per planet
      const halo = new THREE.Mesh(
        new THREE.SphereGeometry(memSize * 2.2, 6, 6),
        new THREE.MeshBasicMaterial({
          color: new THREE.Color(typeColor.r, typeColor.g, typeColor.b),
          transparent: true, opacity: 0.12, blending: THREE.AdditiveBlending, depthWrite: false,
        })
      );
      halo.position.copy(sphere.position);
      group.add(halo);

      // Comet trail (elongated behind the direction of travel)
      const trailGeo = new THREE.BufferGeometry();
      const trailCount = 6;
      const trailPositions = new Float32Array(trailCount * 3);
      const trailOpacities = new Float32Array(trailCount);
      for (let t = 0; t < trailCount; t++) {
        const ta = angle - (t + 1) * 0.04;
        trailPositions[t * 3] = Math.cos(ta) * radius;
        trailPositions[t * 3 + 1] = yOffset;
        trailPositions[t * 3 + 2] = Math.sin(ta) * radius;
        trailOpacities[t] = 0.3 * (1 - t / trailCount);
      }
      trailGeo.setAttribute('position', new THREE.Float32BufferAttribute(trailPositions, 3));
      const trailMat = new THREE.PointsMaterial({
        color: new THREE.Color(typeColor.r, typeColor.g, typeColor.b),
        size: memSize * 1.2, transparent: true, opacity: 0.25,
        blending: THREE.AdditiveBlending, depthWrite: false, sizeAttenuation: true,
      });
      const trail = new THREE.Points(trailGeo, trailMat);
      group.add(trail);

      // Saturn ring for "important" memories
      let saturnRing = null;
      if (mem.importance === 'high') {
        const srGeo = new THREE.RingGeometry(memSize * 1.8, memSize * 2.6, 16);
        const srMat = new THREE.MeshBasicMaterial({
          color: 0xfbbf24, transparent: true, opacity: 0.25,
          side: THREE.DoubleSide, blending: THREE.AdditiveBlending,
        });
        saturnRing = new THREE.Mesh(srGeo, srMat);
        saturnRing.rotation.x = Math.PI * 0.35;
        saturnRing.rotation.z = Math.random() * 0.5;
        saturnRing.position.copy(sphere.position);
        group.add(saturnRing);
      }

      // Invisible hitbox for mouse detection (5x radius)
      const hitbox = new THREE.Mesh(
        new THREE.SphereGeometry(Math.max(memSize * 5, 0.07), 4, 4),
        new THREE.MeshBasicMaterial({ transparent: true, opacity: 0, depthWrite: false })
      );
      hitbox.position.copy(sphere.position);
      group.add(hitbox);

      memoryMeshes.push({ mesh: sphere, halo, hitbox, trail, saturnRing, angle, radius, speed: orbitSpeed, mem, yOffset, frozen: false });
    });
  });

  scene.add(group);

  return {
    group, sun, sunInner, coronaLayers, nebulaLayers, dustParticles, memoryMeshes, bandGroups, orbitRings, dominantType,
    update(t) {
      // Sun gentle pulse (scale 1.0→1.03 in 4s)
      const sunPulse = 1 + Math.sin(t * 1.57) * 0.03;
      sun.scale.setScalar(sunPulse);
      sun.material.opacity = 0.9 + Math.sin(t * 2) * 0.05;
      sunInner.material.opacity = 0.18 + Math.sin(t * 3) * 0.06;
      sunInner.scale.setScalar(sunPulse * 1.05);
      coronaLayers.forEach((c, i) => {
        c.scale.setScalar(1 + Math.sin(t * (0.5 + i * 0.3)) * 0.1);
        c.material.opacity = [0.08, 0.04, 0.02][i] + Math.sin(t * 1.2 + i) * 0.01;
      });

      // Nebula subtle breathing
      nebulaLayers.forEach((neb, i) => {
        const baseOp = [0.045, 0.025, 0.012][i];
        neb.material.opacity = baseOp + Math.sin(t * 0.3 + i * 0.7) * 0.005;
        neb.scale.setScalar(1 + Math.sin(t * 0.15 + i) * 0.03);
      });

      // Dust drift
      dustParticles.rotation.y = t * 0.008;
      dustParticles.rotation.x = Math.sin(t * 0.05) * 0.02;

      // Starfield twinkle (15% of stars pulse slowly)
      twinkleIndices.forEach(idx => {
        const base = starOpacities[idx];
        const twinkle = base + Math.sin(t * (1.5 + idx * 0.3)) * 0.08;
        starSizes[idx] = (0.012 + (idx % 5) * 0.004) * (1 + Math.sin(t * (1.2 + idx * 0.2)) * 0.15);
      });
      starfieldGeo.attributes.size.needsUpdate = true;

      // Orbit memories
      memoryMeshes.forEach(m => {
        if (m.frozen) return;
        const a = m.angle + t * m.speed;
        m.mesh.position.x = Math.cos(a) * m.radius;
        m.mesh.position.z = Math.sin(a) * m.radius;
        m.halo.position.x = m.mesh.position.x;
        m.halo.position.y = m.mesh.position.y;
        m.halo.position.z = m.mesh.position.z;
        // Comet trail particles
        const trailPos = m.trail.geometry.attributes.position;
        for (let ti = 0; ti < 6; ti++) {
          const ta = a - (ti + 1) * 0.04;
          trailPos.setXYZ(ti, Math.cos(ta) * m.radius, m.yOffset, Math.sin(ta) * m.radius);
        }
        trailPos.needsUpdate = true;
        if (m.saturnRing) {
          m.saturnRing.position.copy(m.mesh.position);
        }
        if (m.hitbox) {
          m.hitbox.position.x = m.mesh.position.x;
          m.hitbox.position.y = m.mesh.position.y;
          m.hitbox.position.z = m.mesh.position.z;
        }
      });
    },
    // Filter visibility by type (null = show all)
    setTypeFilter(type) {
      memoryMeshes.forEach(m => {
        const visible = !type || m.mem.type === type;
        m.mesh.material.opacity = visible ? 0.9 : 0.06;
        m.halo.material.opacity = visible ? 0.12 : 0.01;
        m.trail.material.opacity = visible ? 0.25 : 0.02;
        if (m.saturnRing) m.saturnRing.material.opacity = visible ? 0.25 : 0.02;
      });
    },
    // Freeze/unfreeze a specific memory (for pinning)
    freezeMemory(mesh, freeze) {
      const m = memoryMeshes.find(mm => mm.mesh === mesh);
      if (m) m.frozen = freeze;
    },
    dispose() {
      group.traverse(child => {
        if (child.geometry) child.geometry.dispose();
        if (child.material) child.material.dispose();
      });
      scene.remove(group);
    },
  };
}

// ── Thought visualization helpers ──
const THOUGHT_MONTHS = ["", "Janvier", "Fevrier", "Mars", "Avril", "Mai", "Juin", "Juillet", "Aout", "Septembre", "Octobre", "Novembre", "Decembre"];
function formatThoughtTimeFilter(tf) {
  if (!tf) return null;
  const k = tf.kind;
  if (k === 'named_month' || k === 'since_month') return `${k === 'since_month' ? 'Depuis ' : ''}${THOUGHT_MONTHS[tf.value] || tf.value}`;
  if (k === 'today') return "Aujourd'hui";
  if (k === 'yesterday') return 'Hier';
  if (k === 'this_week') return 'Cette semaine';
  if (k === 'last_week') return 'Semaine derniere';
  if (k === 'this_month') return 'Ce mois';
  if (k === 'last_month') return 'Mois dernier';
  if (k === 'last_n_days') return `${tf.value} derniers jours`;
  if (k === 'ago_n_weeks') return `Il y a ${tf.value} semaine(s)`;
  if (k === 'ago_n_months') return `Il y a ${tf.value} mois`;
  return k;
}

const THOUGHT_PHASE_CONFIG = {
  receiving:  { label: 'Reception de la question',        color: '#ffffff', icon: '...' },
  analyzing:  { label: 'Analyse des filtres',              color: '#34d399', icon: '...' },
  scanning:   { label: 'Scan de la memoire',               color: '#a78bfa', icon: '...' },
  reasoning:  { label: 'Raisonnement en cours',            color: '#f472b6', icon: '...' },
  complete:   { label: 'Reponse generee',                  color: '#60a5fa', icon: '...' },
};

// ═══════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════
export function BrainCanvas({ metrics, pulses = [], cinematicTrigger = 0, onCinematicEnd, thoughtEvent, token, onGalaxyChange, liveThoughts = [] }) {
  const canvasRef = useRef(null);
  const animIdRef = useRef(null);
  const labelRefsMap = useRef({});
  const toastRef = useRef(null);
  const sceneRef = useRef(null);
  const lastPulseCount = useRef(0);
  const phaseTitleRef = useRef(null);
  const capLabelRef = useRef(null);
  const introTextRef = useRef(null);
  const finaleTextRef = useRef(null);
  const counterRef = useRef(null);
  const narratorRef = useRef(null);
  const prevCinematicTrigger = useRef(0);
  const hemiLabelLeftRef = useRef(null);
  const hemiLabelRightRef = useRef(null);
  const hemiLabelBottomRef = useRef(null);
  const clusterLabelRefs = useRef([null, null, null, null]);
  const workerClusterLabelRefs = useRef([null, null, null]);
  const intelClusterLabelRefs = useRef([null, null, null, null]);
  const langLabelRef = useRef(null);
  const mouthTextRef = useRef(null);
  const [mouthPhrase, setMouthPhrase] = useState({ idx: -1, text: '', flag: '' });

  // Thought visualization refs
  const thoughtOverlayRef = useRef(null);
  const thoughtPhaseRef = useRef(null);
  const thoughtFiltersRef = useRef(null);
  const thoughtMemScanRef = useRef(null);
  const thoughtResponseRef = useRef(null);
  const [thoughtState, setThoughtState] = useState(null);

  // Galaxy mode state
  const [galaxyMode, setGalaxyMode] = useState(false);
  const [galaxyData, setGalaxyData] = useState(null);
  const [galaxyLoading, setGalaxyLoading] = useState(false);
  const [hoveredStar, setHoveredStar] = useState(null);
  const galaxyGroupRef = useRef(null);
  const [solarMode, setSolarMode] = useState(false);
  const [solarData, setSolarData] = useState(null);
  const [solarLoading, setSolarLoading] = useState(false);
  const [galaxySearch, setGalaxySearch] = useState('');
  const [galaxySearchResults, setGalaxySearchResults] = useState([]);
  const [galaxyCloneFilter, setGalaxyCloneFilter] = useState(null);
  const [galaxyClones, setGalaxyClones] = useState([]);
  const [galaxyCloneDropdown, setGalaxyCloneDropdown] = useState(false);
  const [galaxyCloneSearch, setGalaxyCloneSearch] = useState('');
  const solarRef = useRef(null);
  const solarCenterRef = useRef(new THREE.Vector3());
  const [hoveredMemory, setHoveredMemory] = useState(null);
  const [pinnedMemory, setPinnedMemory] = useState(null);
  const hoveredMemoryMeshRef = useRef(null);
  const pinnedMemoryMeshRef = useRef(null);
  const isPinnedRef = useRef(false);
  const solarTooltipRef = useRef(null);
  const [solarTypeFilter, setSolarTypeFilter] = useState(null);
  const solarTransitionRef = useRef({ phase: 'none', startTime: 0 });

  // Timelapse state
  const [timelapseMode, setTimelapseMode] = useState(false);
  const [timelapseData, setTimelapseData] = useState(null);
  const [timelapsePlaying, setTimelapsePlaying] = useState(false);
  const [timelapseProgress, setTimelapseProgress] = useState(0); // 0-1
  const [timelapseWeekIdx, setTimelapseWeekIdx] = useState(0);
  const [timelapseMilestone, setTimelapseMilestone] = useState(null);
  const timelapseRef = useRef({ animId: null, startTime: 0 });

  // Network mode state
  const [networkMode, setNetworkMode] = useState(false);
  const [networkData, setNetworkData] = useState(null);
  const [networkLoading, setNetworkLoading] = useState(false);
  const [networkClusterDetail, setNetworkClusterDetail] = useState(null);
  const [networkHoveredCluster, setNetworkHoveredCluster] = useState(null);
  const networkRef = useRef(null);

  // Worker mode state
  const [workerMode, setWorkerMode] = useState(false);
  const [workerData, setWorkerData] = useState(null);
  const [workerStats, setWorkerStats] = useState(null);
  const [workerLoading, setWorkerLoading] = useState(false);
  const [workerHoveredTask, setWorkerHoveredTask] = useState(null);
  const workerRef = useRef(null);
  const workerSeenTaskIds = useRef(new Set());

  // Galaxy mode: fetch data and enter
  const enterGalaxy = useCallback(async (personId = null) => {
    if (galaxyLoading) return;
    setGalaxyLoading(true);
    try {
      const url = personId
        ? `${process.env.REACT_APP_BACKEND_URL}/api/dashboard/brain-galaxy?person_id=${encodeURIComponent(personId)}`
        : `${process.env.REACT_APP_BACKEND_URL}/api/dashboard/brain-galaxy`;
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed');
      const data = await res.json();
      setGalaxyData(data);
      setGalaxyMode(true);
      setGalaxyCloneFilter(personId);
      // Fetch clones list if not loaded yet
      if (galaxyClones.length === 0) {
        try {
          const cRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/persons`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (cRes.ok) {
            const cData = await cRes.json();
            const clones = Array.isArray(cData) ? cData : cData.persons || cData.items || [];
            setGalaxyClones(clones);
          }
        } catch (_) {}
      }
    } catch (e) {
      console.error('Galaxy fetch failed:', e);
    } finally {
      setGalaxyLoading(false);
    }
  }, [galaxyLoading, token, galaxyClones.length]);

  // Solar system: enter by contact id + position
  const enterSolarSystem = useCallback(async (contactId, centerPos) => {
    if (solarLoading || solarMode) return;
    setSolarLoading(true);
    solarCenterRef.current = centerPos ? centerPos.clone() : new THREE.Vector3();
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/dashboard/brain-galaxy/${encodeURIComponent(contactId)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed');
      const data = await res.json();
      setSolarData(data);
      setSolarMode(true);
    } catch (e) {
      console.error('Solar system fetch failed:', e);
    } finally {
      setSolarLoading(false);
    }
  }, [solarLoading, solarMode, token]);

  const exitSolarSystem = useCallback(() => {
    setSolarMode(false);
    setSolarData(null);
    setGalaxySearch('');
    setGalaxySearchResults([]);
    setHoveredMemory(null);
    setPinnedMemory(null);
    setSolarTypeFilter(null);
    hoveredMemoryMeshRef.current = null;
    pinnedMemoryMeshRef.current = null;
    isPinnedRef.current = false;
    solarTransitionRef.current = { phase: 'none', startTime: 0 };
  }, []);

  // ── TIMELAPSE: Fetch data and launch cinematic ──
  const TIMELAPSE_DURATION = 25; // seconds for full cinematic
  const startTimelapse = useCallback(async () => {
    if (timelapsePlaying || !galaxyMode || solarMode) return;
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/dashboard/brain-timelapse`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed');
      const data = await res.json();
      setTimelapseData(data);
      setTimelapseMode(true);
      setTimelapsePlaying(true);
      setTimelapseProgress(0);
      setTimelapseWeekIdx(0);
      setTimelapseMilestone(null);

      // Hide all galaxy particles initially
      const sr = sceneRef.current;
      if (sr?.galaxy) {
        sr.galaxy.points.material.opacity = 0;
        sr.galaxy.starData.forEach(s => {
          s.star.material.opacity = 0;
          s.halo.material.opacity = 0;
        });
      }

      // Flag first visit as played
      const cloneId = sr?.galaxy?.cloneId || 'default';
      localStorage.setItem(`timelapse_played_${cloneId}`, 'true');

      // Start animation
      const weeks = data.weeks || [];
      const totalWeeks = weeks.length;
      if (totalWeeks === 0) { setTimelapsePlaying(false); return; }
      const startTime = performance.now();
      timelapseRef.current.startTime = startTime;

      const animateTimelapse = () => {
        const elapsed = (performance.now() - startTime) / 1000;
        const progress = Math.min(elapsed / TIMELAPSE_DURATION, 1);
        const weekIdx = Math.min(Math.floor(progress * totalWeeks), totalWeeks - 1);
        const currentWeek = weeks[weekIdx];
        const maxCumulative = weeks[totalWeeks - 1].cumulative || 1;

        setTimelapseProgress(progress);
        setTimelapseWeekIdx(weekIdx);

        // Reveal particles proportionally
        if (sr?.galaxy) {
          const targetOpacity = Math.min((currentWeek.cumulative / maxCumulative) * 0.85, 0.85);
          sr.galaxy.points.material.opacity = targetOpacity;

          // Reveal stars progressively based on cumulative contacts
          const totalStars = sr.galaxy.starData.length;
          const contactRatio = (currentWeek.total_contacts || 0) / (weeks[totalWeeks - 1].total_contacts || 1);
          const starsToShow = Math.floor(contactRatio * totalStars);
          sr.galaxy.starData.forEach((s, i) => {
            const visible = i < starsToShow;
            s.star.material.opacity = visible ? 0.6 : 0;
            s.halo.material.opacity = visible ? 0.08 : 0;
          });
        }

        // Check for milestone at this week
        const milestones = data.milestones || [];
        const weekDate = currentWeek.date;
        const activeMilestone = milestones.find(m => m.date === weekDate);
        if (activeMilestone && activeMilestone.label !== timelapseMilestone?.label) {
          setTimelapseMilestone(activeMilestone);
          // Auto-clear milestone after 2.5s
          setTimeout(() => setTimelapseMilestone(prev => prev === activeMilestone ? null : prev), 2500);
        }

        if (progress < 1) {
          timelapseRef.current.animId = requestAnimationFrame(animateTimelapse);
        } else {
          // Finished: restore full visibility
          if (sr?.galaxy) {
            sr.galaxy.points.material.opacity = 0.85;
            sr.galaxy.starData.forEach(s => {
              s.star.material.opacity = 0.6;
              s.halo.material.opacity = 0.08;
            });
          }
          setTimelapsePlaying(false);
        }
      };
      timelapseRef.current.animId = requestAnimationFrame(animateTimelapse);
    } catch (e) {
      console.error('Timelapse fetch failed:', e);
      setTimelapsePlaying(false);
    }
  }, [timelapsePlaying, galaxyMode, solarMode, token, timelapseMilestone]);

  const skipTimelapse = useCallback(() => {
    if (timelapseRef.current.animId) {
      cancelAnimationFrame(timelapseRef.current.animId);
    }
    // Restore full galaxy
    const sr = sceneRef.current;
    if (sr?.galaxy) {
      sr.galaxy.points.material.opacity = 0.85;
      sr.galaxy.starData.forEach(s => {
        s.star.material.opacity = 0.6;
        s.halo.material.opacity = 0.08;
      });
    }
    setTimelapsePlaying(false);
    setTimelapseProgress(1);
    setTimelapseWeekIdx(timelapseData ? (timelapseData.weeks || []).length - 1 : 0);
    setTimelapseMilestone(null);
  }, [timelapseData]);

  const exitTimelapse = useCallback(() => {
    skipTimelapse();
    setTimelapseMode(false);
    setTimelapseData(null);
    setTimelapseProgress(0);
    setTimelapseWeekIdx(0);
  }, [skipTimelapse]);

  // Auto-play timelapse on first galaxy visit
  useEffect(() => {
    if (galaxyMode && galaxyData && !solarMode && !timelapseMode) {
      const cloneId = 'default';
      const played = localStorage.getItem(`timelapse_played_${cloneId}`);
      if (!played) {
        // Small delay so galaxy renders first
        const timer = setTimeout(() => startTimelapse(), 1500);
        return () => clearTimeout(timer);
      }
    }
  }, [galaxyMode, galaxyData, solarMode, timelapseMode, startTimelapse]);

  // ── IRM COGNITIVE: Intelligence mode ──
  const [irmMode, setIrmMode] = useState(false);
  const [irmData, setIrmData] = useState(null);
  const [irmLoading, setIrmLoading] = useState(false);
  const [irmCloneSelector, setIrmCloneSelector] = useState(false);
  const [irmClones, setIrmClones] = useState([]);
  const [irmSelectedClone, setIrmSelectedClone] = useState(null);
  const [irmReplayIdx, setIrmReplayIdx] = useState(-1);
  const [irmQuestion, setIrmQuestion] = useState('');
  const [irmAsking, setIrmAsking] = useState(false);
  const [irmResponse, setIrmResponse] = useState(null);
  const [irmPresMode, setIrmPresMode] = useState(false);
  const [irmTypewriterText, setIrmTypewriterText] = useState('');
  const [irmActivatedCaps, setIrmActivatedCaps] = useState([]);
  const irmRef = useRef(null);
  const irmTypewriterRef = useRef(null);

  const askIrmQuestion = useCallback(async (question) => {
    if (!question.trim() || irmAsking || !irmSelectedClone) return;
    setIrmAsking(true);
    setIrmResponse(null);
    setIrmTypewriterText('');
    setIrmQuestion('');
    if (irmTypewriterRef.current) clearInterval(irmTypewriterRef.current);
    try {
      const sessionId = `irm_${irmSelectedClone}_${Date.now()}`;
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/persons/${encodeURIComponent(irmSelectedClone)}/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ message: question, session_id: sessionId }),
      });
      if (!res.ok) throw new Error('Chat failed');
      const data = await res.json();
      const fullText = data.response || data.text || '';
      const respObj = { question, response: fullText, sources_count: data.sources?.length || 0 };
      setIrmResponse(respObj);
      // Typewriter effect for presentation mode
      if (irmPresMode) {
        let idx = 0;
        setIrmTypewriterText('');
        irmTypewriterRef.current = setInterval(() => {
          idx += 2;
          if (idx >= fullText.length) { setIrmTypewriterText(fullText); clearInterval(irmTypewriterRef.current); }
          else setIrmTypewriterText(fullText.slice(0, idx));
        }, 25);
      }
    } catch (e) {
      console.error('IRM chat failed:', e);
      setIrmResponse({ question, response: 'Erreur lors de la génération de la réponse.', error: true });
    } finally {
      setIrmAsking(false);
    }
  }, [irmAsking, irmSelectedClone, token, irmPresMode]);

  const enterNetwork = useCallback(async () => {
    if (irmLoading || irmMode || galaxyMode || workerMode) return;
    // Show clone selector first
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/persons`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        const clones = Array.isArray(data) ? data : data.persons || data.items || [];
        setIrmClones(clones);
      }
    } catch (e) {
      console.error('Failed to fetch clones:', e);
    }
    setIrmCloneSelector(true);
  }, [irmLoading, irmMode, galaxyMode, workerMode, token]);

  const enterIrm = useCallback(async (personId) => {
    setIrmLoading(true);
    setIrmCloneSelector(false);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/dashboard/clone-intelligence/${encodeURIComponent(personId)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed');
      const data = await res.json();
      setIrmData(data);
      setIrmSelectedClone(personId);
      setIrmMode(true);
      setIrmReplayIdx(-1);
    } catch (e) {
      console.error('IRM fetch failed:', e);
    } finally {
      setIrmLoading(false);
    }
  }, [token]);

  const switchIrmClone = useCallback(async (personId) => {
    if (irmRef.current) {
      irmRef.current.dispose();
      irmRef.current = null;
    }
    setIrmData(null);
    await enterIrm(personId);
  }, [enterIrm]);

  const exitIrm = useCallback(() => {
    if (irmRef.current) {
      irmRef.current.dispose();
      irmRef.current = null;
    }
    const sr = sceneRef.current;
    if (sr) { sr.irmScene = null; if (sr.controls) { sr.controls.autoRotate = false; sr.controls.autoRotateSpeed = 0; } }
    setIrmMode(false);
    setIrmData(null);
    setIrmSelectedClone(null);
    setIrmCloneSelector(false);
    setIrmReplayIdx(-1);
    setIrmPresMode(false);
    setIrmActivatedCaps([]);
    if (irmTypewriterRef.current) clearInterval(irmTypewriterRef.current);
  }, []);

  // ── Auto-rotate camera in Presentation mode ──
  useEffect(() => {
    const sr = sceneRef.current;
    if (!sr || !sr.controls || !irmMode) return;
    if (irmPresMode) {
      sr.controls.autoRotate = true;
      sr.controls.autoRotateSpeed = 0.4;
      sr.camera.position.set(0, 1.5, 9.5); // lower angle, slightly further
    } else {
      sr.controls.autoRotate = false;
      sr.controls.autoRotateSpeed = 0;
      sr.camera.position.set(0, 3, 9);
    }
    sr.controls.update();
  }, [irmPresMode, irmMode]);


  const exitNetwork = useCallback(() => {
    if (networkRef.current) {
      networkRef.current.dispose();
      networkRef.current = null;
    }
    setNetworkMode(false);
    setNetworkData(null);
    setNetworkClusterDetail(null);
    setNetworkHoveredCluster(null);
  }, []);

  const enterClusterDetail = useCallback(async (cluster) => {
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/dashboard/brain-network/${encodeURIComponent(cluster.key)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed');
      const data = await res.json();
      setNetworkClusterDetail({ ...data, cluster });
    } catch (e) {
      console.error('Cluster detail fetch failed:', e);
    }
  }, [token]);

  // ── WORKER MODE: Workers en Action ──
  const enterWorkerMode = useCallback(async () => {
    if (workerLoading || workerMode || galaxyMode || networkMode) return;
    setWorkerLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/dashboard/worker-stats`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed');
      const data = await res.json();
      setWorkerData(data);
      setWorkerMode(true);
    } catch (e) {
      console.error('Worker stats fetch failed:', e);
    } finally {
      setWorkerLoading(false);
    }
  }, [workerLoading, workerMode, galaxyMode, networkMode, token]);

  const exitWorkerMode = useCallback(() => {
    if (workerRef.current) {
      workerRef.current.dispose();
      workerRef.current = null;
    }
    setWorkerMode(false);
    setWorkerData(null);
    setWorkerStats(null);
    setWorkerHoveredTask(null);
    workerSeenTaskIds.current.clear();
  }, []);

  // ── Worker mode: auto-refresh stats + inject new tasks ──
  useEffect(() => {
    if (!workerMode) return;
    const interval = setInterval(async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/dashboard/worker-stats`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return;
        const data = await res.json();
        setWorkerStats(data);
        const newTasks = (data.recent_tasks || []).filter(t => t.id && !workerSeenTaskIds.current.has(t.id));
        if (workerRef.current && newTasks.length > 0) {
          newTasks.forEach(t => {
            workerSeenTaskIds.current.add(t.id);
            workerRef.current.spawnTask(t);
          });
        }
      } catch (e) { /* silent */ }
    }, 20000);
    return () => clearInterval(interval);
  }, [workerMode, token]);


  // Build network 3D scene when data is ready
  useEffect(() => {
    const sr = sceneRef.current;
    if (!sr || !sr.scene) return;

    if (networkMode && networkData) {
      // Hide brain elements
      if (sr.brainShell) sr.brainShell.group.visible = false;
      if (sr.leftHemi) sr.leftHemi.group.visible = false;
      if (sr.rightHemi) sr.rightHemi.group.visible = false;
      if (sr.bottomHemi) sr.bottomHemi.group.visible = false;
      if (sr.hemiConns) sr.hemiConns.group.visible = false;
      if (sr.veins) sr.veins.group.visible = false;
      if (sr.orbits) sr.orbits.group.visible = false;
      if (sr.synapses) sr.synapses.group.visible = false;
      if (sr.mouth) sr.mouth.group.visible = false;
      if (sr.constellation) sr.constellation.group.visible = false;

      // Dispose old network
      if (networkRef.current) networkRef.current.dispose();

      // Create network visualization
      const group = new THREE.Group();
      const clusters = networkData.clusters || [];
      const links = networkData.links || [];
      const clusterMeshes = [];

      // Type colors for nebula
      const typeColors = { crm_note: 0xfbbf24, call: 0x60a5fa, email: 0x34d399, sms: 0x22d3ee, whatsapp: 0xa78bfa, document: 0xf97316 };

      // Position clusters in a force-like layout (golden angle spiral)
      const clusterPositions = [];
      const maxCount = Math.max(...clusters.map(c => c.count), 1);
      clusters.forEach((c, i) => {
        const angle = i * 2.399963; // golden angle
        const r = 0.8 + Math.sqrt(i) * 0.55;
        const x = Math.cos(angle) * r;
        const z = Math.sin(angle) * r;
        const y = (Math.random() - 0.5) * 0.4;
        clusterPositions.push(new THREE.Vector3(x, y, z));

        // Nebula sphere for cluster
        const nebulaSize = 0.15 + (c.count / maxCount) * 0.4;
        const color = typeColors[c.dominant_type] || 0x8b5cf6;

        // Inner core (brighter)
        const core = new THREE.Mesh(
          new THREE.SphereGeometry(nebulaSize * 0.4, 12, 12),
          new THREE.MeshBasicMaterial({
            color, transparent: true, opacity: 0.35,
            blending: THREE.AdditiveBlending, depthWrite: false,
          })
        );
        core.position.set(x, y, z);
        group.add(core);

        // Outer nebula (dimmer, larger)
        const outer = new THREE.Mesh(
          new THREE.SphereGeometry(nebulaSize, 10, 10),
          new THREE.MeshBasicMaterial({
            color, transparent: true, opacity: 0.1,
            blending: THREE.AdditiveBlending, depthWrite: false,
          })
        );
        outer.position.set(x, y, z);
        group.add(outer);

        // Subtle haze
        const haze = new THREE.Mesh(
          new THREE.SphereGeometry(nebulaSize * 1.8, 8, 8),
          new THREE.MeshBasicMaterial({
            color, transparent: true, opacity: 0.03,
            blending: THREE.AdditiveBlending, depthWrite: false,
          })
        );
        haze.position.set(x, y, z);
        group.add(haze);

        // Invisible hitbox for raycasting
        const hitbox = new THREE.Mesh(
          new THREE.SphereGeometry(nebulaSize * 1.5, 6, 6),
          new THREE.MeshBasicMaterial({ transparent: true, opacity: 0, depthWrite: false })
        );
        hitbox.position.set(x, y, z);
        hitbox.userData = { cluster: c };
        group.add(hitbox);

        clusterMeshes.push({ core, outer, haze, hitbox, cluster: c, pos: new THREE.Vector3(x, y, z) });
      });

      // Inter-cluster filaments (links)
      const linkLines = [];
      const maxStrength = Math.max(...links.map(l => l.strength), 1);
      links.forEach(link => {
        if (link.source >= clusterPositions.length || link.target >= clusterPositions.length) return;
        const p1 = clusterPositions[link.source];
        const p2 = clusterPositions[link.target];
        const lineGeo = new THREE.BufferGeometry().setFromPoints([p1, p2]);
        const opacity = 0.02 + (link.strength / maxStrength) * 0.08;
        const color = link.type === 'geo' ? 0x34d399 : 0x60a5fa;
        const lineMat = new THREE.LineBasicMaterial({
          color, transparent: true, opacity,
          blending: THREE.AdditiveBlending,
        });
        const line = new THREE.Line(lineGeo, lineMat);
        group.add(line);
        linkLines.push(line);
      });

      // Background dust
      const dustCount = 120;
      const dustPos = new Float32Array(dustCount * 3);
      for (let i = 0; i < dustCount; i++) {
        dustPos[i * 3] = (Math.random() - 0.5) * 14;
        dustPos[i * 3 + 1] = (Math.random() - 0.5) * 8;
        dustPos[i * 3 + 2] = (Math.random() - 0.5) * 14;
      }
      const dustGeo = new THREE.BufferGeometry();
      dustGeo.setAttribute('position', new THREE.Float32BufferAttribute(dustPos, 3));
      const dust = new THREE.Points(dustGeo, new THREE.PointsMaterial({
        color: 0x6b5ce7, size: 1.2, transparent: true, opacity: 0.08,
        blending: THREE.AdditiveBlending, depthWrite: false, sizeAttenuation: false,
      }));
      group.add(dust);

      sr.scene.add(group);

      // Camera position
      sr.camera.position.set(0, 2.5, 5.5);
      if (sr.controls) {
        sr.controls.target.set(0, 0, 0);
        sr.controls.update();
      }

      const networkObj = {
        group, clusterMeshes, linkLines, dust,
        update(t) {
          // Gentle cluster breathing
          clusterMeshes.forEach((cm, i) => {
            const pulse = 1 + Math.sin(t * 0.8 + i * 0.5) * 0.06;
            cm.core.scale.setScalar(pulse);
            cm.outer.scale.setScalar(pulse * 0.97);
            cm.core.material.opacity = 0.3 + Math.sin(t * 1.2 + i) * 0.05;
          });
          dust.rotation.y = t * 0.005;
        },
        getHitboxes() {
          return clusterMeshes.map(cm => cm.hitbox);
        },
        dispose() {
          group.traverse(child => {
            if (child.geometry) child.geometry.dispose();
            if (child.material) child.material.dispose();
          });
          sr.scene.remove(group);
        },
      };
      networkRef.current = networkObj;
      sr.networkGraph = networkObj;

    } else if (!networkMode) {
      // Restore brain elements
      if (sr.brainShell) sr.brainShell.group.visible = true;
      if (sr.leftHemi) sr.leftHemi.group.visible = true;
      if (sr.rightHemi) sr.rightHemi.group.visible = true;
      if (sr.bottomHemi) sr.bottomHemi.group.visible = true;
      if (sr.hemiConns) sr.hemiConns.group.visible = true;
      if (sr.veins) sr.veins.group.visible = true;
      if (sr.orbits) sr.orbits.group.visible = true;
      if (sr.synapses) sr.synapses.group.visible = true;
      if (sr.mouth) sr.mouth.group.visible = true;
      if (sr.constellation) sr.constellation.group.visible = true;

      if (networkRef.current) {
        networkRef.current.dispose();
        networkRef.current = null;
        sr.networkGraph = null;
      }

      // Reset camera
      sr.camera.position.set(0, 0.1, 4.5);
      if (sr.controls) {
        sr.controls.target.set(0, 0, 0);
        sr.controls.update();
      }
    }
  }, [networkMode, networkData]);

  // ── Build IRM Cognitive 3D scene ──
  useEffect(() => {
    const sr = sceneRef.current;
    if (!sr || !sr.scene) return;

    if (irmMode && irmData) {
      // Hide ALL brain elements
      if (sr.brainShell) sr.brainShell.group.visible = false;
      if (sr.leftHemi) sr.leftHemi.group.visible = false;
      if (sr.rightHemi) sr.rightHemi.group.visible = false;
      if (sr.bottomHemi) sr.bottomHemi.group.visible = false;
      if (sr.hemiConns) sr.hemiConns.group.visible = false;
      if (sr.veins) sr.veins.group.visible = false;
      if (sr.orbits) sr.orbits.group.visible = false;
      if (sr.synapses) sr.synapses.group.visible = false;
      if (sr.mouth) sr.mouth.group.visible = false;
      if (sr.constellation) sr.constellation.group.visible = false;
      if (irmRef.current) irmRef.current.dispose();

      const group = new THREE.Group();
      const caps = irmData.capabilities || {};
      const synData = irmData.synapses || [];
      const thoughts = irmData.recent_thoughts || [];

      const FAMILY_COLORS = {
        comprehension: 0x60a5fa, analysis: 0x22d3ee,
        personality: 0xfbbf24, detection: 0x34d399, collaboration: 0xa78bfa,
      };

      // Sort capabilities by weight → inner orbit (top 5) + outer orbit (rest)
      const CAP_KEYS = Object.keys(caps);
      const sorted = [...CAP_KEYS].sort((a, b) => (caps[b].weight || 0) - (caps[a].weight || 0));
      const innerKeys = sorted.slice(0, Math.min(5, sorted.length));
      const outerKeys = sorted.slice(Math.min(5, sorted.length));
      const neuronMap = {};
      const neuronPositions = {};
      const INNER_R = 2.4;
      const OUTER_R = 4.5;

      // ── Central Nucleus ──
      const nucleusCore = new THREE.Mesh(
        new THREE.IcosahedronGeometry(0.3, 1),
        new THREE.MeshBasicMaterial({ color: 0x8b5cf6, wireframe: true, transparent: true, opacity: 0.12 })
      );
      group.add(nucleusCore);
      const nucleusGlow = new THREE.Mesh(
        new THREE.SphereGeometry(0.55, 16, 16),
        new THREE.MeshBasicMaterial({ color: 0x8b5cf6, transparent: true, opacity: 0.04, blending: THREE.AdditiveBlending, depthWrite: false })
      );
      group.add(nucleusGlow);
      const nucleusOuter = new THREE.Mesh(
        new THREE.SphereGeometry(0.85, 12, 12),
        new THREE.MeshBasicMaterial({ color: 0x6366f1, transparent: true, opacity: 0.015, blending: THREE.AdditiveBlending, depthWrite: false })
      );
      group.add(nucleusOuter);

      // ── Orbit rings (dashed) ──
      [INNER_R, OUTER_R].forEach((r, ri) => {
        const ringPts = [];
        for (let i = 0; i <= 128; i++) {
          const a = (i / 128) * Math.PI * 2;
          ringPts.push(new THREE.Vector3(Math.cos(a) * r, 0, Math.sin(a) * r));
        }
        const rGeo = new THREE.BufferGeometry().setFromPoints(ringPts);
        const rMat = new THREE.LineDashedMaterial({
          color: ri === 0 ? 0x8b5cf6 : 0x4b5563, transparent: true,
          opacity: ri === 0 ? 0.1 : 0.05, dashSize: 0.2, gapSize: 0.12, depthWrite: false,
        });
        const rLine = new THREE.Line(rGeo, rMat);
        rLine.computeLineDistances();
        rLine.rotation.x = Math.PI / 2;
        group.add(rLine);
      });

      // ── Crystal geometry per family ──
      function makeCrystalGeo(family, size) {
        switch (family) {
          case 'comprehension': return new THREE.IcosahedronGeometry(size, 0);
          case 'analysis': return new THREE.OctahedronGeometry(size, 0);
          case 'personality': return new THREE.TorusGeometry(size * 0.65, size * 0.22, 8, 16);
          case 'detection': return new THREE.TetrahedronGeometry(size * 1.1, 0);
          case 'collaboration': return new THREE.DodecahedronGeometry(size, 0);
          default: return new THREE.IcosahedronGeometry(size, 0);
        }
      }

      function placeNeurons(keys, radius) {
        keys.forEach((key, idx) => {
          const cap = caps[key];
          const family = cap.family || 'comprehension';
          const w = cap.weight || 0;
          const angle = (idx / keys.length) * Math.PI * 2 - Math.PI / 2;
          const x = Math.cos(angle) * radius;
          const z = Math.sin(angle) * radius;
          const y = Math.sin(angle * 1.5) * 0.4;
          const pos = new THREE.Vector3(x, y, z);
          neuronPositions[key] = pos;

          const color = FAMILY_COLORS[family] || 0x60a5fa;
          const baseSize = 0.18 + w * 0.38;

          // Wireframe crystal shell
          const wireGeo = makeCrystalGeo(family, baseSize);
          const wireMat = new THREE.MeshBasicMaterial({ color, wireframe: true, transparent: true, opacity: 0.35 + w * 0.45 });
          const wireMesh = new THREE.Mesh(wireGeo, wireMat);
          wireMesh.position.copy(pos);
          group.add(wireMesh);

          // Solid inner core (low opacity glow)
          const solidGeo = makeCrystalGeo(family, baseSize * 0.75);
          const solidMat = new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0.06 + w * 0.14 });
          const solidMesh = new THREE.Mesh(solidGeo, solidMat);
          solidMesh.position.copy(pos);
          group.add(solidMesh);

          // Outer glow halo
          const haloGeo = new THREE.SphereGeometry(baseSize * 2.5, 10, 10);
          const haloMat = new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0.015 + w * 0.045, blending: THREE.AdditiveBlending, depthWrite: false });
          const halo = new THREE.Mesh(haloGeo, haloMat);
          halo.position.copy(pos);
          group.add(halo);

          // Orbiting electron particles
          const electrons = [];
          const eCount = w > 0.3 ? 3 : 2;
          for (let e = 0; e < eCount; e++) {
            const eMesh = new THREE.Mesh(
              new THREE.SphereGeometry(0.02, 6, 6),
              new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0.5 + w * 0.4, blending: THREE.AdditiveBlending })
            );
            eMesh.position.copy(pos);
            group.add(eMesh);
            electrons.push({ mesh: eMesh, orbitR: baseSize * 1.7, phase: (e / eCount) * Math.PI * 2, speed: 1.5 + Math.random() * 0.8, tilt: 0.3 + Math.random() * 0.4 });
          }

          neuronMap[key] = {
            wire: wireMesh, solid: solidMesh, halo, pos, weight: w, color, baseSize, family,
            baseWireOp: 0.35 + w * 0.45, baseSolidOp: 0.06 + w * 0.14, baseHaloOp: 0.015 + w * 0.045,
            flashIntensity: 0, dimFactor: 1, electrons,
          };
        });
      }
      placeNeurons(innerKeys, INNER_R);
      placeNeurons(outerKeys, OUTER_R);

      // ── Synapses — glowing tubes with travelling pulse particles ──
      const synapseMeshes = [];
      synData.forEach(syn => {
        const from = neuronPositions[syn.from];
        const to = neuronPositions[syn.to];
        if (!from || !to) return;
        const strength = syn.strength || 0;
        const mid = from.clone().add(to).multiplyScalar(0.5);
        mid.y += from.distanceTo(to) * 0.18;
        const curve = new THREE.QuadraticBezierCurve3(from, mid, to);
        const tubeR = 0.004 + strength * 0.014;
        const tubeGeo = new THREE.TubeGeometry(curve, 28, tubeR, 5, false);
        const fromF = caps[syn.from]?.family || 'comprehension';
        const toF = caps[syn.to]?.family || 'comprehension';
        const avgColor = new THREE.Color(FAMILY_COLORS[fromF]).lerp(new THREE.Color(FAMILY_COLORS[toF]), 0.5);
        const tubeMat = new THREE.MeshBasicMaterial({ color: avgColor, transparent: true, opacity: 0.08 + strength * 0.2, blending: THREE.AdditiveBlending, depthWrite: false });
        const tube = new THREE.Mesh(tubeGeo, tubeMat);
        group.add(tube);
        // Traveling pulse particle
        const pMesh = new THREE.Mesh(
          new THREE.SphereGeometry(0.025 + strength * 0.025, 6, 6),
          new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.4, blending: THREE.AdditiveBlending })
        );
        group.add(pMesh);
        synapseMeshes.push({ tube, tubeMat, pMesh, curve, from: syn.from, to: syn.to, strength, baseOp: 0.08 + strength * 0.2, flashIntensity: 0, pulseT: Math.random() });
      });

      // ── Shockwave ring ──
      const shockGeo = new THREE.TorusGeometry(0.1, 0.02, 8, 64);
      const shockMat = new THREE.MeshBasicMaterial({ color: 0xc084fc, transparent: true, opacity: 0, blending: THREE.AdditiveBlending, depthWrite: false });
      const shockRing = new THREE.Mesh(shockGeo, shockMat);
      shockRing.rotation.x = Math.PI / 2;
      group.add(shockRing);
      let shockProgress = -1;

      // ── Background: nebula sphere + volumetric dust + depth grid ──
      const nebula = new THREE.Mesh(
        new THREE.SphereGeometry(14, 16, 16),
        new THREE.MeshBasicMaterial({ color: 0x120828, transparent: true, opacity: 0.15, side: THREE.BackSide, depthWrite: false })
      );
      group.add(nebula);
      const dustCount = 250;
      const dustArr = new Float32Array(dustCount * 3);
      for (let i = 0; i < dustCount; i++) {
        dustArr[i * 3] = (Math.random() - 0.5) * 18;
        dustArr[i * 3 + 1] = (Math.random() - 0.5) * 12;
        dustArr[i * 3 + 2] = (Math.random() - 0.5) * 10;
      }
      const dustGeo = new THREE.BufferGeometry();
      dustGeo.setAttribute('position', new THREE.Float32BufferAttribute(dustArr, 3));
      const dust = new THREE.Points(dustGeo, new THREE.PointsMaterial({ color: 0x7c3aed, size: 0.6, transparent: true, opacity: 0.07, blending: THREE.AdditiveBlending, depthWrite: false, sizeAttenuation: false }));
      group.add(dust);
      // Subtle grid floor
      const gridPts = [];
      for (let gx = -8; gx <= 8; gx += 1.5) { gridPts.push(new THREE.Vector3(gx, -2.8, -8), new THREE.Vector3(gx, -2.8, 8)); }
      for (let gz = -8; gz <= 8; gz += 1.5) { gridPts.push(new THREE.Vector3(-8, -2.8, gz), new THREE.Vector3(8, -2.8, gz)); }
      const gridLines = new THREE.LineSegments(
        new THREE.BufferGeometry().setFromPoints(gridPts),
        new THREE.LineBasicMaterial({ color: 0x4b5563, transparent: true, opacity: 0.03, depthWrite: false })
      );
      group.add(gridLines);

      sr.scene.add(group);
      sr.camera.position.set(0, 3, 9);
      if (sr.controls) { sr.controls.target.set(0, 0, 0); sr.controls.update(); }

      // ── Replay & animation system ──
      let globalClock = 0;
      let replayTimer = 0;
      let currentReplayIdx = -1;
      const REPLAY_DELAY = 2.0;
      const THOUGHT_INTERVAL = 4.0;

      function activateThought(idx, liveThought) {
        const thought = liveThought || (idx >= 0 && idx < thoughts.length ? thoughts[idx] : null);
        if (!thought) return;
        const activated = thought.activated || [];
        const isLive = !!liveThought;
        // Track activated caps for label visibility in presentation mode
        setIrmActivatedCaps(activated);
        setTimeout(() => setIrmActivatedCaps([]), 3000);
        // Shockwave
        shockProgress = 0;
        // Dim non-activated neurons
        Object.entries(neuronMap).forEach(([k, n]) => { n.dimTarget = activated.includes(k) ? 1 : 0.08; });
        // Flash activated neurons with stagger
        activated.forEach((capKey, i) => {
          const n = neuronMap[capKey];
          if (n) setTimeout(() => { n.flashIntensity = isLive ? 1.6 : 1.0; }, i * 150);
        });
        // Flash synapses
        synapseMeshes.forEach(s => {
          if (activated.includes(s.from) && activated.includes(s.to)) s.flashIntensity = isLive ? 1.6 : 1.0;
        });
        // Nucleus pulse on live
        if (isLive) { nucleusCore.material.opacity = 0.3; nucleusGlow.material.opacity = 0.15; }
        if (liveThought) { thoughts.unshift(liveThought); if (thoughts.length > 25) thoughts.pop(); setIrmReplayIdx(0); }
        else if (idx >= 0) setIrmReplayIdx(idx);
      }

      const irmObj = {
        group, neuronMap, synapseMeshes, dust, thoughts,
        activateThought,
        update(t) {
          const dt = Math.min(t - globalClock, 0.1);
          globalClock = t;
          replayTimer += dt;

          // Auto-replay
          if (thoughts.length > 0) {
            const targetIdx = Math.floor((replayTimer - REPLAY_DELAY) / THOUGHT_INTERVAL);
            if (targetIdx >= 0 && targetIdx !== currentReplayIdx && targetIdx < thoughts.length) { currentReplayIdx = targetIdx; activateThought(targetIdx); }
            if (targetIdx >= thoughts.length) { replayTimer = 0; currentReplayIdx = -1; }
          }

          // Nucleus pulse
          const nPulse = 0.7 + Math.sin(t * 1.2) * 0.3;
          nucleusCore.material.opacity = Math.max(0.06, nucleusCore.material.opacity + (0.12 - nucleusCore.material.opacity) * dt * 2) * nPulse;
          nucleusCore.rotation.y = t * 0.15;
          nucleusCore.rotation.x = Math.sin(t * 0.1) * 0.2;
          nucleusGlow.material.opacity = Math.max(0.02, nucleusGlow.material.opacity + (0.04 - nucleusGlow.material.opacity) * dt * 2);
          nucleusGlow.scale.setScalar(1 + Math.sin(t * 0.8) * 0.1);
          nucleusOuter.scale.setScalar(1 + Math.sin(t * 0.5) * 0.06);

          // Shockwave
          if (shockProgress >= 0 && shockProgress < 1) {
            shockProgress += dt * 1.3;
            shockRing.scale.setScalar(0.5 + shockProgress * (OUTER_R + 2));
            shockMat.opacity = (1 - shockProgress) * 0.25;
          } else { shockMat.opacity = 0; }

          // Animate neurons
          Object.values(neuronMap).forEach(n => {
            const pulse = Math.sin(t * 1.5 + n.pos.x * 2) * 0.03;
            // Crystal rotation
            n.wire.rotation.y = t * 0.25 + n.pos.x;
            n.wire.rotation.x = Math.sin(t * 0.2 + n.pos.z) * 0.2;
            n.solid.rotation.copy(n.wire.rotation);
            // Dim factor lerp
            const dTarget = n.dimTarget !== undefined ? n.dimTarget : 1;
            n.dimFactor += (dTarget - n.dimFactor) * dt * 4;
            if (Math.abs(dTarget - 0.08) < 0.01 && n.flashIntensity <= 0) n.dimTarget = 1; // auto-restore
            const df = n.dimFactor;
            // Opacity
            n.wire.material.opacity = n.baseWireOp * df + n.flashIntensity * 0.5 + pulse;
            n.solid.material.opacity = n.baseSolidOp * df + n.flashIntensity * 0.15;
            n.halo.material.opacity = n.baseHaloOp * df + n.flashIntensity * 0.14;
            // Scale bounce on flash
            const s = 1 + pulse * 0.3 + n.flashIntensity * 0.45;
            n.wire.scale.setScalar(s);
            n.solid.scale.setScalar(s);
            n.halo.scale.setScalar(1 + n.flashIntensity * 0.35);
            // Flash decay
            if (n.flashIntensity > 0) n.flashIntensity = Math.max(0, n.flashIntensity - dt * 0.4);
            // Orbiting electrons
            n.electrons.forEach(el => {
              const ea = t * el.speed + el.phase;
              el.mesh.position.set(n.pos.x + Math.cos(ea) * el.orbitR, n.pos.y + Math.sin(ea * 0.7) * el.orbitR * el.tilt, n.pos.z + Math.sin(ea) * el.orbitR);
              el.mesh.material.opacity = (0.35 + n.weight * 0.35) * df;
            });
          });

          // Animate synapses
          synapseMeshes.forEach(s => {
            s.tubeMat.opacity = s.baseOp + s.flashIntensity * 0.4;
            s.pulseT = (s.pulseT + dt * (0.12 + s.strength * 0.3)) % 1;
            const pt = s.curve.getPoint(s.pulseT);
            s.pMesh.position.copy(pt);
            s.pMesh.material.opacity = (0.25 + s.strength * 0.4 + s.flashIntensity * 0.5) * (s.tubeMat.opacity > 0.06 ? 1 : 0.2);
            if (s.flashIntensity > 0) s.flashIntensity = Math.max(0, s.flashIntensity - dt * 0.4);
          });

          dust.rotation.y = t * 0.004;
          dust.rotation.x = Math.sin(t * 0.15) * 0.003;
        },
        dispose() {
          group.traverse(child => { if (child.geometry) child.geometry.dispose(); if (child.material) child.material.dispose(); });
          sr.scene.remove(group);
        },
      };
      irmRef.current = irmObj;
      sr.irmScene = irmObj;

    } else if (!irmMode) {
      if (irmRef.current) {
        irmRef.current.dispose();
        irmRef.current = null;
      }
      if (sr) sr.irmScene = null;
      // Restore brain elements (only if not in another mode)
      if (!workerMode && !networkMode && !galaxyMode) {
        if (sr.brainShell) sr.brainShell.group.visible = true;
        if (sr.leftHemi) sr.leftHemi.group.visible = true;
        if (sr.rightHemi) sr.rightHemi.group.visible = true;
        if (sr.bottomHemi) sr.bottomHemi.group.visible = true;
        if (sr.hemiConns) sr.hemiConns.group.visible = true;
        if (sr.veins) sr.veins.group.visible = true;
        if (sr.orbits) sr.orbits.group.visible = true;
        if (sr.synapses) sr.synapses.group.visible = true;
        if (sr.mouth) sr.mouth.group.visible = true;
        if (sr.constellation) sr.constellation.group.visible = true;
      }
    }
  }, [irmMode, irmData]);

  // ── IRM Live: React to thought_completed WebSocket events ──
  const lastThoughtCountRef = useRef(0);
  useEffect(() => {
    if (!irmMode || !irmRef.current || liveThoughts.length <= lastThoughtCountRef.current) return;
    const newThoughts = liveThoughts.slice(lastThoughtCountRef.current);
    lastThoughtCountRef.current = liveThoughts.length;
    newThoughts.forEach(thought => {
      if (thought.person_id && irmSelectedClone && thought.person_id !== irmSelectedClone) return;
      const irm = irmRef.current;
      if (!irm) return;
      // Add to replay queue as live thought
      const liveThought = {
        id: 'live_' + Date.now(),
        message: thought.message || '',
        response_preview: thought.response_preview || '',
        activated: thought.activated || [],
        retrieval_mode: thought.retrieval_mode || '',
        memory_count: thought.memory_count || 0,
        response_time_ms: thought.response_time_ms || 0,
        feedback: null,
        created_at: new Date().toISOString(),
        live: true,
      };
      // Flash neurons immediately
      irm.activateThought(null, liveThought);
      setIrmReplayIdx(prev => prev);
    });
  }, [liveThoughts, irmMode, irmSelectedClone]);

  // ── Build Worker 3D scene (dynamic conveyor belt V3) ──
  useEffect(() => {
    const sr = sceneRef.current;
    if (!sr || !sr.scene) return;

    if (workerMode && workerData) {
      // Hide brain shell, hemispheres, connections, mouth — but KEEP sources & veins
      if (sr.brainShell) sr.brainShell.group.visible = false;
      if (sr.leftHemi) sr.leftHemi.group.visible = false;
      if (sr.rightHemi) sr.rightHemi.group.visible = false;
      if (sr.bottomHemi) sr.bottomHemi.group.visible = false;
      if (sr.hemiConns) sr.hemiConns.group.visible = false;
      if (sr.synapses) sr.synapses.group.visible = false;
      if (sr.mouth) sr.mouth.group.visible = false;
      if (sr.constellation) sr.constellation.group.visible = false;
      // Keep orbits (sources at top) and veins visible!
      if (sr.orbits) sr.orbits.group.visible = true;
      if (sr.veins) sr.veins.group.visible = true;
      if (workerRef.current) workerRef.current.dispose();

      const group = new THREE.Group();
      const TYPE_COLORS = {
        ringover_sync: 0x60a5fa, email_sync: 0x34d399, teams_sync: 0x22d3ee,
        scan_duplicates: 0xf97316, crm_sync: 0xfbbf24, whatsapp_sync: 0xa78bfa,
      };
      // Map task types to source keys for orbit lookup
      const TASK_TO_SOURCE = {
        ringover_sync: 'call', email_sync: 'email', teams_sync: 'teams',
        crm_sync: 'crm_note', whatsapp_sync: 'whatsapp', scan_duplicates: 'crm_note',
      };
      const VEIN_TARGET = new THREE.Vector3(0, 1.5, 0);

      // Phase durations
      const VEIN_DUR = 1.6;
      const DROP_DUR = 0.6;
      const TRANSIT_DUR = 2.0;
      const PROCESS_DUR = 1.5;
      const EXIT_DUR = 1.8;
      const REPLAY_INTERVAL = 0.5;

      const BELT_LEFT = -3.5;
      const BELT_PROCESS_START = -0.8;
      const BELT_PROCESS_END = 0.8;
      const BELT_Y = -0.5;
      const BIN_CENTER = new THREE.Vector3(1.5, -2.0, 0);

      // ─── BELT ───
      const beltGeo = new THREE.BoxGeometry(8, 0.06, 1.2);
      const beltMat = new THREE.MeshBasicMaterial({ color: 0x1a1a2e, transparent: true, opacity: 0.8 });
      const belt = new THREE.Mesh(beltGeo, beltMat);
      belt.position.set(0, BELT_Y - 0.03, 0);
      group.add(belt);
      const edgeMat = new THREE.LineBasicMaterial({ color: 0x22c55e, transparent: true, opacity: 0.15 });
      [0.6, -0.6].forEach(z => {
        group.add(new THREE.Line(new THREE.BufferGeometry().setFromPoints([new THREE.Vector3(-4, BELT_Y, z), new THREE.Vector3(4, BELT_Y, z)]), edgeMat));
      });
      const scrollMarks = [];
      for (let i = 0; i < 16; i++) {
        const markGeo = new THREE.PlaneGeometry(0.02, 0.8);
        const markMat = new THREE.MeshBasicMaterial({ color: 0x22c55e, transparent: true, opacity: 0.04, side: THREE.DoubleSide });
        const mark = new THREE.Mesh(markGeo, markMat);
        mark.rotation.x = -Math.PI / 2;
        mark.position.set(-4 + i * 0.525, BELT_Y + 0.005, 0);
        group.add(mark);
        scrollMarks.push(mark);
      }

      // Processing zone
      const archGeo = new THREE.TorusGeometry(0.7, 0.015, 8, 32, Math.PI);
      const archMat = new THREE.MeshBasicMaterial({ color: 0xfbbf24, transparent: true, opacity: 0.3, blending: THREE.AdditiveBlending });
      const arch = new THREE.Mesh(archGeo, archMat);
      arch.position.set(0, BELT_Y, 0);
      group.add(arch);
      const zoneGeo = new THREE.PlaneGeometry(2.0, 1.2);
      const zoneMat = new THREE.MeshBasicMaterial({ color: 0xfbbf24, transparent: true, opacity: 0.025, side: THREE.DoubleSide, blending: THREE.AdditiveBlending });
      const zone = new THREE.Mesh(zoneGeo, zoneMat);
      zone.rotation.x = -Math.PI / 2;
      zone.position.set(0, BELT_Y + 0.01, 0);
      group.add(zone);

      // Error bin
      const binGeo = new THREE.BoxGeometry(1.6, 0.35, 0.8);
      const binMat2 = new THREE.MeshBasicMaterial({ color: 0xef4444, transparent: true, opacity: 0.06 });
      const bin = new THREE.Mesh(binGeo, binMat2);
      bin.position.copy(BIN_CENTER);
      group.add(bin);
      const binEdgeMat = new THREE.LineBasicMaterial({ color: 0xef4444, transparent: true, opacity: 0.2 });
      [[-0.4, 0.4]].forEach(([z1, z2]) => {
        [z1, z2].forEach(z => {
          group.add(new THREE.Line(new THREE.BufferGeometry().setFromPoints([
            new THREE.Vector3(BIN_CENTER.x - 0.8, BIN_CENTER.y + 0.175, z),
            new THREE.Vector3(BIN_CENTER.x - 0.8, BIN_CENTER.y - 0.175, z),
            new THREE.Vector3(BIN_CENTER.x + 0.8, BIN_CENTER.y - 0.175, z),
            new THREE.Vector3(BIN_CENTER.x + 0.8, BIN_CENTER.y + 0.175, z),
          ]), binEdgeMat));
        });
      });

      // ─── MEMORY HEMISPHERE (right side — blue/cyan like Mémoire) ───
      const MEM_POS = new THREE.Vector3(4.5, 0.2, 0);
      const memSphereGeo = new THREE.SphereGeometry(0.9, 20, 14);
      const memSphereMat = new THREE.MeshBasicMaterial({ color: 0x3b82f6, wireframe: true, transparent: true, opacity: 0.06 });
      const memSphere = new THREE.Mesh(memSphereGeo, memSphereMat);
      memSphere.position.copy(MEM_POS);
      group.add(memSphere);
      // Equator ring
      const memRingGeo = new THREE.TorusGeometry(0.92, 0.012, 8, 48);
      const memRingMat = new THREE.MeshBasicMaterial({ color: 0x60a5fa, transparent: true, opacity: 0.1 });
      const memRing = new THREE.Mesh(memRingGeo, memRingMat);
      memRing.position.copy(MEM_POS);
      memRing.rotation.x = Math.PI / 2;
      group.add(memRing);
      // Inner glow core
      const memGlowGeo = new THREE.SphereGeometry(0.6, 12, 12);
      const memGlowMat = new THREE.MeshBasicMaterial({ color: 0x60a5fa, transparent: true, opacity: 0.0, blending: THREE.AdditiveBlending, depthWrite: false });
      const memGlow = new THREE.Mesh(memGlowGeo, memGlowMat);
      memGlow.position.copy(MEM_POS);
      group.add(memGlow);
      // Fill particles (neurons) — larger, more visible
      // Fill particles (neurons) — large, visible individual spheres
      const MAX_NEURONS = 80;
      const neuronMeshes = [];
      const neuronGeo = new THREE.SphereGeometry(0.06, 6, 6);
      let neuronCount = 0;
      let memFlash = 0;
      // Inner fill sphere that grows with fill ratio
      const fillSphereGeo = new THREE.SphereGeometry(0.1, 12, 12);
      const fillSphereMat = new THREE.MeshBasicMaterial({ color: 0x60a5fa, transparent: true, opacity: 0.0, blending: THREE.AdditiveBlending, depthWrite: false });
      const fillSphere = new THREE.Mesh(fillSphereGeo, fillSphereMat);
      fillSphere.position.copy(MEM_POS);
      group.add(fillSphere);

      function addNeuron() {
        if (neuronCount >= MAX_NEURONS) return;
        const r = 0.1 + Math.random() * 0.7;
        const theta = Math.random() * Math.PI;
        const phi = Math.random() * Math.PI * 2;
        const neuronMesh = new THREE.Mesh(neuronGeo, new THREE.MeshBasicMaterial({ color: 0x93c5fd, transparent: true, opacity: 0.8, blending: THREE.AdditiveBlending, depthWrite: false }));
        neuronMesh.position.set(
          MEM_POS.x + r * Math.sin(theta) * Math.cos(phi),
          MEM_POS.y + r * Math.cos(theta) * 0.8,
          MEM_POS.z + r * Math.sin(theta) * Math.sin(phi)
        );
        group.add(neuronMesh);
        neuronMeshes.push(neuronMesh);
        neuronCount++;
        memFlash = 1.0;
      }

      // Background dust
      const dustCount = 60;
      const dustPos = new Float32Array(dustCount * 3);
      for (let i = 0; i < dustCount; i++) {
        dustPos[i * 3] = (Math.random() - 0.5) * 14;
        dustPos[i * 3 + 1] = (Math.random() - 0.5) * 6;
        dustPos[i * 3 + 2] = (Math.random() - 0.5) * 8;
      }
      const dustGeo = new THREE.BufferGeometry();
      dustGeo.setAttribute('position', new THREE.Float32BufferAttribute(dustPos, 3));
      const dust = new THREE.Points(dustGeo, new THREE.PointsMaterial({ color: 0x22c55e, size: 1.0, transparent: true, opacity: 0.05, blending: THREE.AdditiveBlending, depthWrite: false, sizeAttenuation: false }));
      group.add(dust);

      sr.scene.add(group);
      sr.camera.position.set(0.5, 2.5, 8);
      if (sr.controls) { sr.controls.target.set(0.5, 0.2, 0); sr.controls.update(); }

      // ─── DYNAMIC CUBE SYSTEM ───
      const activeCubes = [];
      let globalClock = 0;
      let replayDone = false;
      const replayTasks = [...(workerData.recent_tasks || [])].reverse();
      const replaySchedule = replayTasks.map((task, i) => ({ task, spawnAt: i * REPLAY_INTERVAL }));
      let nextReplayIdx = 0;
      let totalAbsorbed = 0;

      // Track seen task IDs to avoid duplicates on polling refresh
      workerSeenTaskIds.current.clear();
      replayTasks.forEach(t => { if (t.id) workerSeenTaskIds.current.add(t.id); });

      function getSourceWorldPos(taskType) {
        const sourceKey = TASK_TO_SOURCE[taskType] || 'call';
        const sourceObjs = sr.orbits?.sourceObjs || [];
        const so = sourceObjs.find(o => o.key === sourceKey) || sourceObjs[0];
        if (!so) return new THREE.Vector3(0, 3, 0);
        const wp = new THREE.Vector3();
        so.icon.getWorldPosition(wp);
        return wp;
      }

      function spawnCube(task) {
        const size = 0.08 + Math.min((task.items_processed || 0) / 5, 1) * 0.12;
        const geo = new THREE.BoxGeometry(size, size, size);
        const typeColor = TYPE_COLORS[task.type] || 0x8b5cf6;
        const mat = new THREE.MeshBasicMaterial({ color: typeColor, transparent: true, opacity: 0.0 });
        const mesh = new THREE.Mesh(geo, mat);
        const srcPos = getSourceWorldPos(task.type);
        mesh.position.copy(srcPos);
        group.add(mesh);
        const hitGeo = new THREE.BoxGeometry(size * 2.5, size * 2.5, size * 2.5);
        const hitbox = new THREE.Mesh(hitGeo, new THREE.MeshBasicMaterial({ visible: false }));
        hitbox.position.copy(mesh.position);
        group.add(hitbox);
        const zOff = (Math.random() - 0.5) * 0.6;
        const beltLandX = BELT_LEFT + Math.random() * 0.8;
        // Trigger vein pulse for visual feedback
        const sourceKey = TASK_TO_SOURCE[task.type] || 'call';
        if (sr.veins) sr.veins.triggerPulse(sourceKey);
        activeCubes.push({ mesh, hitbox, task, typeColor, size, zOff, beltLandX, srcPos: srcPos.clone(), spawnTime: globalClock, phase: 'vein', trail: null });
      }

      function bezierPoint(p0, p1, p2, p3, t) {
        const u = 1 - t;
        return new THREE.Vector3(
          u*u*u*p0.x + 3*u*u*t*p1.x + 3*u*t*t*p2.x + t*t*t*p3.x,
          u*u*u*p0.y + 3*u*u*t*p1.y + 3*u*t*t*p2.y + t*t*t*p3.y,
          u*u*u*p0.z + 3*u*u*t*p1.z + 3*u*t*t*p2.z + t*t*t*p3.z,
        );
      }

      function updateCube(c, dt) {
        const age = globalClock - c.spawnTime;
        const isFailed = c.task.status === 'failed';

        if (c.phase === 'vein') {
          // Travel from source through vein to vein target (brain center)
          const t = Math.min(age / VEIN_DUR, 1);
          const eased = t * t * (3 - 2 * t);
          const mid = c.srcPos.clone().lerp(VEIN_TARGET, 0.5);
          mid.x += (c.srcPos.z > 0 ? 0.3 : -0.3);
          c.mesh.position.lerpVectors(c.srcPos, VEIN_TARGET, eased);
          c.mesh.position.x += Math.sin(eased * Math.PI) * (mid.x - (c.srcPos.x + VEIN_TARGET.x) / 2) * 0.3;
          c.mesh.material.opacity = 0.15 + eased * 0.75;
          c.mesh.rotation.y = eased * Math.PI * 1.5;
          c.mesh.scale.setScalar(0.5 + eased * 0.5);
          if (t >= 1) c.phase = 'drop';
        } else if (c.phase === 'drop') {
          // Drop from vein target to belt
          const t = Math.min((age - VEIN_DUR) / DROP_DUR, 1);
          const eased = t * t; // gravity-like
          const dropStart = VEIN_TARGET.clone();
          const dropEnd = new THREE.Vector3(c.beltLandX, BELT_Y + c.size / 2, c.zOff);
          c.mesh.position.lerpVectors(dropStart, dropEnd, eased);
          c.mesh.rotation.y += dt * 3;
          if (t >= 1) c.phase = 'transit';
        } else if (c.phase === 'transit') {
          const tOff = VEIN_DUR + DROP_DUR;
          const t = Math.min((age - tOff) / TRANSIT_DUR, 1);
          const eased = t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
          c.mesh.position.x = c.beltLandX + eased * (BELT_PROCESS_START - c.beltLandX);
          c.mesh.position.y = BELT_Y + c.size / 2;
          c.mesh.position.z = c.zOff * (1 - eased * 0.5);
          if (t >= 1) c.phase = 'process';
        } else if (c.phase === 'process') {
          const tOff = VEIN_DUR + DROP_DUR + TRANSIT_DUR;
          const t = Math.min((age - tOff) / PROCESS_DUR, 1);
          c.mesh.position.x = BELT_PROCESS_START + t * (BELT_PROCESS_END - BELT_PROCESS_START);
          c.mesh.scale.setScalar(1 + Math.sin(t * Math.PI * 3) * 0.12);
          c.mesh.rotation.y += dt * 2;
          if (t > 0.3 && t < 0.7) {
            const tc = new THREE.Color(c.typeColor);
            c.mesh.material.color.copy(tc.lerp(new THREE.Color(0xfbbf24), Math.sin((t - 0.3) / 0.4 * Math.PI) * 0.3));
          }
          if (t >= 1) {
            c.mesh.scale.setScalar(1);
            c.mesh.material.color.set(c.typeColor);
            c.phase = isFailed ? 'fall' : 'fly';
            c.exitStart = globalClock;
            c.flyOrigin = c.mesh.position.clone();
          }
        } else if (c.phase === 'fly') {
          const t = Math.min((globalClock - c.exitStart) / EXIT_DUR, 1);
          const eased = t * t * (3 - 2 * t);
          const p0 = c.flyOrigin;
          const pos = bezierPoint(p0, new THREE.Vector3(p0.x + 1.2, p0.y + 1.2, c.zOff * 0.3), new THREE.Vector3(MEM_POS.x - 0.8, MEM_POS.y + 0.6, 0), MEM_POS, eased);
          c.mesh.position.copy(pos);
          c.mesh.scale.setScalar(1 - eased * 0.8);
          c.mesh.material.opacity = 0.9 - eased * 0.5;
          c.mesh.material.color.lerp(new THREE.Color(0x60a5fa), eased * 0.5);
          c.mesh.rotation.y += dt * 4;
          c.mesh.rotation.x += dt * 2;
          if (t > 0.15 && Math.random() < 0.2) {
            if (!c.trail) c.trail = [];
            const tGeo = new THREE.SphereGeometry(0.008, 4, 4);
            const tp = new THREE.Mesh(tGeo, new THREE.MeshBasicMaterial({ color: 0x60a5fa, transparent: true, opacity: 0.3, blending: THREE.AdditiveBlending, depthWrite: false }));
            tp.position.copy(pos);
            tp._life = 0.6;
            group.add(tp);
            c.trail.push(tp);
          }
          if (t >= 1) { c.phase = 'done'; c.mesh.visible = false; c.hitbox.visible = false; addNeuron(); totalAbsorbed++; }
        } else if (c.phase === 'fall') {
          const t = Math.min((globalClock - c.exitStart) / (EXIT_DUR * 0.6), 1);
          if (!c.binTarget) {
            c.binTarget = new THREE.Vector3(
              BIN_CENTER.x + (Math.random() - 0.5) * 1.2,
              BIN_CENTER.y + (Math.random() - 0.5) * 0.15,
              BIN_CENTER.z + (Math.random() - 0.5) * 0.5
            );
          }
          c.mesh.position.x = c.flyOrigin.x + (c.binTarget.x - c.flyOrigin.x) * t;
          c.mesh.position.y = c.flyOrigin.y + (c.binTarget.y - c.flyOrigin.y) * t * t;
          c.mesh.position.z = c.zOff * (1 - t) + c.binTarget.z * t;
          c.mesh.rotation.x += dt * 5;
          c.mesh.rotation.z += dt * 3;
          c.mesh.material.color.set(0xef4444);
          c.mesh.material.opacity = 0.9;
          c.mesh.scale.setScalar(1.2);
          if (t >= 1) {
            c.phase = 'dead';
            // Leave a visible resting cube in the bin
            const restGeo = new THREE.BoxGeometry(c.size * 1.5, c.size * 1.5, c.size * 1.5);
            const restMat = new THREE.MeshBasicMaterial({ color: 0xef4444, transparent: true, opacity: 0.7 });
            const restMesh = new THREE.Mesh(restGeo, restMat);
            restMesh.position.copy(c.binTarget);
            restMesh.rotation.set(Math.random() * Math.PI, Math.random() * Math.PI, Math.random() * Math.PI);
            group.add(restMesh);
            c.mesh.visible = false;
          }
        }
        c.hitbox.position.copy(c.mesh.position);
        if (c.trail) {
          c.trail.forEach(tp => { tp._life -= dt * 1.2; tp.material.opacity = Math.max(0, tp._life * 0.3); tp.position.y += dt * 0.05; if (tp._life <= 0) { group.remove(tp); tp.geometry.dispose(); tp.material.dispose(); } });
          c.trail = c.trail.filter(tp => tp._life > 0);
        }
      }

      const raycaster = new THREE.Raycaster();
      const mouse = new THREE.Vector2();
      function getHoveredCube(event) {
        const canvas = canvasRef.current;
        if (!canvas) return null;
        const rect = canvas.getBoundingClientRect();
        mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
        mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
        raycaster.setFromCamera(mouse, sr.camera);
        const hits = raycaster.intersectObjects(activeCubes.filter(c => c.phase !== 'done').map(c => c.hitbox));
        return hits.length > 0 ? activeCubes.find(c => c.hitbox === hits[0].object) || null : null;
      }

      const workerObj = {
        group, activeCubes, scrollMarks, arch, zone, dust, belt,
        memSphere, memGlow, neuronMeshes, MEM_POS,
        neuronCount: () => neuronCount,
        totalAbsorbed: () => totalAbsorbed,
        getHoveredCube,
        spawnTask(task) { spawnCube(task); },
        spawnFromPulse(pulse) {
          spawnCube({
            id: 'live_' + Date.now(),
            type: pulse.source?.includes('ringover') ? 'ringover_sync' : pulse.source?.includes('email') ? 'email_sync' : pulse.source?.includes('teams') ? 'teams_sync' : pulse.type === 'call' ? 'ringover_sync' : pulse.type === 'email' ? 'email_sync' : 'crm_sync',
            status: 'completed', items_processed: 1, items_created: 1, started_at: new Date().toISOString(),
          });
        },
        update(t) {
          const dt = t - globalClock;
          globalClock = t;
          if (!replayDone) {
            while (nextReplayIdx < replaySchedule.length && replaySchedule[nextReplayIdx].spawnAt <= globalClock) { spawnCube(replaySchedule[nextReplayIdx].task); nextReplayIdx++; }
            if (nextReplayIdx >= replaySchedule.length) replayDone = true;
          }
          activeCubes.forEach(c => updateCube(c, dt));
          scrollMarks.forEach((m, i) => { m.position.x = -4 + ((i * 0.525 + t * 0.25) % 8.4); });
          const hasProc = activeCubes.some(c => c.phase === 'process');
          arch.material.opacity = (hasProc ? 0.35 : 0.12) + Math.sin(t * 2) * 0.08;
          zone.material.opacity = (hasProc ? 0.035 : 0.01) + Math.sin(t * 2) * 0.008;
          // Memory hemisphere fill feedback
          const fillRatio = neuronCount / MAX_NEURONS;
          memGlowMat.opacity = fillRatio * 0.3 + memFlash * 0.4;
          memGlow.scale.setScalar(0.3 + fillRatio * 0.9 + memFlash * 0.2);
          memSphereMat.opacity = 0.06 + fillRatio * 0.25 + memFlash * 0.15;
          memSphereMat.color.setHex(fillRatio > 0.3 ? 0x60a5fa : 0x3b82f6);
          memRingMat.opacity = 0.1 + fillRatio * 0.25 + memFlash * 0.2;
          memSphere.scale.setScalar(1 + fillRatio * 0.15 + memFlash * 0.05);
          // Inner fill sphere grows with neuron count
          fillSphereMat.opacity = fillRatio * 0.15 + memFlash * 0.1;
          fillSphere.scale.setScalar(fillRatio * 8.0 + memFlash * 0.5);
          // Neuron pulse
          neuronMeshes.forEach((nm, i) => {
            nm.material.opacity = 0.6 + Math.sin(t * 2 + i * 0.5) * 0.2 + memFlash * 0.3;
            nm.scale.setScalar(1 + Math.sin(t * 3 + i * 0.7) * 0.15);
          });
          if (memFlash > 0) memFlash = Math.max(0, memFlash - dt * 2.0);
          dust.rotation.y = t * 0.002;
        },
        dispose() {
          group.traverse(child => { if (child.geometry) child.geometry.dispose(); if (child.material) child.material.dispose(); });
          sr.scene.remove(group);
        },
      };
      workerRef.current = workerObj;
      sr.workerScene = workerObj;

    } else if (!workerMode) {
      // Restore brain elements
      if (sr.brainShell) sr.brainShell.group.visible = true;
      if (sr.leftHemi) sr.leftHemi.group.visible = true;
      if (sr.rightHemi) sr.rightHemi.group.visible = true;
      if (sr.bottomHemi) sr.bottomHemi.group.visible = true;
      if (sr.hemiConns) sr.hemiConns.group.visible = true;
      if (sr.veins) sr.veins.group.visible = true;
      if (sr.orbits) sr.orbits.group.visible = true;
      if (sr.synapses) sr.synapses.group.visible = true;
      if (sr.mouth) sr.mouth.group.visible = true;
      if (sr.constellation) sr.constellation.group.visible = true;

      if (workerRef.current) {
        workerRef.current.dispose();
        workerRef.current = null;
        sr.workerScene = null;
      }

      sr.camera.position.set(0, 0.1, 4.5);
      if (sr.controls) {
        sr.controls.target.set(0, 0, 0);
        sr.controls.update();
      }
    }
  }, [workerMode, workerData]);

  // Worker: mouse move for cube hover tooltip
  const handleWorkerMouseMove = useCallback((event) => {
    if (!workerMode || !workerRef.current) return;
    const hovered = workerRef.current.getHoveredCube(event);
    if (hovered) {
      setWorkerHoveredTask(hovered.task);
    } else {
      setWorkerHoveredTask(null);
    }
  }, [workerMode]);

  // Worker: listen for new pulses → spawn cubes in real-time
  useEffect(() => {
    if (!workerMode || !workerRef.current) return;
    if (pulses.length === 0) return;
    const latest = pulses[pulses.length - 1];
    if (latest && latest.event === 'memory_created') {
      workerRef.current.spawnFromPulse(latest);
    }
  }, [workerMode, pulses]);

  // Network: mouse move for cluster hover
  const handleNetworkMouseMove = useCallback((event) => {
    if (!networkMode || !networkRef.current) return;
    const sr = sceneRef.current;
    if (!sr) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const mouse = new THREE.Vector2(
      ((event.clientX - rect.left) / rect.width) * 2 - 1,
      -((event.clientY - rect.top) / rect.height) * 2 + 1,
    );
    const raycaster = new THREE.Raycaster();
    raycaster.setFromCamera(mouse, sr.camera);
    const hitboxes = networkRef.current.getHitboxes();
    const hits = raycaster.intersectObjects(hitboxes);
    if (hits.length > 0) {
      const cluster = hits[0].object.userData.cluster;
      setNetworkHoveredCluster(cluster);
    } else {
      setNetworkHoveredCluster(null);
    }
  }, [networkMode]);

  // Network: click to zoom into cluster
  const handleNetworkClick = useCallback((event) => {
    if (!networkMode || !networkRef.current) return;
    const sr = sceneRef.current;
    if (!sr) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const mouse = new THREE.Vector2(
      ((event.clientX - rect.left) / rect.width) * 2 - 1,
      -((event.clientY - rect.top) / rect.height) * 2 + 1,
    );
    const raycaster = new THREE.Raycaster();
    raycaster.setFromCamera(mouse, sr.camera);
    const hitboxes = networkRef.current.getHitboxes();
    const hits = raycaster.intersectObjects(hitboxes);
    if (hits.length > 0) {
      const cluster = hits[0].object.userData.cluster;
      enterClusterDetail(cluster);
    }
  }, [networkMode, enterClusterDetail]);

  // Galaxy canvas click → raycasting on stars
  const handleGalaxyClick = useCallback((event) => {
    const sr = sceneRef.current;
    if (!galaxyMode || solarMode || !sr?.galaxy) return;
    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const mouse = new THREE.Vector2(
      ((event.clientX - rect.left) / rect.width) * 2 - 1,
      -((event.clientY - rect.top) / rect.height) * 2 + 1,
    );
    const raycaster = new THREE.Raycaster();
    raycaster.setFromCamera(mouse, sr.camera);

    // Raycast against halo meshes (larger hit area)
    const halos = sr.galaxy.starData.map(s => s.halo);
    const hits = raycaster.intersectObjects(halos);
    if (hits.length > 0) {
      const idx = halos.indexOf(hits[0].object);
      if (idx >= 0) {
        const starInfo = sr.galaxy.starData[idx];
        const contactId = (galaxyData?.top_contacts || [])[idx]?.id;
        if (contactId) {
          enterSolarSystem(contactId, starInfo.position);
        }
      }
    }
  }, [galaxyMode, solarMode, galaxyData, enterSolarSystem]);

  // Solar system: mousemove raycasting for memory tooltips
  const handleSolarMouseMove = useCallback((event) => {
    const sr = sceneRef.current;
    if (!solarMode || !sr?.solarSystem) {
      if (hoveredMemoryMeshRef.current) { hoveredMemoryMeshRef.current = null; setHoveredMemory(null); }
      return;
    }
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const mouse = new THREE.Vector2(
      ((event.clientX - rect.left) / rect.width) * 2 - 1,
      -((event.clientY - rect.top) / rect.height) * 2 + 1,
    );
    const raycaster = new THREE.Raycaster();
    raycaster.setFromCamera(mouse, sr.camera);
    const hitboxes = sr.solarSystem.memoryMeshes.map(m => m.hitbox || m.mesh);
    const hits = raycaster.intersectObjects(hitboxes);
    if (hits.length > 0) {
      const idx = hitboxes.indexOf(hits[0].object);
      if (idx >= 0 && sr.solarSystem.memoryMeshes[idx].mesh !== hoveredMemoryMeshRef.current) {
        hoveredMemoryMeshRef.current = sr.solarSystem.memoryMeshes[idx].mesh;
        setHoveredMemory(sr.solarSystem.memoryMeshes[idx].mem);
      }
    } else if (hoveredMemoryMeshRef.current) {
      hoveredMemoryMeshRef.current = null;
      if (!isPinnedRef.current) setHoveredMemory(null);
    }
  }, [solarMode]);

  // Solar system: click to pin/unpin memory tooltip
  const handleSolarClick = useCallback((event) => {
    if (!solarMode) return;
    const sr = sceneRef.current;
    if (hoveredMemory) {
      if (isPinnedRef.current && pinnedMemory === hoveredMemory) {
        // Unpin: unfreeze the memory
        if (sr?.solarSystem && pinnedMemoryMeshRef.current) {
          sr.solarSystem.freezeMemory(pinnedMemoryMeshRef.current, false);
        }
        setPinnedMemory(null);
        pinnedMemoryMeshRef.current = null;
        isPinnedRef.current = false;
      } else {
        // Unfreeze previous pinned if any
        if (sr?.solarSystem && pinnedMemoryMeshRef.current) {
          sr.solarSystem.freezeMemory(pinnedMemoryMeshRef.current, false);
        }
        setPinnedMemory(hoveredMemory);
        pinnedMemoryMeshRef.current = hoveredMemoryMeshRef.current;
        isPinnedRef.current = true;
        // Freeze the newly pinned memory (stops orbiting)
        if (sr?.solarSystem && hoveredMemoryMeshRef.current) {
          sr.solarSystem.freezeMemory(hoveredMemoryMeshRef.current, true);
        }
      }
    } else if (isPinnedRef.current) {
      if (sr?.solarSystem && pinnedMemoryMeshRef.current) {
        sr.solarSystem.freezeMemory(pinnedMemoryMeshRef.current, false);
      }
      setPinnedMemory(null);
      pinnedMemoryMeshRef.current = null;
      isPinnedRef.current = false;
    }
  }, [solarMode, hoveredMemory, pinnedMemory]);

  // Combined canvas click handler
  const handleCanvasClick = useCallback((event) => {
    handleGalaxyClick(event);
    handleSolarClick(event);
    handleNetworkClick(event);
  }, [handleGalaxyClick, handleSolarClick, handleNetworkClick]);

  // Galaxy search debounce
  useEffect(() => {
    if (!galaxySearch || galaxySearch.length < 2 || !token) {
      setGalaxySearchResults([]);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/dashboard/brain-galaxy/search?q=${encodeURIComponent(galaxySearch)}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        setGalaxySearchResults(data.results || []);
      } catch (e) { setGalaxySearchResults([]); }
    }, 300);
    return () => clearTimeout(timer);
  }, [galaxySearch, token]);

  const intensity = metrics?.intensity || 0.3;
  const memoryCount = metrics?.totalMemories || 0;
  const contactCount = metrics?.totalContacts || 0;
  const queueHealth = metrics?.queueHealth || 0.8;
  const sourceList = metrics?.sourceList || [];

  const sourceKey = sourceList.map(s => s.key).sort().join(',');
  const valuesRef = useRef({ intensity });
  valuesRef.current = { intensity };

  // Notify parent of galaxy/solar mode change
  useEffect(() => {
    if (onGalaxyChange) onGalaxyChange(galaxyMode || solarMode || networkMode || workerMode || irmMode || irmCloneSelector);
  }, [galaxyMode, solarMode, networkMode, workerMode, irmMode, irmCloneSelector, onGalaxyChange]);

  // Detect new pulses → trigger vein pulse + brain flash
  useEffect(() => {
    if (pulses.length <= lastPulseCount.current) {
      lastPulseCount.current = pulses.length;
      return;
    }
    const newPulses = pulses.slice(lastPulseCount.current);
    lastPulseCount.current = pulses.length;
    const sr = sceneRef.current;
    if (!sr) return;

    newPulses.forEach(p => {
      const src = sr.sourceList.find(s => s.key === p.type);
      if (!src) return;

      // Trigger pulse on the vein (by key)
      sr.veins.triggerPulse(p.type);
      // Flash brain shell in the source color
      sr.brainShell.flash(src.color);

      // Cascade: after vein pulse reaches Workers (1.8s), propagate through hemispheres
      const VEIN_DURATION = 1.8;
      // Flash Workers immediately (vein target)
      setTimeout(() => {
        if (sr.bottomHemi) sr.bottomHemi.flash();
      }, VEIN_DURATION * 700); // flash slightly before arrival for fluid feel

      // Cascade through hemisphere connections
      setTimeout(() => {
        if (sr.hemiConns) sr.hemiConns.triggerCascade(sr.clock.getElapsedTime());
      }, VEIN_DURATION * 900);

      // Flash Mémoire when cascade arrives
      setTimeout(() => {
        if (sr.leftHemi) sr.leftHemi.flash();
      }, (VEIN_DURATION + 1.0) * 1000);

      // Flash Intelligence when cascade arrives
      setTimeout(() => {
        if (sr.rightHemi) sr.rightHemi.flash();
      }, (VEIN_DURATION + 1.8) * 1000);

      // Show toast
      if (toastRef.current) {
        const labels = { call: 'Appel', email: 'Email', sms: 'SMS', whatsapp: 'WhatsApp', crm_note: 'CRM', note: 'Note' };
        const label = labels[p.type] || p.type;
        const contact = p.contact ? ` · ${p.contact}` : '';
        toastRef.current.textContent = `${label}${contact}`;
        toastRef.current.style.opacity = '1';
        toastRef.current.style.borderColor = src.color;
        setTimeout(() => { if (toastRef.current) toastRef.current.style.opacity = '0'; }, 3000);
      }
    });
  }, [pulses]);

  // ── THOUGHT VISUALIZATION ("La Pensée en Direct") ──
  useEffect(() => {
    if (!thoughtEvent || !sceneRef.current) return;
    const sr = sceneRef.current;
    const te = thoughtEvent;

    // Build filter labels for overlay
    const filters = te.filters || {};
    const timeLabel = filters.time_filter ? formatThoughtTimeFilter(filters.time_filter) : null;
    const contactLabel = filters.contact_name || null;
    const sourceLabel = filters.source_type || null;

    // Phase 0 (0ms): Question received — brain shell flash white
    setThoughtState({ phase: 'receiving', query: te.query, filters: {}, memoriesUsed: 0, responseTimeMs: 0 });
    if (sr.brainShell) sr.brainShell.flash(0xffffff);

    // Phase 1 (600ms): Workers activate — show filters
    setTimeout(() => {
      if (sr.bottomHemi) sr.bottomHemi.flash();
      setThoughtState(prev => ({
        ...prev,
        phase: 'analyzing',
        filters: { time: timeLabel, contact: contactLabel, source: sourceLabel },
      }));
    }, 600);

    // Phase 2 (1800ms): Memory scan — flash Mémoire hemisphere
    setTimeout(() => {
      if (sr.leftHemi) sr.leftHemi.flash();
      if (sr.hemiConns) sr.hemiConns.triggerCascade(sr.clock.getElapsedTime());
      setThoughtState(prev => ({
        ...prev,
        phase: 'scanning',
        memoriesUsed: te.memoriesUsed || 0,
      }));
    }, 1800);

    // Phase 3 (3200ms): Intelligence reasoning — flash Intelligence
    setTimeout(() => {
      if (sr.rightHemi) sr.rightHemi.flash();
      setThoughtState(prev => ({ ...prev, phase: 'reasoning' }));
    }, 3200);

    // Phase 4 (4500ms): Response ready
    setTimeout(() => {
      setThoughtState(prev => ({
        ...prev,
        phase: 'complete',
        responseTimeMs: te.responseTimeMs || 0,
      }));
    }, 4500);

    // Phase 5 (8000ms): Fade out
    setTimeout(() => {
      setThoughtState(null);
    }, 8000);
  }, [thoughtEvent]);

  // ── GALAXY MODE ──
  useEffect(() => {
    const sr = sceneRef.current;
    if (!sr) return;

    if (galaxyMode && galaxyData) {
      // Hide brain elements
      if (sr.brainShell) sr.brainShell.group.visible = false;
      if (sr.leftHemi) sr.leftHemi.group.visible = false;
      if (sr.rightHemi) sr.rightHemi.group.visible = false;
      if (sr.bottomHemi) sr.bottomHemi.group.visible = false;
      if (sr.hemiConns) sr.hemiConns.group.visible = false;
      if (sr.veins) sr.veins.group.visible = false;
      if (sr.orbits) sr.orbits.group.visible = false;
      if (sr.mouth) sr.mouth.group.visible = false;

      // Create galaxy (with safety guard)
      if (!sr.scene || typeof sr.scene.add !== 'function') {
        console.error('Galaxy: scene not ready', sr.scene);
        return;
      }
      try {
        if (galaxyGroupRef.current) galaxyGroupRef.current.dispose();
        const galaxy = createGalaxy(sr.scene, galaxyData);
        galaxyGroupRef.current = galaxy;
        sr.galaxy = galaxy;
      } catch (e) {
        console.error('Galaxy creation failed:', e);
        return;
      }

      // Animate camera to center of galaxy
      const cam = sr.camera;
      const targetPos = { x: 0, y: 0.5, z: 5 };
      const startPos = { x: cam.position.x, y: cam.position.y, z: cam.position.z };
      if (sr.controls) sr.controls.target.set(0, -0.3, 0);
      let progress = 0;
      const animateIn = () => {
        progress += 0.02;
        if (progress >= 1) progress = 1;
        const ease = 1 - Math.pow(1 - progress, 3);
        cam.position.x = startPos.x + (targetPos.x - startPos.x) * ease;
        cam.position.y = startPos.y + (targetPos.y - startPos.y) * ease;
        cam.position.z = startPos.z + (targetPos.z - startPos.z) * ease;
        cam.lookAt(0, -0.3, 0);
        if (progress < 1) requestAnimationFrame(animateIn);
      };
      animateIn();
    } else {
      // Restore brain elements
      if (sr.brainShell) sr.brainShell.group.visible = true;
      if (sr.leftHemi) sr.leftHemi.group.visible = true;
      if (sr.rightHemi) sr.rightHemi.group.visible = true;
      if (sr.bottomHemi) sr.bottomHemi.group.visible = true;
      if (sr.hemiConns) sr.hemiConns.group.visible = true;
      if (sr.veins) sr.veins.group.visible = true;
      if (sr.orbits) sr.orbits.group.visible = true;

      if (sr.mouth) sr.mouth.group.visible = true;

      // Remove galaxy and reset camera
      if (galaxyGroupRef.current) {
        galaxyGroupRef.current.dispose();
        galaxyGroupRef.current = null;
        sr.galaxy = null;
      }
      if (sr.camera && sr.controls) {
        sr.camera.position.set(0, 1.5, 7.0);
        sr.controls.target.set(0, 0.5, 0);
        sr.controls.update();
      }
    }
  }, [galaxyMode, galaxyData]);

  // Solar system mode — "Isolation Solaire" warp transition
  useEffect(() => {
    const sr = sceneRef.current;
    if (!sr || !sr.scene) return;

    if (solarMode && solarData) {
      // === ENTER: Warp Speed → Blackout → Emergence ===
      const galaxyPts = sr.galaxy?.points;
      const galaxyStars = sr.galaxy?.starData || [];
      const startTime = performance.now();
      solarTransitionRef.current = { phase: 'warp-in', startTime };
      setSolarTypeFilter(null);

      // Store original galaxy positions for warp stretch
      let originalPositions = null;
      if (galaxyPts) {
        originalPositions = new Float32Array(galaxyPts.geometry.attributes.position.array);
        sr._galaxyOriginalPositions = originalPositions; // persist for reverse warp
      }

      let solarCreated = false;
      const animateWarp = () => {
        const elapsed = (performance.now() - startTime) / 1000;
        const trans = solarTransitionRef.current;
        if (trans.phase !== 'warp-in') return;

        // T0→0.3s: Stretch galaxy particles outward (warp speed effect)
        if (elapsed < 0.4) {
          const t = elapsed / 0.4;
          if (galaxyPts && originalPositions) {
            const posArr = galaxyPts.geometry.attributes.position.array;
            for (let i = 0; i < posArr.length; i += 3) {
              const ox = originalPositions[i], oy = originalPositions[i + 1], oz = originalPositions[i + 2];
              const stretch = 1 + t * 3; // stretch outward
              posArr[i] = ox * stretch;
              posArr[i + 1] = oy * stretch;
              posArr[i + 2] = oz * stretch;
            }
            galaxyPts.geometry.attributes.position.needsUpdate = true;
            galaxyPts.material.opacity = 0.85 * (1 - t);
          }
          galaxyStars.forEach(s => {
            s.star.material.opacity = 0.6 * (1 - t);
            s.halo.material.opacity = 0.08 * (1 - t);
          });
          requestAnimationFrame(animateWarp);
          return;
        }

        // T0.4→0.8s: Complete dissolution
        if (elapsed < 0.8) {
          if (galaxyPts) {
            galaxyPts.material.opacity = 0;
            galaxyPts.visible = false;
          }
          galaxyStars.forEach(s => {
            s.star.visible = false;
            s.halo.visible = false;
          });
          // Hide galaxy nebula ring
          if (sr.galaxy?.group) {
            sr.galaxy.group.children.forEach(c => { c.visible = false; });
          }
          requestAnimationFrame(animateWarp);
          return;
        }

        // T0.8→1.5s: Create solar system & animate emergence
        if (!solarCreated) {
          solarCreated = true;
          if (solarRef.current) solarRef.current.dispose();
          const solar = createSolarSystem(sr.scene, solarData, solarCenterRef.current);
          solarRef.current = solar;
          sr.solarSystem = solar;

          // Start everything invisible, then fade in
          solar.group.traverse(child => {
            if (child.material && child.material.transparent) {
              child._targetOpacity = child.material.opacity;
              child.material.opacity = 0;
            }
          });

          // Animate camera
          const cam = sr.camera;
          const center = solarCenterRef.current;
          const targetPos = { x: center.x, y: center.y + 1.2, z: center.z + 3.2 };
          const startPos = { x: cam.position.x, y: cam.position.y, z: cam.position.z };
          if (sr.controls) sr.controls.target.copy(center);

          const emergeStart = performance.now();
          const animateEmerge = () => {
            const et = (performance.now() - emergeStart) / 1000;
            const progress = Math.min(et / 0.8, 1);
            const ease = 1 - Math.pow(1 - progress, 3);

            // Camera fly
            cam.position.x = startPos.x + (targetPos.x - startPos.x) * ease;
            cam.position.y = startPos.y + (targetPos.y - startPos.y) * ease;
            cam.position.z = startPos.z + (targetPos.z - startPos.z) * ease;
            cam.lookAt(center.x, center.y, center.z);

            // Fade in solar system elements
            solar.group.traverse(child => {
              if (child.material && child._targetOpacity !== undefined) {
                child.material.opacity = child._targetOpacity * ease;
              }
            });

            if (progress < 1) {
              requestAnimationFrame(animateEmerge);
            } else {
              solarTransitionRef.current = { phase: 'idle', startTime: 0 };
            }
          };
          animateEmerge();
        }
      };
      animateWarp();

    } else {
      // === EXIT: Reverse warp ===
      const sr2 = sceneRef.current;

      // Fade out solar system
      if (solarRef.current) {
        solarRef.current.dispose();
        solarRef.current = null;
        if (sr2) sr2.solarSystem = null;
      }

      // Restore galaxy particles with reverse warp
      if (sr2?.galaxy) {
        const galaxy = sr2.galaxy;
        // Restore original particle positions (they were stretched during warp-in)
        if (sr2._galaxyOriginalPositions && galaxy.points) {
          const posArr = galaxy.points.geometry.attributes.position.array;
          for (let i = 0; i < posArr.length; i++) {
            posArr[i] = sr2._galaxyOriginalPositions[i];
          }
          galaxy.points.geometry.attributes.position.needsUpdate = true;
        }
        // Make everything visible again
        galaxy.group.children.forEach(c => { c.visible = true; });
        galaxy.starData.forEach(s => {
          s.star.visible = true;
          s.halo.visible = true;
        });

        // Animate galaxy fade-in
        const reverseStart = performance.now();
        const animateReverse = () => {
          const et = (performance.now() - reverseStart) / 1000;
          const progress = Math.min(et / 0.6, 1);
          const ease = 1 - Math.pow(1 - progress, 3);

          galaxy.points.material.opacity = 0.85 * ease;
          galaxy.starData.forEach(s => {
            s.star.material.opacity = 0.6 * ease;
            s.halo.material.opacity = 0.08 * ease;
          });

          if (progress < 1) requestAnimationFrame(animateReverse);
        };
        animateReverse();
      }

      // Return camera to galaxy overview
      if (sr2?.camera && sr2?.controls && galaxyMode) {
        sr2.camera.position.set(0, 0.5, 5);
        sr2.controls.target.set(0, -0.3, 0);
        sr2.controls.update();
      }
    }
  }, [solarMode, solarData, galaxyMode]);

  // Cinematic mode trigger
  useEffect(() => {
    if (cinematicTrigger > prevCinematicTrigger.current && sceneRef.current) {
      prevCinematicTrigger.current = cinematicTrigger;
      const sr = sceneRef.current;
      sr.cinematicActive = true;
      sr.cinematicStartClock = sr.clock.getElapsedTime();
      sr.controls.enabled = false;
      sr.controls.autoRotate = false;
      sr.lookAtTarget = sr.controls.target.clone();
      sr._vocalStarted = false;
      sr._audioEl = new Audio();
      sr._audioEl.volume = 0.7;
      sr._lastAudioIdx = -1;
      sr._lastLangIdx = -1;
      sr._narrationsPlayed = {};
      sr._mouthCallback = (idx) => {
        if (idx >= 0 && idx < MOUTH_PHRASES.length) {
          setMouthPhrase({ idx, text: MOUTH_PHRASES[idx].text, flag: MOUTH_PHRASES[idx].flag });
        } else {
          setMouthPhrase({ idx: -1, text: '', flag: '' });
        }
      };
      if (sr.capNodes) sr.capNodes.reset();
    }
  }, [cinematicTrigger]);

  const initScene = useCallback((canvas) => {
    const w = canvas.clientWidth, h = canvas.clientHeight;
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x020810);
    const camera = new THREE.PerspectiveCamera(50, w / h, 0.1, 100);
    camera.position.set(0, 1.5, 7.0);
    const renderer = new THREE.WebGLRenderer({ canvas, antialias: true });
    renderer.setSize(w, h);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.sortObjects = true;
    scene.add(new THREE.AmbientLight(0xffffff, 0.15));
    const dirLight = new THREE.DirectionalLight(0xffffff, 0.4);
    dirLight.position.set(2, 3, 4);
    scene.add(dirLight);

    const controls = new OrbitControls(camera, canvas);
    controls.enablePan = false;
    controls.minDistance = 3;
    controls.maxDistance = 25;
    controls.autoRotate = false;
    controls.autoRotateSpeed = 0;
    controls.enableDamping = true;
    controls.dampingFactor = 0.05;

    const brainShell = createBrainShell(scene);
    const synapses = createSynapses(scene);
    const orbits = createSourceOrbits(scene, sourceList);
    const veins = createSourceVeins(scene, sourceList);
    const leftHemi = createLeftHemisphere(scene);
    const rightHemi = createRightHemisphere(scene);
    const bottomHemi = createBottomHemisphere(scene);
    const hemiConns = createHemisphereConnections(scene);
    const mouth = createMouth(scene);
    const capNodes = createCapabilityNodes(leftHemi.group, rightHemi.group, bottomHemi.group);
    const constellation = createConstellation(scene);

    const clock = new THREE.Clock();
    sceneRef.current = {
      scene, renderer,
      orbits, veins, brainShell, sourceList,
      leftHemi, rightHemi, bottomHemi, hemiConns, mouth, capNodes,
      clock, camera, controls,
      cinematicActive: false, cinematicStartClock: 0,
      lookAtTarget: new THREE.Vector3(),
    };
    function animate() {
      animIdRef.current = requestAnimationFrame(animate);
      const t = clock.getElapsedTime();
      const v = valuesRef.current;

      brainShell.update(t, v.intensity);
      synapses.update(t, v.intensity);
      leftHemi.update(t, v.intensity);
      rightHemi.update(t, v.intensity);
      bottomHemi.update(t, v.intensity);
      hemiConns.update(t, v.intensity);
      mouth.update(t, v.intensity);
      orbits.update(t, v.intensity);
      veins.update(t, v.intensity, orbits.sourceObjs);
      constellation.update(t);
      capNodes.update(t);

      // Galaxy animation
      if (sceneRef.current?.galaxy) sceneRef.current.galaxy.update(t);
      // Solar system animation
      if (sceneRef.current?.solarSystem) sceneRef.current.solarSystem.update(t);
      // Network graph animation
      if (sceneRef.current?.networkGraph) sceneRef.current.networkGraph.update(t);
      // Worker scene animation
      if (sceneRef.current?.workerScene) sceneRef.current.workerScene.update(t);
      if (sceneRef.current?.irmScene) sceneRef.current.irmScene.update(t);

      // Solar tooltip liaison line (connects planet to fixed card)
      const ttSvg = solarTooltipRef.current;
      const hMesh = pinnedMemoryMeshRef.current || hoveredMemoryMeshRef.current;
      if (ttSvg && hMesh && sceneRef.current?.solarSystem) {
        const wp = new THREE.Vector3();
        hMesh.getWorldPosition(wp);
        const proj = wp.clone().project(camera);
        if (proj.z < 1) {
          const sx = (proj.x * 0.5 + 0.5) * canvas.clientWidth;
          const sy = (-proj.y * 0.5 + 0.5) * canvas.clientHeight;
          // Card is fixed at bottom-right: approximate center of card
          const cardX = canvas.clientWidth - 24 - 160; // right:24 + half card width
          const cardY = canvas.clientHeight - 100 - 60; // bottom:100 + half card height
          const line = ttSvg.querySelector('#solar-liaison');
          if (line) {
            line.setAttribute('x1', sx);
            line.setAttribute('y1', sy);
            line.setAttribute('x2', cardX);
            line.setAttribute('y2', cardY);
          }
          ttSvg.style.opacity = '1';
        } else {
          ttSvg.style.opacity = '0';
        }
      } else if (ttSvg) {
        ttSvg.style.opacity = '0';
      }

      // Cinematic mode
      const sr = sceneRef.current;
      if (sr && sr.cinematicActive) {
        const ct = t - sr.cinematicStartClock;
        const ended = runCinematic(ct, t, sr, camera, canvas, {
          phaseTitle: phaseTitleRef, capLabel: capLabelRef,
          intro: introTextRef, finale: finaleTextRef, counter: counterRef,
          narrator: narratorRef,
        });
        if (ended) {
          sr.cinematicActive = false;
          sr.leftHemi.group.position.set(0, 0, 0);
          sr.rightHemi.group.position.set(0, 0, 0);
          sr.bottomHemi.group.position.set(0, 0, 0);
          sr.mouth.group.position.set(0, 0, 0);
          sr.mouth.group.position.set(0, 0, 0);
          sr.hemiConns.group.visible = true;
          controls.target.copy(sr.lookAtTarget);
          controls.enabled = true;
          controls.autoRotate = false;
          // Hide overlays
          if (phaseTitleRef.current) phaseTitleRef.current.style.opacity = '0';
          if (capLabelRef.current) capLabelRef.current.style.opacity = '0';
          if (introTextRef.current) introTextRef.current.style.opacity = '0';
          if (counterRef.current) counterRef.current.style.opacity = '0';
          if (narratorRef.current) narratorRef.current.style.opacity = '0';
          if (finaleTextRef.current) finaleTextRef.current.style.opacity = '0';
          // Stop audio
          if (sr._audioEl) { sr._audioEl.pause(); sr._audioEl.src = ''; }
          if (onCinematicEnd) onCinematicEnd();
        }
      }

      controls.update();
      renderer.render(scene, camera);

      // Detect overlay-hiding modes (cinematic or galaxy)
      const sr2 = sceneRef.current;
      const cinematicOn = (sr2 && sr2.cinematicActive) || !!(sr2 && sr2.galaxy) || !!(sr2 && sr2.solarSystem) || !!(sr2 && sr2.networkGraph) || !!(sr2 && sr2.workerScene) || !!(sr2 && sr2.irmScene);

      // Project source labels (hidden during cinematic/galaxy)
      orbits.sourceObjs.forEach((so, idx) => {
        if (idx >= sourceList.length) return;
        const labelEl = labelRefsMap.current[sourceList[idx].key];
        if (!labelEl) return;
        if (cinematicOn && !sr2.workerScene) { labelEl.style.opacity = '0'; return; }
        const wp = new THREE.Vector3();
        so.icon.getWorldPosition(wp);
        const proj = wp.clone().project(camera);
        const sx = (proj.x * 0.5 + 0.5) * canvas.clientWidth;
        const sy = (-proj.y * 0.5 + 0.5) * canvas.clientHeight;
        if (proj.z > 1) { labelEl.style.opacity = '0'; }
        else { labelEl.style.opacity = '1'; labelEl.style.left = `${sx}px`; labelEl.style.top = `${sy - 28}px`; }
      });

      // Project hemisphere labels (hidden during cinematic/galaxy)
      const hemiLabels = [
        { ref: hemiLabelLeftRef, pos: new THREE.Vector3(-0.8, 0.6, 0), group: leftHemi.group },
        { ref: hemiLabelRightRef, pos: new THREE.Vector3(0.8, 0.6, 0), group: rightHemi.group },
        { ref: hemiLabelBottomRef, pos: new THREE.Vector3(0, 2.0, 0), group: bottomHemi.group },
      ];
      hemiLabels.forEach(hl => {
        const el = hl.ref.current;
        if (!el) return;
        // In worker mode: show Mémoire label next to memory hemisphere
        if (cinematicOn) {
          const ws = sceneRef.current?.workerScene;
          if (ws && ws.MEM_POS && hl.ref === hemiLabelLeftRef) {
            const memWorldPos = ws.MEM_POS.clone();
            memWorldPos.y += 1.1;
            const proj2 = memWorldPos.project(camera);
            if (proj2.z > 1) { el.style.opacity = '0'; return; }
            const sx2 = (proj2.x * 0.5 + 0.5) * canvas.clientWidth;
            const sy2 = (-proj2.y * 0.5 + 0.5) * canvas.clientHeight;
            el.style.left = `${sx2}px`;
            el.style.top = `${sy2}px`;
            el.style.opacity = '0.9';
            return;
          }
          el.style.opacity = '0';
          return;
        }
        const worldPos = hl.pos.clone();
        // Apply group position offset (for post-cinematic reset)
        worldPos.add(hl.group.position);
        const proj = worldPos.project(camera);
        if (proj.z > 1) { el.style.opacity = '0'; return; }
        const sx = (proj.x * 0.5 + 0.5) * canvas.clientWidth;
        const sy = (-proj.y * 0.5 + 0.5) * canvas.clientHeight;
        el.style.left = `${sx}px`;
        el.style.top = `${sy}px`;
        el.style.opacity = '0.85';
      });

      // Project cluster labels (PROMESSE, BLOCAGE, FAIT, DECISION)
      if (leftHemi.clusters) {
        leftHemi.clusters.forEach((cl, idx) => {
          const el = clusterLabelRefs.current[idx];
          if (!el) return;
          if (cinematicOn || cl.core.material.opacity < 0.15) { el.style.opacity = '0'; return; }
          const wp = new THREE.Vector3();
          cl.core.getWorldPosition(wp);
          const proj = wp.project(camera);
          if (proj.z > 1) { el.style.opacity = '0'; return; }
          const sx = (proj.x * 0.5 + 0.5) * canvas.clientWidth;
          const sy = (-proj.y * 0.5 + 0.5) * canvas.clientHeight;
          el.style.left = `${sx}px`;
          el.style.top = `${sy - 22}px`;
          el.style.opacity = String(Math.min(cl.core.material.opacity * 1.6, 0.95));
        });
      }

      // Project intel cluster labels (COMPREHENSION, RAISONNEMENT, RESOLUTION, ADAPTATION)
      if (rightHemi.clusters) {
        rightHemi.clusters.forEach((cl, idx) => {
          const el = intelClusterLabelRefs.current[idx];
          if (!el) return;
          if (cinematicOn || cl.core.material.opacity < 0.15) { el.style.opacity = '0'; return; }
          const wp = new THREE.Vector3();
          cl.core.getWorldPosition(wp);
          const proj = wp.project(camera);
          if (proj.z > 1) { el.style.opacity = '0'; return; }
          const sx = (proj.x * 0.5 + 0.5) * canvas.clientWidth;
          const sy = (-proj.y * 0.5 + 0.5) * canvas.clientHeight;
          el.style.left = `${sx}px`;
          el.style.top = `${sy - 22}px`;
          el.style.opacity = String(Math.min(cl.core.material.opacity * 1.6, 0.95));
        });
      }

      // Project vocal ring label + speech text
      const langEl = langLabelRef.current;
      const mouthEl = mouthTextRef.current;
      if (langEl) {
        if (cinematicOn) { langEl.style.opacity = '0'; }
        else {
          const mouthPos = mouth.worldPos().clone();
          mouthPos.y += 0.55;
          const lp = mouthPos.project(camera);
          if (lp.z > 1) { langEl.style.opacity = '0'; }
          else {
            const lsx = (lp.x * 0.5 + 0.5) * canvas.clientWidth;
            const lsy = (-lp.y * 0.5 + 0.5) * canvas.clientHeight;
            langEl.style.left = `${lsx}px`;
            langEl.style.top = `${lsy}px`;
            langEl.style.opacity = mouth.speaking ? '0' : '0.85';
          }
        }
      }
      if (mouthEl) {
        if (!mouth.speaking || cinematicOn) { mouthEl.style.opacity = '0'; }
        else {
          const mouthPos2 = mouth.worldPos().clone();
          const mp = mouthPos2.project(camera);
          if (mp.z > 1) { mouthEl.style.opacity = '0'; }
          else {
            const msx = (mp.x * 0.5 + 0.5) * canvas.clientWidth;
            const msy = (-mp.y * 0.5 + 0.5) * canvas.clientHeight;
            mouthEl.style.left = `${msx}px`;
            mouthEl.style.top = `${msy}px`;
            mouthEl.style.opacity = String(Math.min(mouth.openAmount * 1.5, 1));
          }
        }
      }

      // Project worker cluster labels (INGESTION, TRAITEMENT, ORCHESTRATION)
      if (bottomHemi.clusters) {
        bottomHemi.clusters.forEach((cl, idx) => {
          const el = workerClusterLabelRefs.current[idx];
          if (!el) return;
          if (cinematicOn || cl.core.material.opacity < 0.15) { el.style.opacity = '0'; return; }
          const wp = new THREE.Vector3();
          cl.core.getWorldPosition(wp);
          const proj = wp.project(camera);
          if (proj.z > 1) { el.style.opacity = '0'; return; }
          const sx = (proj.x * 0.5 + 0.5) * canvas.clientWidth;
          const sy = (-proj.y * 0.5 + 0.5) * canvas.clientHeight;
          el.style.left = `${sx}px`;
          el.style.top = `${sy - 22}px`;
          el.style.opacity = String(Math.min(cl.core.material.opacity * 1.6, 0.95));
        });
      }
    }
    animate();

    const onResize = () => {
      const nw = canvas.clientWidth, nh = canvas.clientHeight;
      camera.aspect = nw / nh;
      camera.updateProjectionMatrix();
      renderer.setSize(nw, nh);
    };
    window.addEventListener('resize', onResize);

    return () => {
      window.removeEventListener('resize', onResize);
      cancelAnimationFrame(animIdRef.current);
      sceneRef.current = null;
      controls.dispose(); renderer.dispose();
      brainShell.dispose(); synapses.dispose(); leftHemi.dispose(); rightHemi.dispose(); bottomHemi.dispose(); hemiConns.dispose(); mouth.dispose(); capNodes.dispose(); orbits.dispose(); veins.dispose(); constellation.dispose();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memoryCount, contactCount, queueHealth, sourceKey]);

  useEffect(() => {
    if (!canvasRef.current) return;
    return initScene(canvasRef.current);
  }, [initScene]);

  // Live worker display data: prefer fresh polling stats, fallback to initial data
  const wd = workerStats || workerData;

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%', overflow: 'hidden' }} data-testid="brain-canvas-wrapper">
      <canvas ref={canvasRef} data-testid="brain-canvas" onClick={handleCanvasClick} onMouseMove={(e) => { handleSolarMouseMove(e); handleNetworkMouseMove(e); handleWorkerMouseMove(e); }} style={{ width: '100%', height: '100%', display: 'block', cursor: workerMode ? (workerHoveredTask ? 'pointer' : 'default') : networkMode ? (networkHoveredCluster ? 'pointer' : 'default') : solarMode ? (hoveredMemory ? 'pointer' : 'default') : (galaxyMode ? 'crosshair' : 'default') }} />

      {sourceList.map(s => {
        const isActive = s.active !== false;
        const dotColor = isActive ? s.color : '#555566';
        const dotShadow = isActive ? `0 0 8px ${s.color}` : 'none';
        const labelColor = isActive ? 'rgba(255,255,255,0.9)' : 'rgba(255,255,255,0.3)';
        const countColor = isActive ? 'rgba(255,255,255,0.4)' : 'rgba(255,255,255,0.2)';
        const countText = isActive ? s.count?.toLocaleString() : '\u2014';
        const borderColor = isActive ? 'rgba(255,255,255,0.12)' : 'rgba(255,255,255,0.05)';
        return (
          <div
            key={s.key}
            ref={el => { if (el) labelRefsMap.current[s.key] = el; }}
            data-testid={`brain-label-${s.key}`}
            style={{ position: 'absolute', left: 0, top: 0, transform: 'translate(-50%, -100%)', pointerEvents: 'none', transition: 'opacity 0.15s', zIndex: 5 }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '3px 10px', borderRadius: '9999px', background: isActive ? 'rgba(0,0,0,0.7)' : 'rgba(0,0,0,0.4)', backdropFilter: 'blur(8px)', border: `1px solid ${borderColor}`, whiteSpace: 'nowrap' }}>
              <span style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: dotColor, boxShadow: dotShadow, flexShrink: 0 }} />
              <span style={{ fontSize: '11px', color: labelColor, fontWeight: 500 }}>{s.label}</span>
              <span style={{ fontSize: '11px', color: countColor }}>{countText}</span>
            </div>
          </div>
        );
      })}

      {/* Cluster knowledge labels (PROMESSE, BLOCAGE, FAIT, DECISION) */}
      {[
        { label: 'PROMESSE', color: '#fbbf24', idx: 0 },
        { label: 'BLOCAGE', color: '#ef4444', idx: 1 },
        { label: 'FAIT', color: '#22d3ee', idx: 2 },
        { label: 'DECISION', color: '#c084fc', idx: 3 },
      ].map(cl => (
        <div
          key={cl.label}
          ref={el => { if (el) clusterLabelRefs.current[cl.idx] = el; }}
          data-testid={`brain-cluster-${cl.label.toLowerCase()}`}
          style={{
            position: 'absolute', left: 0, top: 0, transform: 'translate(-50%, -100%)',
            pointerEvents: 'none', opacity: 0, transition: 'opacity 0.3s',
            zIndex: 7, whiteSpace: 'nowrap',
          }}
        >
          <div style={{
            display: 'flex', alignItems: 'center', gap: '4px',
            padding: '2px 8px', borderRadius: '9999px',
            background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(4px)',
            border: `1px solid ${cl.color}33`,
          }}>
            <span style={{ width: '5px', height: '5px', borderRadius: '50%', backgroundColor: cl.color, boxShadow: `0 0 6px ${cl.color}` }} />
            <span style={{ fontSize: '9px', fontWeight: 700, letterSpacing: '1px', color: cl.color }}>{cl.label}</span>
          </div>
        </div>
      ))}

      {/* Intelligence cluster labels (COMPREHENSION, RAISONNEMENT, RESOLUTION, ADAPTATION) */}
      {[
        { label: 'COMPREHENSION', color: '#f472b6', idx: 0 },
        { label: 'RAISONNEMENT', color: '#67e8f9', idx: 1 },
        { label: 'RESOLUTION', color: '#fb923c', idx: 2 },
        { label: 'ADAPTATION', color: '#fde68a', idx: 3 },
      ].map(cl => (
        <div
          key={cl.label}
          ref={el => { if (el) intelClusterLabelRefs.current[cl.idx] = el; }}
          data-testid={`brain-intel-${cl.label.toLowerCase()}`}
          style={{
            position: 'absolute', left: 0, top: 0, transform: 'translate(-50%, -100%)',
            pointerEvents: 'none', opacity: 0, transition: 'opacity 0.3s',
            zIndex: 7, whiteSpace: 'nowrap',
          }}
        >
          <div style={{
            display: 'flex', alignItems: 'center', gap: '4px',
            padding: '2px 8px', borderRadius: '9999px',
            background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(4px)',
            border: `1px solid ${cl.color}33`,
          }}>
            <span style={{ width: '5px', height: '5px', borderRadius: '50%', backgroundColor: cl.color, boxShadow: `0 0 6px ${cl.color}` }} />
            <span style={{ fontSize: '9px', fontWeight: 700, letterSpacing: '1px', color: cl.color }}>{cl.label}</span>
          </div>
        </div>
      ))}

      {/* Language label under Intelligence hemisphere */}
      <div
        ref={langLabelRef}
        data-testid="brain-lang-label"
        style={{
          position: 'absolute', left: 0, top: 0, transform: 'translate(-50%, -50%)',
          pointerEvents: 'auto', cursor: 'pointer', opacity: 0, transition: 'opacity 0.4s',
          zIndex: 7, whiteSpace: 'nowrap',
        }}
        onClick={() => {
          const sr = sceneRef.current;
          if (sr && sr.mouth && !sr.mouth.speaking) {
            sr.mouth.startSpeaking((idx) => {
              if (idx >= 0 && idx < MOUTH_PHRASES.length) {
                setMouthPhrase({ idx, text: MOUTH_PHRASES[idx].text, flag: MOUTH_PHRASES[idx].flag });
              } else {
                setMouthPhrase({ idx: -1, text: '', flag: '' });
              }
            });
          }
        }}
      >
        <div style={{
          display: 'flex', alignItems: 'center', gap: '5px',
          padding: '3px 10px', borderRadius: '9999px',
          background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)',
          border: '1px solid rgba(139,92,246,0.2)',
        }}>
          <span style={{ fontSize: '10px' }}>&#127760;</span>
          <span style={{ fontSize: '9px', fontWeight: 600, letterSpacing: '0.5px', color: '#d8b4fe' }}>95+ langues</span>
        </div>
      </div>

      {/* Mouth speech text */}
      <div
        ref={mouthTextRef}
        data-testid="brain-mouth-text"
        style={{
          position: 'absolute', left: 0, top: 0, transform: 'translate(-50%, 0)',
          pointerEvents: 'none', opacity: 0, transition: 'opacity 0.3s',
          zIndex: 12, whiteSpace: 'nowrap', textAlign: 'center',
        }}
      >
        {mouthPhrase.idx >= 0 && (
          <div style={{
            padding: '6px 18px', borderRadius: '12px',
            background: 'rgba(124,58,237,0.15)', backdropFilter: 'blur(8px)',
            border: '1px solid rgba(192,132,252,0.3)',
          }}>
            <span style={{ fontSize: '14px', marginRight: '6px' }}>{mouthPhrase.flag}</span>
            <span style={{ fontSize: '13px', fontWeight: 500, color: '#e9d5ff', letterSpacing: '0.3px' }}>{mouthPhrase.text}</span>
          </div>
        )}
      </div>

      {/* Worker cluster labels (INGESTION, TRAITEMENT, ORCHESTRATION) */}
      {[
        { label: 'INGESTION', color: '#22d3ee', idx: 0 },
        { label: 'TRAITEMENT', color: '#f59e0b', idx: 1 },
        { label: 'ORCHESTRATION', color: '#10b981', idx: 2 },
      ].map(cl => (
        <div
          key={cl.label}
          ref={el => { if (el) workerClusterLabelRefs.current[cl.idx] = el; }}
          data-testid={`brain-worker-${cl.label.toLowerCase()}`}
          style={{
            position: 'absolute', left: 0, top: 0, transform: 'translate(-50%, -100%)',
            pointerEvents: 'none', opacity: 0, transition: 'opacity 0.3s',
            zIndex: 7, whiteSpace: 'nowrap',
          }}
        >
          <div style={{
            display: 'flex', alignItems: 'center', gap: '4px',
            padding: '2px 8px', borderRadius: '9999px',
            background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(4px)',
            border: `1px solid ${cl.color}33`,
          }}>
            <span style={{ width: '5px', height: '5px', borderRadius: '50%', backgroundColor: cl.color, boxShadow: `0 0 6px ${cl.color}` }} />
            <span style={{ fontSize: '9px', fontWeight: 700, letterSpacing: '1px', color: cl.color }}>{cl.label}</span>
          </div>
        </div>
      ))}

      {/* Hemisphere permanent labels */}
      <div
        ref={hemiLabelLeftRef}
        data-testid="brain-hemi-label-left"
        onClick={() => enterGalaxy()}
        style={{
          position: 'absolute', left: 0, top: 0, transform: 'translate(-50%, -50%)',
          pointerEvents: 'auto', cursor: 'pointer', opacity: 0, transition: 'opacity 0.4s',
          zIndex: 8, whiteSpace: 'nowrap',
        }}
      >
        <div style={{
          display: 'flex', alignItems: 'center', gap: '6px',
          padding: '4px 12px', borderRadius: '9999px',
          background: 'rgba(59,130,246,0.08)', border: '1px solid rgba(96,165,250,0.25)',
          backdropFilter: 'blur(6px)',
          transition: 'all 0.2s',
        }}
        onMouseEnter={e => { e.currentTarget.style.background = 'rgba(59,130,246,0.2)'; e.currentTarget.style.borderColor = 'rgba(96,165,250,0.5)'; }}
        onMouseLeave={e => { e.currentTarget.style.background = 'rgba(59,130,246,0.08)'; e.currentTarget.style.borderColor = 'rgba(96,165,250,0.25)'; }}
        >
          <span style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: '#60a5fa', boxShadow: '0 0 8px #60a5fa' }} />
          <span style={{ fontSize: '10px', fontWeight: 700, letterSpacing: '1.5px', color: '#93c5fd', textTransform: 'uppercase' }}>M&eacute;moire</span>
          <span style={{ fontSize: '11px', fontWeight: 600, color: '#60a5fa' }}>&middot; 12</span>
        </div>
        {galaxyLoading && (
          <div style={{ position: 'absolute', top: '100%', left: '50%', transform: 'translateX(-50%)', marginTop: '4px' }}>
            <div style={{ fontSize: '9px', color: '#60a5fa', animation: 'pulse-dot 1s infinite' }}>Chargement...</div>
          </div>
        )}
      </div>

      <div
        ref={hemiLabelRightRef}
        data-testid="brain-hemi-label-right"
        onClick={enterNetwork}
        style={{
          position: 'absolute', left: 0, top: 0, transform: 'translate(-50%, -50%)',
          pointerEvents: 'auto', cursor: 'pointer', opacity: 0, transition: 'opacity 0.4s',
          zIndex: 8, whiteSpace: 'nowrap',
        }}
      >
        <div style={{
          display: 'flex', alignItems: 'center', gap: '6px',
          padding: '4px 12px', borderRadius: '9999px',
          background: 'rgba(139,92,246,0.08)', border: '1px solid rgba(192,132,252,0.25)',
          backdropFilter: 'blur(6px)',
          transition: 'all 0.2s',
        }}
        onMouseEnter={e => { e.currentTarget.style.background = 'rgba(139,92,246,0.2)'; e.currentTarget.style.borderColor = 'rgba(192,132,252,0.5)'; }}
        onMouseLeave={e => { e.currentTarget.style.background = 'rgba(139,92,246,0.08)'; e.currentTarget.style.borderColor = 'rgba(192,132,252,0.25)'; }}
        >
          <span style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: '#c084fc', boxShadow: '0 0 8px #c084fc' }} />
          <span style={{ fontSize: '10px', fontWeight: 700, letterSpacing: '1.5px', color: '#d8b4fe', textTransform: 'uppercase' }}>Intelligence</span>
          <span style={{ fontSize: '11px', fontWeight: 600, color: '#c084fc' }}>&middot; 14</span>
        </div>
        {networkLoading && (
          <div style={{ position: 'absolute', top: '100%', left: '50%', transform: 'translateX(-50%)', marginTop: '4px' }}>
            <div style={{ fontSize: '9px', color: '#c084fc', animation: 'pulse-dot 1s infinite' }}>Chargement...</div>
          </div>
        )}
      </div>

      <div
        ref={hemiLabelBottomRef}
        data-testid="brain-hemi-label-bottom"
        onClick={enterWorkerMode}
        style={{
          position: 'absolute', left: 0, top: 0, transform: 'translate(-50%, -50%)',
          pointerEvents: 'auto', cursor: 'pointer', opacity: 0, transition: 'opacity 0.4s',
          zIndex: 8, whiteSpace: 'nowrap',
        }}
      >
        <div style={{
          display: 'flex', alignItems: 'center', gap: '6px',
          padding: '4px 12px', borderRadius: '9999px',
          background: 'rgba(34,197,94,0.08)', border: '1px solid rgba(34,197,94,0.25)',
          backdropFilter: 'blur(6px)',
          transition: 'all 0.2s',
        }}
        onMouseEnter={e => { e.currentTarget.style.background = 'rgba(34,197,94,0.2)'; e.currentTarget.style.borderColor = 'rgba(34,197,94,0.5)'; }}
        onMouseLeave={e => { e.currentTarget.style.background = 'rgba(34,197,94,0.08)'; e.currentTarget.style.borderColor = 'rgba(34,197,94,0.25)'; }}
        >
          <span style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: '#22c55e', boxShadow: '0 0 8px #22c55e' }} />
          <span style={{ fontSize: '10px', fontWeight: 700, letterSpacing: '1.5px', color: '#86efac', textTransform: 'uppercase' }}>Workers</span>
          <span style={{ fontSize: '11px', fontWeight: 600, color: '#22c55e' }}>&middot; 11</span>
        </div>
        {workerLoading && (
          <div style={{ position: 'absolute', top: '100%', left: '50%', transform: 'translateX(-50%)', marginTop: '4px' }}>
            <div style={{ fontSize: '9px', color: '#22c55e', animation: 'pulse-dot 1s infinite' }}>Chargement...</div>
          </div>
        )}
      </div>

      <div
        ref={toastRef}
        data-testid="brain-pulse-toast"
        style={{
          position: 'absolute', bottom: '70px', right: '16px',
          padding: '6px 16px', borderRadius: '9999px',
          background: 'rgba(0,0,0,0.8)', backdropFilter: 'blur(8px)',
          border: '1px solid rgba(255,255,255,0.15)',
          color: 'rgba(255,255,255,0.9)', fontSize: '12px', fontWeight: 500,
          opacity: 0, transition: 'opacity 0.3s', pointerEvents: 'none', zIndex: 10,
        }}
      />

      {/* Cinematic overlays */}
      <div ref={narratorRef} data-testid="cinematic-narrator" style={{
        position: 'absolute', top: '42%', left: '50%', transform: 'translate(-50%, -50%)',
        textAlign: 'center', opacity: 0, transition: 'opacity 0.6s', zIndex: 25, pointerEvents: 'none',
        maxWidth: '600px', padding: '0 20px',
        fontSize: '20px', fontWeight: 500, fontStyle: 'italic', color: 'rgba(255,255,255,0.9)',
        letterSpacing: '0.5px', lineHeight: '1.6',
        textShadow: '0 0 20px rgba(96,165,250,0.4), 0 2px 10px rgba(0,0,0,0.8)',
      }} />

      <div ref={introTextRef} data-testid="cinematic-intro" style={{
        position: 'absolute', top: '40%', left: '50%', transform: 'translate(-50%, -50%)',
        textAlign: 'center', opacity: 0, transition: 'opacity 0.8s', zIndex: 20, pointerEvents: 'none',
      }}>
        <div style={{ fontSize: '22px', fontWeight: 600, letterSpacing: '2px', color: 'white' }}>
          Ce clone poss&egrave;de <span style={{ color: '#60a5fa', fontSize: '32px', fontWeight: 700 }}>37</span> capacit&eacute;s actives
        </div>
        <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.4)', marginTop: '8px' }}>
          r&eacute;parties en 3 zones
        </div>
      </div>

      <div ref={phaseTitleRef} data-testid="cinematic-phase" style={{
        position: 'absolute', top: '6%', left: '50%', transform: 'translateX(-50%)',
        textAlign: 'center', opacity: 0, transition: 'opacity 0.5s', zIndex: 20, pointerEvents: 'none',
      }} />

      <div ref={capLabelRef} data-testid="cinematic-cap-label" style={{
        position: 'absolute', pointerEvents: 'none', zIndex: 20,
        fontSize: '15px', fontWeight: 600, opacity: 0, transition: 'opacity 0.12s, left 0.15s, top 0.15s',
        textShadow: '0 0 12px currentColor, 0 0 24px currentColor, 0 2px 8px rgba(0,0,0,0.9)',
        whiteSpace: 'nowrap',
      }} />

      <div ref={counterRef} data-testid="cinematic-counter" style={{
        position: 'absolute', bottom: '8%', right: '5%',
        fontSize: '18px', fontWeight: 700, color: 'rgba(255,255,255,0.3)', letterSpacing: '2px',
        opacity: 0, transition: 'opacity 0.4s', zIndex: 20, pointerEvents: 'none',
        fontFamily: 'monospace',
      }} />

      <div ref={finaleTextRef} data-testid="cinematic-finale" style={{
        position: 'absolute', top: '18%', left: '50%', transform: 'translate(-50%, -50%)',
        textAlign: 'center', opacity: 0, transition: 'opacity 1.2s', zIndex: 20, pointerEvents: 'none',
      }}>
        <div style={{ fontSize: '38px', fontWeight: 700, letterSpacing: '4px', color: 'white' }}>
          37 capacit&eacute;s &middot; 95+ langues &middot; 24/7
        </div>
        <div style={{ fontSize: '16px', color: 'rgba(255,255,255,0.45)', marginTop: '14px', fontStyle: 'italic' }}>
          Votre &eacute;quipe ne dort jamais
        </div>
      </div>

      {/* ── GALAXY MODE OVERLAY ── */}
      {galaxyMode && galaxyData && !solarMode && !timelapseMode && (
        <div data-testid="galaxy-overlay" style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 20 }}>
          {/* Back button */}
          <button
            data-testid="galaxy-back-btn"
            onClick={() => { setGalaxyMode(false); setGalaxySearch(''); setGalaxySearchResults([]); setGalaxyCloneFilter(null); setGalaxyCloneDropdown(false); setGalaxyCloneSearch(''); }}
            style={{
              position: 'absolute', top: '16px', left: '16px', pointerEvents: 'auto',
              display: 'flex', alignItems: 'center', gap: '8px',
              padding: '8px 18px', borderRadius: '9999px',
              background: 'rgba(0,0,0,0.7)', border: '1px solid rgba(96,165,250,0.3)',
              color: '#93c5fd', cursor: 'pointer', fontSize: '12px', fontWeight: 600,
              backdropFilter: 'blur(8px)', transition: 'all 0.2s',
              letterSpacing: '0.5px',
            }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(96,165,250,0.6)'; e.currentTarget.style.background = 'rgba(59,130,246,0.15)'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(96,165,250,0.3)'; e.currentTarget.style.background = 'rgba(0,0,0,0.7)'; }}
          >
            &larr; Retour au cerveau
          </button>

          {/* Title */}
          <div style={{
            position: 'absolute', top: '16px', left: '50%', transform: 'translateX(-50%)',
            textAlign: 'center', animation: 'thought-slide-in 0.6s ease-out',
          }}>
            <div style={{
              fontSize: '16px', fontWeight: 700, color: 'white', letterSpacing: '3px',
              textTransform: 'uppercase',
              textShadow: '0 0 30px rgba(96,165,250,0.4), 0 0 60px rgba(96,165,250,0.15)',
            }}>
              Galaxie de la M&eacute;moire
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px' }}>
              <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.35)', letterSpacing: '1px' }}>
                {(galaxyData.clusters || []).reduce((s, c) => s + c.count, 0).toLocaleString()} m&eacute;moires
              </span>
              {!timelapsePlaying && (
                <button
                  data-testid="timelapse-replay-btn"
                  onClick={startTimelapse}
                  style={{
                    pointerEvents: 'auto', display: 'inline-flex', alignItems: 'center', gap: '5px',
                    padding: '3px 12px', borderRadius: '9999px', cursor: 'pointer',
                    background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(96,165,250,0.25)',
                    color: '#93c5fd', fontSize: '10px', fontWeight: 600,
                    transition: 'all 0.2s', letterSpacing: '0.5px',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(96,165,250,0.6)'; e.currentTarget.style.background = 'rgba(59,130,246,0.12)'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(96,165,250,0.25)'; e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; }}
                >
                  &#9654; Time-Lapse
                </button>
              )}
            </div>
          </div>

          {/* Clone filter dropdown */}
          <div data-testid="galaxy-clone-filter" style={{
            position: 'absolute', top: '70px', left: '50%', transform: 'translateX(-50%)',
            pointerEvents: 'auto', zIndex: 20,
            animation: 'thought-slide-in 0.5s ease-out 0.15s both',
          }}>
            <button
              data-testid="galaxy-clone-dropdown-toggle"
              onClick={() => setGalaxyCloneDropdown(p => !p)}
              style={{
                padding: '5px 16px', borderRadius: '9999px', cursor: 'pointer',
                fontSize: '10px', fontWeight: 600, letterSpacing: '0.5px',
                transition: 'all 0.2s', border: '1px solid rgba(96,165,250,0.4)',
                background: 'rgba(0,0,0,0.6)', color: '#93c5fd',
                backdropFilter: 'blur(8px)', display: 'flex', alignItems: 'center', gap: '6px',
                whiteSpace: 'nowrap',
              }}
            >
              <span style={{ width: '5px', height: '5px', borderRadius: '50%', backgroundColor: '#60a5fa', boxShadow: '0 0 6px #60a5fa' }} />
              {galaxyCloneFilter ? galaxyClones.find(c => c.id === galaxyCloneFilter)?.name || 'Clone' : 'Tous les clones'}
              <span style={{ fontSize: '8px', marginLeft: '2px', opacity: 0.6 }}>{galaxyCloneDropdown ? '\u25B2' : '\u25BC'}</span>
            </button>

            {galaxyCloneDropdown && (
              <div style={{
                position: 'absolute', top: 'calc(100% + 6px)', left: '50%', transform: 'translateX(-50%)',
                minWidth: '220px', maxHeight: '280px', borderRadius: '12px',
                background: 'rgba(10,10,20,0.95)', border: '1px solid rgba(96,165,250,0.25)',
                backdropFilter: 'blur(12px)', overflow: 'hidden',
                boxShadow: '0 8px 32px rgba(0,0,0,0.6)',
              }}>
                {galaxyClones.length > 5 && (
                  <div style={{ padding: '8px 10px 4px' }}>
                    <input
                      data-testid="galaxy-clone-search"
                      type="text"
                      value={galaxyCloneSearch}
                      onChange={e => setGalaxyCloneSearch(e.target.value)}
                      placeholder="Rechercher un clone..."
                      autoFocus
                      style={{
                        width: '100%', padding: '5px 10px', borderRadius: '8px', fontSize: '11px',
                        background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)',
                        color: '#fff', outline: 'none',
                      }}
                    />
                  </div>
                )}
                <div style={{ overflowY: 'auto', maxHeight: '230px', padding: '4px 0' }}>
                  <div
                    data-testid="galaxy-filter-all"
                    onClick={() => { if (galaxyCloneFilter !== null) enterGalaxy(null); setGalaxyCloneDropdown(false); setGalaxyCloneSearch(''); }}
                    style={{
                      padding: '7px 14px', cursor: 'pointer', fontSize: '11px', fontWeight: 600,
                      color: !galaxyCloneFilter ? '#93c5fd' : 'rgba(255,255,255,0.5)',
                      background: !galaxyCloneFilter ? 'rgba(96,165,250,0.1)' : 'transparent',
                      transition: 'background 0.15s',
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.background = 'rgba(96,165,250,0.15)'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = !galaxyCloneFilter ? 'rgba(96,165,250,0.1)' : 'transparent'; }}
                  >
                    <span>Tous les clones</span>
                  </div>
                  {galaxyClones
                    .filter(c => !galaxyCloneSearch || c.name.toLowerCase().includes(galaxyCloneSearch.toLowerCase()))
                    .map(clone => (
                    <div
                      key={clone.id}
                      data-testid={`galaxy-filter-${clone.id}`}
                      onClick={() => { if (galaxyCloneFilter !== clone.id) enterGalaxy(clone.id); setGalaxyCloneDropdown(false); setGalaxyCloneSearch(''); }}
                      style={{
                        padding: '7px 14px', cursor: 'pointer', fontSize: '11px',
                        color: galaxyCloneFilter === clone.id ? '#93c5fd' : 'rgba(255,255,255,0.5)',
                        background: galaxyCloneFilter === clone.id ? 'rgba(96,165,250,0.1)' : 'transparent',
                        transition: 'background 0.15s',
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      }}
                      onMouseEnter={e => { e.currentTarget.style.background = 'rgba(96,165,250,0.15)'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = galaxyCloneFilter === clone.id ? 'rgba(96,165,250,0.1)' : 'transparent'; }}
                    >
                      <span>{clone.name}</span>
                      {clone.memory_count != null && <span style={{ fontSize: '9px', opacity: 0.4 }}>{clone.memory_count.toLocaleString()}</span>}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Search bar */}
          <div style={{
            position: 'absolute', top: '16px', right: '16px', pointerEvents: 'auto',
            width: '240px',
          }}>
            <div style={{
              display: 'flex', alignItems: 'center', gap: '8px',
              padding: '6px 14px', borderRadius: '9999px',
              background: 'rgba(0,0,0,0.7)', border: '1px solid rgba(96,165,250,0.2)',
              backdropFilter: 'blur(8px)',
            }}>
              <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.3)' }}>&#128269;</span>
              <input
                data-testid="galaxy-search-input"
                type="text"
                value={galaxySearch}
                onChange={e => setGalaxySearch(e.target.value)}
                placeholder="Rechercher un contact..."
                style={{
                  flex: 1, background: 'transparent', border: 'none', outline: 'none',
                  color: 'white', fontSize: '11px', letterSpacing: '0.3px',
                }}
              />
              {galaxySearch && (
                <span
                  onClick={() => { setGalaxySearch(''); setGalaxySearchResults([]); }}
                  style={{ fontSize: '10px', color: 'rgba(255,255,255,0.3)', cursor: 'pointer' }}
                >&times;</span>
              )}
            </div>
            {/* Search results dropdown */}
            {galaxySearchResults.length > 0 && (
              <div data-testid="galaxy-search-results" style={{
                marginTop: '4px', borderRadius: '12px',
                background: 'rgba(0,0,0,0.85)', border: '1px solid rgba(96,165,250,0.15)',
                backdropFilter: 'blur(12px)', overflow: 'hidden',
              }}>
                {galaxySearchResults.map(r => (
                  <div
                    key={r.id}
                    data-testid={`galaxy-search-result-${r.id}`}
                    onClick={() => {
                      enterSolarSystem(r.id, null);
                      setGalaxySearch('');
                      setGalaxySearchResults([]);
                    }}
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      padding: '8px 14px', cursor: 'pointer',
                      transition: 'background 0.15s',
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = 'rgba(59,130,246,0.12)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.7)', fontWeight: 500 }}>{r.name || r.id}</span>
                    <span style={{ fontSize: '9px', color: 'rgba(255,255,255,0.25)' }}>{r.count} mem.</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Color Legend */}
          <div style={{
            position: 'absolute', bottom: '24px', right: '16px',
            display: 'flex', flexDirection: 'column', gap: '4px',
            animation: 'thought-slide-in 0.5s ease-out 0.2s both',
          }}>
            <div style={{ fontSize: '9px', color: 'rgba(255,255,255,0.25)', letterSpacing: '1.5px', textTransform: 'uppercase', marginBottom: '2px' }}>
              Types
            </div>
            {[
              { label: 'Appels', color: '#60a5fa' },
              { label: 'Emails', color: '#34d399' },
              { label: 'CRM', color: '#fbbf24' },
              { label: 'SMS', color: '#22d3ee' },
              { label: 'WhatsApp', color: '#a78bfa' },
              { label: 'Documents', color: '#f97316' },
            ].map(item => (
              <div key={item.label} style={{
                display: 'flex', alignItems: 'center', gap: '6px',
                padding: '3px 10px', borderRadius: '8px',
                background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)',
              }}>
                <span style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: item.color, boxShadow: `0 0 6px ${item.color}` }} />
                <span style={{ fontSize: '10px', color: 'rgba(255,255,255,0.55)', fontWeight: 500 }}>{item.label}</span>
              </div>
            ))}
          </div>

          {/* Top contacts (clickable) */}
          <div style={{
            position: 'absolute', bottom: '24px', left: '16px',
            display: 'flex', flexDirection: 'column', gap: '3px', pointerEvents: 'auto',
            animation: 'thought-slide-in 0.5s ease-out 0.3s both',
          }}>
            <div style={{ fontSize: '9px', color: 'rgba(255,255,255,0.25)', letterSpacing: '1.5px', textTransform: 'uppercase', marginBottom: '2px' }}>
              Top contacts &middot; cliquer pour explorer
            </div>
            {(galaxyData.top_contacts || []).slice(0, 10).map((tc, i) => (
              <div
                key={tc.id || i}
                onClick={() => tc.id && tc.id !== 'unknown' && enterSolarSystem(tc.id, sceneRef.current?.galaxy?.starData?.[i]?.position || null)}
                style={{
                  display: 'flex', alignItems: 'center', gap: '6px',
                  padding: '3px 10px', borderRadius: '6px',
                  background: 'rgba(0,0,0,0.4)', cursor: tc.id && tc.id !== 'unknown' ? 'pointer' : 'default',
                  transition: 'background 0.15s',
                }}
                onMouseEnter={e => { if (tc.id !== 'unknown') e.currentTarget.style.background = 'rgba(59,130,246,0.15)'; }}
                onMouseLeave={e => e.currentTarget.style.background = 'rgba(0,0,0,0.4)'}
              >
                <span style={{
                  width: '5px', height: '5px', borderRadius: '50%', backgroundColor: '#60a5fa',
                  opacity: 0.3 + (1 - i / 10) * 0.7,
                  boxShadow: i < 3 ? '0 0 4px #60a5fa' : 'none',
                }} />
                <span style={{ fontSize: '10px', color: `rgba(255,255,255,${0.3 + (1 - i / 10) * 0.4})`, fontWeight: i < 3 ? 600 : 400 }}>
                  {tc.name || 'Inconnu'}
                </span>
                <span style={{ fontSize: '9px', color: 'rgba(255,255,255,0.2)' }}>{tc.count}</span>
              </div>
            ))}
          </div>

          {/* Loading indicator */}
          {solarLoading && (
            <div style={{
              position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
              padding: '12px 24px', borderRadius: '9999px',
              background: 'rgba(0,0,0,0.8)', border: '1px solid rgba(96,165,250,0.3)',
              backdropFilter: 'blur(12px)', pointerEvents: 'none',
            }}>
              <span style={{ fontSize: '12px', color: '#93c5fd', animation: 'pulse-dot 1s infinite' }}>Chargement du syst&egrave;me solaire...</span>
            </div>
          )}
        </div>
      )}

      {/* ── TIMELAPSE CINEMATIC OVERLAY ── */}
      {galaxyMode && timelapseMode && timelapseData && (
        <div data-testid="timelapse-overlay" style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 30 }}>
          {/* Date + Counter HUD (top) — hidden when summary is showing */}
          {(timelapsePlaying || timelapseProgress < 1) && (
          <div style={{
            position: 'absolute', top: 0, left: 0, right: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '32px',
            padding: '14px 24px',
            background: 'linear-gradient(180deg, rgba(0,0,0,0.7) 0%, transparent 100%)',
            animation: 'thought-slide-in 0.5s ease-out',
          }}>
            {/* Current date */}
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.3)', letterSpacing: '2px', textTransform: 'uppercase' }}>
                Date
              </div>
              <div data-testid="timelapse-date" style={{
                fontSize: '18px', fontWeight: 700, color: 'white',
                fontVariantNumeric: 'tabular-nums', letterSpacing: '1px',
                textShadow: '0 0 20px rgba(96,165,250,0.4)',
              }}>
                {(() => {
                  const w = (timelapseData.weeks || [])[timelapseWeekIdx];
                  if (!w) return '—';
                  const d = new Date(w.date);
                  return d.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
                })()}
              </div>
            </div>

            {/* Memories counter */}
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.3)', letterSpacing: '2px', textTransform: 'uppercase' }}>
                M&eacute;moires
              </div>
              <div data-testid="timelapse-memories" style={{
                fontSize: '22px', fontWeight: 800, color: '#60a5fa',
                fontVariantNumeric: 'tabular-nums',
                textShadow: '0 0 20px rgba(96,165,250,0.5)',
              }}>
                {((timelapseData.weeks || [])[timelapseWeekIdx]?.cumulative || 0).toLocaleString('fr-FR')}
              </div>
            </div>

            {/* Contacts counter */}
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.3)', letterSpacing: '2px', textTransform: 'uppercase' }}>
                Contacts
              </div>
              <div data-testid="timelapse-contacts" style={{
                fontSize: '18px', fontWeight: 700, color: '#34d399',
                fontVariantNumeric: 'tabular-nums',
                textShadow: '0 0 15px rgba(52,211,153,0.4)',
              }}>
                {((timelapseData.weeks || [])[timelapseWeekIdx]?.total_contacts || 0).toLocaleString('fr-FR')}
              </div>
            </div>

            {/* Sources active */}
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.3)', letterSpacing: '2px', textTransform: 'uppercase' }}>
                Sources
              </div>
              <div style={{
                fontSize: '16px', fontWeight: 600, color: '#fbbf24',
                textShadow: '0 0 10px rgba(251,191,36,0.3)',
              }}>
                {(() => {
                  const weeks = timelapseData.weeks || [];
                  const sources = new Set();
                  for (let i = 0; i <= timelapseWeekIdx && i < weeks.length; i++) {
                    (weeks[i].new_types || []).forEach(t => sources.add(t));
                  }
                  return sources.size;
                })()}
              </div>
            </div>
          </div>
          )}

          {/* Milestone flash (center) */}
          {timelapseMilestone && (
            <div data-testid="timelapse-milestone" style={{
              position: 'absolute', top: '42%', left: '50%', transform: 'translate(-50%, -50%)',
              textAlign: 'center', animation: 'thought-slide-in 0.4s ease-out',
              pointerEvents: 'none',
            }}>
              <div style={{
                padding: '14px 28px', borderRadius: '16px',
                background: 'rgba(0,0,0,0.8)', backdropFilter: 'blur(16px)',
                border: '1px solid rgba(251,191,36,0.3)',
                boxShadow: '0 0 40px rgba(251,191,36,0.1)',
              }}>
                <div style={{
                  fontSize: '9px', color: '#fbbf24', letterSpacing: '2px',
                  textTransform: 'uppercase', marginBottom: '4px', fontWeight: 700,
                }}>
                  {timelapseMilestone.type === 'first_memory' ? 'Origine' :
                   timelapseMilestone.type === 'threshold' ? 'Seuil franchi' :
                   timelapseMilestone.type === 'new_source' ? 'Nouvelle source' :
                   timelapseMilestone.type === 'peak_week' ? 'Record' : 'Milestone'}
                </div>
                <div style={{
                  fontSize: '16px', fontWeight: 700, color: 'white',
                  textShadow: '0 0 20px rgba(251,191,36,0.4)',
                }}>
                  {timelapseMilestone.label}
                </div>
                {timelapseMilestone.date && (
                  <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.3)', marginTop: '4px' }}>
                    {new Date(timelapseMilestone.date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Timeline bar (bottom) */}
          <div style={{
            position: 'absolute', bottom: '24px', left: '24px', right: '24px',
            animation: 'thought-slide-in 0.5s ease-out 0.3s both',
          }}>
            {/* Activity histogram */}
            <div style={{
              display: 'flex', alignItems: 'flex-end', height: '30px', gap: '1px',
              marginBottom: '4px', opacity: 0.4,
            }}>
              {(timelapseData.weeks || []).map((w, i) => {
                const maxNew = Math.max(...(timelapseData.weeks || []).map(wk => wk.new_memories || 0), 1);
                const h = Math.max(2, (w.new_memories / maxNew) * 28);
                const active = i <= timelapseWeekIdx;
                return (
                  <div
                    key={i}
                    style={{
                      flex: 1, height: `${h}px`, minWidth: '1px',
                      backgroundColor: active ? '#60a5fa' : 'rgba(255,255,255,0.08)',
                      borderRadius: '1px 1px 0 0',
                      transition: 'background-color 0.15s',
                    }}
                  />
                );
              })}
            </div>

            {/* Progress bar */}
            <div style={{
              height: '3px', borderRadius: '2px',
              background: 'rgba(255,255,255,0.06)',
              overflow: 'hidden',
            }}>
              <div
                data-testid="timelapse-progress"
                style={{
                  height: '100%', borderRadius: '2px',
                  background: 'linear-gradient(90deg, #3b82f6, #60a5fa)',
                  boxShadow: '0 0 8px rgba(96,165,250,0.4)',
                  width: `${timelapseProgress * 100}%`,
                  transition: 'width 0.1s linear',
                }}
              />
            </div>

            {/* Date labels */}
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '4px' }}>
              <span style={{ fontSize: '9px', color: 'rgba(255,255,255,0.2)' }}>
                {timelapseData.totals?.first_date ? new Date(timelapseData.totals.first_date).toLocaleDateString('fr-FR', { month: 'short', year: 'numeric' }) : ''}
              </span>
              <span style={{ fontSize: '9px', color: 'rgba(255,255,255,0.2)' }}>
                {timelapseData.totals?.last_date ? new Date(timelapseData.totals.last_date).toLocaleDateString('fr-FR', { month: 'short', year: 'numeric' }) : ''}
              </span>
            </div>
          </div>

          {/* Skip / Close button */}
          {timelapsePlaying ? (
            <button
              data-testid="timelapse-skip-btn"
              onClick={skipTimelapse}
              style={{
                position: 'absolute', bottom: '80px', right: '24px', pointerEvents: 'auto',
                padding: '6px 16px', borderRadius: '9999px', cursor: 'pointer',
                background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)',
                color: 'rgba(255,255,255,0.4)', fontSize: '10px', fontWeight: 600,
                transition: 'all 0.2s',
              }}
              onMouseEnter={e => { e.currentTarget.style.color = 'rgba(255,255,255,0.7)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.3)'; }}
              onMouseLeave={e => { e.currentTarget.style.color = 'rgba(255,255,255,0.4)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.12)'; }}
            >
              Skip &raquo;
            </button>
          ) : (
            <button
              data-testid="timelapse-close-btn"
              onClick={exitTimelapse}
              style={{
                position: 'absolute', bottom: '80px', right: '24px', pointerEvents: 'auto',
                padding: '6px 16px', borderRadius: '9999px', cursor: 'pointer',
                background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(96,165,250,0.25)',
                color: '#93c5fd', fontSize: '10px', fontWeight: 600,
                transition: 'all 0.2s',
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(96,165,250,0.6)'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(96,165,250,0.25)'; }}
            >
              Fermer le Time-Lapse
            </button>
          )}

          {/* Final summary (after timelapse finishes) */}
          {!timelapsePlaying && timelapseProgress >= 1 && (
            <div data-testid="timelapse-summary" style={{
              position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
              animation: 'thought-slide-in 0.6s ease-out',
            }}>
              <div style={{
                padding: '24px 36px', borderRadius: '20px',
                background: 'rgba(2,8,16,0.9)', backdropFilter: 'blur(20px)',
                border: '1px solid rgba(96,165,250,0.15)',
                boxShadow: '0 8px 60px rgba(0,0,0,0.5), 0 0 40px rgba(96,165,250,0.05)',
                textAlign: 'center', minWidth: '300px',
              }}>
                <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.25)', letterSpacing: '3px', textTransform: 'uppercase', marginBottom: '12px' }}>
                  {(() => {
                    const f = timelapseData.totals?.first_date;
                    const l = timelapseData.totals?.last_date;
                    if (!f || !l) return '';
                    const days = Math.round((new Date(l) - new Date(f)) / (1000 * 60 * 60 * 24));
                    const months = Math.round(days / 30);
                    return months > 0 ? `${months} mois d'apprentissage` : `${days} jours d'apprentissage`;
                  })()}
                </div>
                <div style={{ display: 'flex', justifyContent: 'center', gap: '28px', marginBottom: '14px' }}>
                  <div>
                    <div style={{ fontSize: '28px', fontWeight: 800, color: '#60a5fa', fontVariantNumeric: 'tabular-nums' }}>
                      {(timelapseData.totals?.memories || 0).toLocaleString('fr-FR')}
                    </div>
                    <div style={{ fontSize: '9px', color: 'rgba(255,255,255,0.3)', letterSpacing: '1px' }}>m&eacute;moires</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '28px', fontWeight: 800, color: '#34d399', fontVariantNumeric: 'tabular-nums' }}>
                      {(timelapseData.totals?.contacts || 0).toLocaleString('fr-FR')}
                    </div>
                    <div style={{ fontSize: '9px', color: 'rgba(255,255,255,0.3)', letterSpacing: '1px' }}>contacts</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '28px', fontWeight: 800, color: '#fbbf24', fontVariantNumeric: 'tabular-nums' }}>
                      {timelapseData.totals?.sources || 0}
                    </div>
                    <div style={{ fontSize: '9px', color: 'rgba(255,255,255,0.3)', letterSpacing: '1px' }}>sources</div>
                  </div>
                </div>
                <div style={{
                  fontSize: '11px', color: 'rgba(255,255,255,0.2)', borderTop: '1px solid rgba(255,255,255,0.06)',
                  paddingTop: '10px',
                }}>
                  Vitesse actuelle : ~{timelapseData.totals?.avg_per_day || 0} m&eacute;moires/jour
                </div>
                <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.12)', marginTop: '8px', fontStyle: 'italic' }}>
                  Le clone n'oublie jamais.
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── SOLAR SYSTEM OVERLAY — "Isolation Solaire" ── */}
      {galaxyMode && solarMode && solarData && (
        <div data-testid="solar-overlay" style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 20 }}>
          {/* Glassmorphism header bar */}
          <div style={{
            position: 'absolute', top: 0, left: 0, right: 0,
            display: 'flex', alignItems: 'center', gap: '16px',
            padding: '12px 20px',
            background: 'rgba(2,8,16,0.75)', backdropFilter: 'blur(16px)',
            borderBottom: '1px solid rgba(255,255,255,0.06)',
            animation: 'thought-slide-in 0.5s ease-out',
          }}>
            {/* Back button */}
            <button
              data-testid="solar-back-btn"
              onClick={exitSolarSystem}
              style={{
                pointerEvents: 'auto', display: 'flex', alignItems: 'center', gap: '6px',
                padding: '6px 14px', borderRadius: '9999px',
                background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(167,139,250,0.3)',
                color: '#c4b5fd', cursor: 'pointer', fontSize: '11px', fontWeight: 600,
                transition: 'all 0.2s', letterSpacing: '0.5px',
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(167,139,250,0.6)'; e.currentTarget.style.background = 'rgba(139,92,246,0.15)'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(167,139,250,0.3)'; e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; }}
            >
              &larr; Galaxie
            </button>

            {/* Contact name + stats */}
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: '10px' }}>
                <span style={{
                  fontSize: '16px', fontWeight: 700, color: 'white', letterSpacing: '1.5px',
                  textShadow: '0 0 20px rgba(167,139,250,0.4)',
                }}>
                  {solarData.contact?.name || 'Contact'}
                </span>
                {solarData.contact?.phone && (
                  <span style={{ fontSize: '10px', color: 'rgba(255,255,255,0.25)', padding: '2px 8px', borderRadius: '4px', background: 'rgba(255,255,255,0.05)' }}>
                    {solarData.contact.phone}
                  </span>
                )}
              </div>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {Object.entries(solarData.stats || {}).map(([type, count]) => {
                  const typeLabels = { call: 'Appels', email: 'Emails', crm_note: 'CRM', sms: 'SMS', whatsapp: 'WhatsApp', document: 'Docs' };
                  const typeColors = { call: '#60a5fa', email: '#34d399', crm_note: '#fbbf24', sms: '#22d3ee', whatsapp: '#a78bfa', document: '#f97316' };
                  return (
                    <div key={type} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <span style={{ width: '5px', height: '5px', borderRadius: '50%', backgroundColor: typeColors[type] || '#888', boxShadow: `0 0 4px ${typeColors[type] || '#888'}` }} />
                      <span style={{ fontSize: '10px', color: 'rgba(255,255,255,0.4)' }}>{count} {typeLabels[type] || type}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.15)' }}>
              {solarData.total || 0} m&eacute;moires
            </div>
          </div>

          {/* Orbit band labels (left) */}
          <div style={{
            position: 'absolute', bottom: '24px', left: '16px',
            display: 'flex', flexDirection: 'column', gap: '4px',
            animation: 'thought-slide-in 0.5s ease-out 0.2s both',
          }}>
            <div style={{ fontSize: '9px', color: 'rgba(255,255,255,0.25)', letterSpacing: '1.5px', textTransform: 'uppercase', marginBottom: '2px' }}>
              Orbites temporelles
            </div>
            {['Cette semaine', 'Ce mois', '3 derniers mois', '6 derniers mois', 'Plus ancien'].map((label, i) => {
              const bandColors = ['#fbbf24', '#f59e0b', '#8b5cf6', '#3b82f6', '#1e3a5f'];
              return (
                <div key={label} style={{
                  display: 'flex', alignItems: 'center', gap: '6px',
                  padding: '2px 8px', borderRadius: '6px',
                  background: 'rgba(0,0,0,0.4)',
                }}>
                  <div style={{ width: '14px', height: '2px', backgroundColor: bandColors[i], opacity: 0.5 + (1 - i / 5) * 0.5, borderRadius: '1px' }} />
                  <span style={{ fontSize: '10px', color: `rgba(255,255,255,${0.25 + (1 - i / 5) * 0.25})` }}>{label}</span>
                </div>
              );
            })}
          </div>

          {/* Clickable Type Legend (right) */}
          <div style={{
            position: 'absolute', bottom: '24px', right: '16px',
            display: 'flex', flexDirection: 'column', gap: '4px',
            animation: 'thought-slide-in 0.5s ease-out 0.3s both',
          }}>
            <div style={{ fontSize: '9px', color: 'rgba(255,255,255,0.25)', letterSpacing: '1.5px', textTransform: 'uppercase', marginBottom: '2px' }}>
              Filtres
            </div>
            {[
              { type: null, label: 'Tous', color: '#ffffff' },
              { type: 'call', label: 'Appels', color: '#60a5fa' },
              { type: 'email', label: 'Emails', color: '#34d399' },
              { type: 'crm_note', label: 'CRM', color: '#fbbf24' },
              { type: 'sms', label: 'SMS', color: '#22d3ee' },
              { type: 'whatsapp', label: 'WhatsApp', color: '#a78bfa' },
              { type: 'document', label: 'Documents', color: '#f97316' },
            ].map(item => {
              const isActive = solarTypeFilter === item.type;
              return (
                <div
                  key={item.label}
                  data-testid={`solar-filter-${item.type || 'all'}`}
                  onClick={() => {
                    const newFilter = isActive ? null : item.type;
                    setSolarTypeFilter(newFilter);
                    if (sceneRef.current?.solarSystem) {
                      sceneRef.current.solarSystem.setTypeFilter(newFilter);
                    }
                  }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '6px',
                    padding: '4px 12px', borderRadius: '8px', cursor: 'pointer', pointerEvents: 'auto',
                    background: isActive ? `${item.color}15` : 'rgba(0,0,0,0.5)',
                    border: isActive ? `1px solid ${item.color}50` : '1px solid transparent',
                    backdropFilter: 'blur(4px)', transition: 'all 0.2s',
                  }}
                >
                  <span style={{
                    width: '6px', height: '6px', borderRadius: '50%',
                    backgroundColor: item.color,
                    boxShadow: isActive ? `0 0 8px ${item.color}` : `0 0 4px ${item.color}60`,
                    opacity: isActive ? 1 : 0.6,
                  }} />
                  <span style={{
                    fontSize: '10px', fontWeight: isActive ? 600 : 500,
                    color: isActive ? item.color : 'rgba(255,255,255,0.45)',
                  }}>{item.label}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── SOLAR MEMORY TOOLTIP — Fixed card with liaison line ── */}
      {solarMode && (hoveredMemory || pinnedMemory) && (() => {
        const mem = pinnedMemory || hoveredMemory;
        const typeColors = { call: '#60a5fa', email: '#34d399', crm_note: '#fbbf24', sms: '#22d3ee', whatsapp: '#a78bfa', document: '#f97316' };
        const typeLabels = { call: 'Appel', email: 'Email', crm_note: 'Note CRM', sms: 'SMS', whatsapp: 'WhatsApp', document: 'Document' };
        const dirLabels = { inbound: 'entrant', outbound: 'sortant' };
        const tc = typeColors[mem.type] || '#888';
        const tl = typeLabels[mem.type] || mem.type;
        const dirLabel = mem.direction ? ` ${dirLabels[mem.direction] || ''}` : '';
        const d = mem.timestamp ? new Date(mem.timestamp) : null;
        const dateStr = d ? d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';
        const durStr = mem.duration ? `${Math.floor(mem.duration / 60)}min ${mem.duration % 60}s` : '';
        return (
          <>
            {/* SVG liaison line (drawn from planet to card) */}
            <svg
              ref={solarTooltipRef}
              data-testid="solar-tooltip-line"
              style={{
                position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 24,
                width: '100%', height: '100%', opacity: 0,
              }}
            >
              <line
                id="solar-liaison"
                x1="0" y1="0" x2="0" y2="0"
                stroke={tc} strokeWidth="1" strokeDasharray="4 3" opacity="0.35"
              />
            </svg>
            {/* Fixed card — bottom right */}
            <div
              data-testid="solar-tooltip"
              style={{
                position: 'absolute', bottom: '100px', right: '24px',
                pointerEvents: pinnedMemory ? 'auto' : 'none',
                maxWidth: '320px', zIndex: 25,
                animation: 'thought-slide-in 0.25s ease-out',
              }}
            >
              <div style={{
                display: 'flex', borderRadius: '14px', overflow: 'hidden',
                background: 'rgba(5,5,15,0.92)', border: `1px solid ${tc}25`,
                backdropFilter: 'blur(20px)',
                boxShadow: `0 8px 40px rgba(0,0,0,0.6), 0 0 30px ${tc}08`,
              }}>
                {/* Left color accent bar */}
                <div style={{ width: '4px', backgroundColor: tc, opacity: 0.7, flexShrink: 0 }} />
                <div style={{ padding: '12px 16px', flex: 1 }}>
                  {/* Type badge + close */}
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <span style={{ width: '7px', height: '7px', borderRadius: '50%', backgroundColor: tc, boxShadow: `0 0 8px ${tc}` }} />
                      <span style={{ fontSize: '10px', fontWeight: 700, color: tc, textTransform: 'uppercase', letterSpacing: '1.2px' }}>
                        {tl}{dirLabel ? ` ${dirLabel}` : ''}
                      </span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      {durStr && <span style={{ fontSize: '9px', color: 'rgba(255,255,255,0.3)' }}>{durStr}</span>}
                      {pinnedMemory && (
                        <span
                          onClick={(e) => {
                            e.stopPropagation();
                            const sr = sceneRef.current;
                            if (sr?.solarSystem && pinnedMemoryMeshRef.current) {
                              sr.solarSystem.freezeMemory(pinnedMemoryMeshRef.current, false);
                            }
                            setPinnedMemory(null);
                            pinnedMemoryMeshRef.current = null;
                            isPinnedRef.current = false;
                          }}
                          style={{ fontSize: '12px', color: 'rgba(255,255,255,0.25)', cursor: 'pointer', lineHeight: 1 }}
                        >&times;</span>
                      )}
                    </div>
                  </div>
                  {/* Date */}
                  {dateStr && (
                    <div style={{ fontSize: '9px', color: 'rgba(255,255,255,0.3)', marginBottom: '6px', letterSpacing: '0.3px' }}>
                      {dateStr}
                    </div>
                  )}
                  {/* Summary */}
                  {mem.summary && (
                    <div style={{
                      fontSize: '11px', color: 'rgba(255,255,255,0.78)', lineHeight: '1.55',
                      borderTop: '1px solid rgba(255,255,255,0.06)', paddingTop: '6px',
                    }}>
                      {mem.summary}
                    </div>
                  )}
                  {/* Source */}
                  {mem.source && (
                    <div style={{ fontSize: '8px', color: 'rgba(255,255,255,0.12)', marginTop: '6px', wordBreak: 'break-all' }}>
                      {mem.source}
                    </div>
                  )}
                  {/* Pin indicator */}
                  {pinnedMemory && (
                    <div style={{ fontSize: '8px', color: 'rgba(255,255,255,0.15)', marginTop: '6px', textAlign: 'center', letterSpacing: '0.5px' }}>
                      &#128204; &Eacute;pingl&eacute; &middot; cliquer ailleurs pour fermer
                    </div>
                  )}
                </div>
              </div>
            </div>
          </>
        );
      })()}

      {/* ── THOUGHT VISUALIZATION OVERLAY ("La Pensée en Direct") ── */}
      {thoughtState && (
        <div
          ref={thoughtOverlayRef}
          data-testid="thought-overlay"
          style={{
            position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 30,
            transition: 'opacity 0.5s',
            opacity: thoughtState.phase === 'complete' ? 0.8 : 1,
          }}
        >
          {/* Phase indicator — top center */}
          <div
            ref={thoughtPhaseRef}
            data-testid="thought-phase"
            style={{
              position: 'absolute', top: '5%', left: '50%', transform: 'translateX(-50%)',
              display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px',
            }}
          >
            <div style={{
              display: 'flex', alignItems: 'center', gap: '10px',
              padding: '8px 20px', borderRadius: '24px',
              background: 'rgba(0,0,0,0.7)', border: `1px solid ${THOUGHT_PHASE_CONFIG[thoughtState.phase]?.color || '#fff'}40`,
              backdropFilter: 'blur(12px)',
            }}>
              <div style={{
                width: '8px', height: '8px', borderRadius: '50%',
                background: THOUGHT_PHASE_CONFIG[thoughtState.phase]?.color || '#fff',
                boxShadow: `0 0 12px ${THOUGHT_PHASE_CONFIG[thoughtState.phase]?.color || '#fff'}`,
                animation: thoughtState.phase !== 'complete' ? 'pulse-dot 1s ease-in-out infinite' : 'none',
              }} />
              <span style={{
                fontSize: '13px', fontWeight: 600, letterSpacing: '1.5px',
                color: THOUGHT_PHASE_CONFIG[thoughtState.phase]?.color || '#fff',
                textTransform: 'uppercase',
              }}>
                {THOUGHT_PHASE_CONFIG[thoughtState.phase]?.label || ''}
              </span>
            </div>
            {thoughtState.query && (
              <div style={{
                fontSize: '11px', color: 'rgba(255,255,255,0.4)', fontStyle: 'italic',
                maxWidth: '400px', textAlign: 'center', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>
                &laquo; {thoughtState.query} &raquo;
              </div>
            )}
          </div>

          {/* Filter tags — left side near Workers */}
          {(thoughtState.phase === 'analyzing' || thoughtState.phase === 'scanning' || thoughtState.phase === 'reasoning' || thoughtState.phase === 'complete') && (
            <div
              ref={thoughtFiltersRef}
              data-testid="thought-filters"
              style={{
                position: 'absolute', top: '20%', right: '4%',
                display: 'flex', flexDirection: 'column', gap: '6px',
                animation: 'thought-slide-in 0.4s ease-out',
              }}
            >
              {thoughtState.filters?.time && (
                <div style={{
                  padding: '4px 12px', borderRadius: '12px', fontSize: '11px', fontWeight: 600,
                  background: 'rgba(52, 211, 153, 0.15)', border: '1px solid rgba(52, 211, 153, 0.3)',
                  color: '#34d399', letterSpacing: '0.5px',
                }}>
                  Periode : {thoughtState.filters.time}
                </div>
              )}
              {thoughtState.filters?.contact && (
                <div style={{
                  padding: '4px 12px', borderRadius: '12px', fontSize: '11px', fontWeight: 600,
                  background: 'rgba(96, 165, 250, 0.15)', border: '1px solid rgba(96, 165, 250, 0.3)',
                  color: '#60a5fa', letterSpacing: '0.5px',
                }}>
                  Contact : {thoughtState.filters.contact}
                </div>
              )}
              {thoughtState.filters?.source && thoughtState.filters.source !== 'all' && (
                <div style={{
                  padding: '4px 12px', borderRadius: '12px', fontSize: '11px', fontWeight: 600,
                  background: 'rgba(244, 114, 182, 0.15)', border: '1px solid rgba(244, 114, 182, 0.3)',
                  color: '#f472b6', letterSpacing: '0.5px',
                }}>
                  Source : {thoughtState.filters.source}
                </div>
              )}
              {!thoughtState.filters?.time && !thoughtState.filters?.contact && (
                <div style={{
                  padding: '4px 12px', borderRadius: '12px', fontSize: '11px', fontWeight: 600,
                  background: 'rgba(255,255,255, 0.08)', border: '1px solid rgba(255,255,255, 0.15)',
                  color: 'rgba(255,255,255,0.5)', letterSpacing: '0.5px',
                }}>
                  Recherche globale
                </div>
              )}
            </div>
          )}

          {/* Memory scan indicator — left side near Memory hemisphere */}
          {(thoughtState.phase === 'scanning' || thoughtState.phase === 'reasoning' || thoughtState.phase === 'complete') && (
            <div
              ref={thoughtMemScanRef}
              data-testid="thought-mem-scan"
              style={{
                position: 'absolute', bottom: '25%', left: '4%',
                animation: 'thought-slide-in 0.4s ease-out',
              }}
            >
              <div style={{
                padding: '6px 14px', borderRadius: '12px', fontSize: '12px',
                background: 'rgba(167, 139, 250, 0.12)', border: '1px solid rgba(167, 139, 250, 0.25)',
                color: '#a78bfa', fontWeight: 600, letterSpacing: '0.5px',
              }}>
                {thoughtState.memoriesUsed} memoire{thoughtState.memoriesUsed !== 1 ? 's' : ''} activee{thoughtState.memoriesUsed !== 1 ? 's' : ''}
              </div>
            </div>
          )}

          {/* Response ready indicator — bottom center */}
          {thoughtState.phase === 'complete' && (
            <div
              ref={thoughtResponseRef}
              data-testid="thought-response"
              style={{
                position: 'absolute', bottom: '8%', left: '50%', transform: 'translateX(-50%)',
                display: 'flex', alignItems: 'center', gap: '10px',
                animation: 'thought-slide-in 0.4s ease-out',
              }}
            >
              <div style={{
                padding: '6px 16px', borderRadius: '16px', fontSize: '12px',
                background: 'rgba(96, 165, 250, 0.12)', border: '1px solid rgba(96, 165, 250, 0.25)',
                color: '#60a5fa', fontWeight: 600, letterSpacing: '0.5px',
              }}>
                Reponse generee en {thoughtState.responseTimeMs}ms
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── WORKER MODE OVERLAY — "Les Workers en Action" ── */}
      {workerMode && workerData && (
        <div data-testid="worker-overlay" style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 20 }}>
          {/* Back button */}
          <button
            data-testid="worker-back-btn"
            onClick={exitWorkerMode}
            style={{
              position: 'absolute', top: '16px', left: '16px', pointerEvents: 'auto',
              display: 'flex', alignItems: 'center', gap: '8px',
              padding: '8px 18px', borderRadius: '9999px',
              background: 'rgba(0,0,0,0.7)', border: '1px solid rgba(34,197,94,0.3)',
              color: '#86efac', cursor: 'pointer', fontSize: '12px', fontWeight: 600,
              backdropFilter: 'blur(8px)', transition: 'all 0.2s', letterSpacing: '0.5px',
            }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(34,197,94,0.6)'; e.currentTarget.style.background = 'rgba(34,197,94,0.12)'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(34,197,94,0.3)'; e.currentTarget.style.background = 'rgba(0,0,0,0.7)'; }}
          >
            &larr; Retour au cerveau
          </button>

          {/* Title */}
          <div style={{
            position: 'absolute', top: '16px', left: '50%', transform: 'translateX(-50%)',
            textAlign: 'center', animation: 'thought-slide-in 0.6s ease-out',
          }}>
            <div style={{
              fontSize: '16px', fontWeight: 700, color: 'white', letterSpacing: '3px',
              textTransform: 'uppercase',
              textShadow: '0 0 30px rgba(34,197,94,0.4), 0 0 60px rgba(34,197,94,0.15)',
            }}>
              Workers en Action
            </div>
            <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.35)', letterSpacing: '1px', marginTop: '4px' }}>
              Pendant que vous dormez, votre clone travaille
            </div>
          </div>

          {/* Live throughput counter (top-right) */}
          <div style={{
            position: 'absolute', top: '16px', right: '16px',
            animation: 'thought-slide-in 0.5s ease-out 0.2s both',
          }}>
            <div style={{
              padding: '12px 18px', borderRadius: '14px',
              background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(12px)',
              border: '1px solid rgba(34,197,94,0.15)',
            }}>
              {/* Throughput */}
              <div style={{ textAlign: 'center', marginBottom: '10px' }}>
                <div style={{ fontSize: '28px', fontWeight: 800, color: '#22c55e', fontVariantNumeric: 'tabular-nums', letterSpacing: '1px' }}>
                  {wd.throughput_per_hour}
                </div>
                <div style={{ fontSize: '9px', color: 'rgba(255,255,255,0.25)', letterSpacing: '1.5px', textTransform: 'uppercase' }}>
                  m&eacute;moires / heure
                </div>
              </div>

              {/* Stats row */}
              <div style={{ display: 'flex', gap: '14px', paddingTop: '8px', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '16px', fontWeight: 700, color: '#22c55e' }}>{wd.totals_24h?.completed || 0}</div>
                  <div style={{ fontSize: '8px', color: 'rgba(255,255,255,0.25)' }}>termin&eacute;es</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '16px', fontWeight: 700, color: '#ef4444' }}>{wd.totals_24h?.failed || 0}</div>
                  <div style={{ fontSize: '8px', color: 'rgba(255,255,255,0.25)' }}>erreurs</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '16px', fontWeight: 700, color: '#fbbf24' }}>{wd.queue || 0}</div>
                  <div style={{ fontSize: '8px', color: 'rgba(255,255,255,0.25)' }}>en file</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '16px', fontWeight: 700, color: '#60a5fa' }}>{wd.success_rate}%</div>
                  <div style={{ fontSize: '8px', color: 'rgba(255,255,255,0.25)' }}>succ&egrave;s</div>
                </div>
              </div>

              {/* All-time stats */}
              <div style={{ marginTop: '8px', paddingTop: '6px', borderTop: '1px solid rgba(255,255,255,0.04)' }}>
                <div style={{ fontSize: '9px', color: 'rgba(255,255,255,0.2)', letterSpacing: '1px', textTransform: 'uppercase', marginBottom: '4px' }}>Depuis toujours</div>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <span style={{ fontSize: '10px', color: 'rgba(255,255,255,0.4)' }}>{(wd.all_time?.total_jobs || 0).toLocaleString()} jobs</span>
                  <span style={{ fontSize: '10px', color: '#22c55e' }}>{(wd.all_time?.memories_created || 0).toLocaleString()} m&eacute;m.</span>
                </div>
              </div>
            </div>
          </div>

          {/* Hourly histogram (bottom-center) */}
          <div style={{
            position: 'absolute', bottom: '20px', left: '50%', transform: 'translateX(-50%)',
            animation: 'thought-slide-in 0.5s ease-out 0.4s both',
          }}>
            <div style={{
              padding: '10px 16px', borderRadius: '12px',
              background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(8px)',
              border: '1px solid rgba(34,197,94,0.1)',
            }}>
              <div style={{ fontSize: '9px', color: 'rgba(255,255,255,0.2)', letterSpacing: '1.5px', textTransform: 'uppercase', marginBottom: '6px', textAlign: 'center' }}>
                D&eacute;bit horaire &middot; derni&egrave;res 24h
              </div>
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: '2px', height: '40px' }}>
                {(wd.hourly_histogram || []).map((h, i) => {
                  const max = Math.max(...(wd.hourly_histogram || []).map(x => x.completed + x.failed), 1);
                  const total = h.completed + h.failed;
                  const barH = Math.max((total / max) * 36, 1);
                  const failRatio = total > 0 ? h.failed / total : 0;
                  const now = new Date().getHours();
                  return (
                    <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '10px' }}>
                      <div style={{ position: 'relative', width: '6px', height: `${barH}px`, borderRadius: '2px', overflow: 'hidden' }}>
                        <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: `${(1 - failRatio) * 100}%`, background: i === now ? '#22c55e' : 'rgba(34,197,94,0.4)', borderRadius: '2px 2px 0 0' }} />
                        {failRatio > 0 && (
                          <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: `${failRatio * 100}%`, background: 'rgba(239,68,68,0.5)', borderRadius: '0 0 2px 2px' }} />
                        )}
                      </div>
                      {i % 6 === 0 && (
                        <span style={{ fontSize: '7px', color: 'rgba(255,255,255,0.15)', marginTop: '2px' }}>{i}h</span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Type breakdown (bottom-left) */}
          <div style={{
            position: 'absolute', bottom: '20px', left: '16px',
            animation: 'thought-slide-in 0.5s ease-out 0.3s both',
          }}>
            <div style={{
              padding: '10px 14px', borderRadius: '12px',
              background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(8px)',
              border: '1px solid rgba(34,197,94,0.1)',
            }}>
              <div style={{ fontSize: '9px', color: 'rgba(255,255,255,0.2)', letterSpacing: '1.5px', textTransform: 'uppercase', marginBottom: '6px' }}>
                Sources &middot; 7 derniers jours
              </div>
              {(wd.type_breakdown || []).map((tb, i) => {
                const colors = { ringover_sync: '#60a5fa', email_sync: '#34d399', teams_sync: '#22d3ee', scan_duplicates: '#f97316', crm_sync: '#fbbf24' };
                const labels = { ringover_sync: 'Ringover', email_sync: 'Emails', teams_sync: 'Teams', scan_duplicates: 'Scan Doublons', crm_sync: 'CRM' };
                const c = colors[tb.type] || '#8b5cf6';
                return (
                  <div key={tb.type || i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', marginBottom: '3px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <span style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: c, boxShadow: `0 0 4px ${c}` }} />
                      <span style={{ fontSize: '10px', color: 'rgba(255,255,255,0.55)' }}>{labels[tb.type] || tb.type}</span>
                    </div>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <span style={{ fontSize: '10px', color: 'rgba(255,255,255,0.4)', fontVariantNumeric: 'tabular-nums' }}>{tb.count}</span>
                      {tb.failed > 0 && (
                        <span style={{ fontSize: '9px', color: '#ef4444', opacity: 0.7 }}>({tb.failed} err)</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Error bin (bottom-right) */}
          {wd.error_bin && wd.error_bin.length > 0 && (
            <div style={{
              position: 'absolute', bottom: '20px', right: '16px',
              maxWidth: '280px',
              animation: 'thought-slide-in 0.5s ease-out 0.5s both',
            }}>
              <div style={{
                padding: '10px 14px', borderRadius: '12px',
                background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(8px)',
                border: '1px solid rgba(239,68,68,0.15)',
              }}>
                <div style={{ fontSize: '9px', color: 'rgba(239,68,68,0.5)', letterSpacing: '1.5px', textTransform: 'uppercase', marginBottom: '6px' }}>
                  Bac d'erreurs
                </div>
                {wd.error_bin.slice(0, 5).map((err, i) => {
                  const labels = { ringover_sync: 'Ringover', email_sync: 'Email', teams_sync: 'Teams' };
                  return (
                    <div key={i} style={{ marginBottom: '4px', paddingBottom: '4px', borderBottom: '1px solid rgba(255,255,255,0.03)' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <span style={{ width: '4px', height: '4px', borderRadius: '50%', backgroundColor: '#ef4444' }} />
                        <span style={{ fontSize: '9px', color: 'rgba(255,255,255,0.35)' }}>{labels[err.type] || err.type}</span>
                      </div>
                      <div style={{ fontSize: '8px', color: 'rgba(239,68,68,0.4)', marginLeft: '8px', marginTop: '1px', lineHeight: 1.3 }}>
                        {(err.error || '').slice(0, 80)}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Zone labels on the belt */}
          <div style={{
            position: 'absolute', top: '55%', left: '15%', transform: 'translate(-50%, -50%)',
            animation: 'thought-slide-in 0.4s ease-out 0.6s both',
          }}>
            <span style={{ fontSize: '9px', color: 'rgba(107,114,128,0.4)', letterSpacing: '2px', textTransform: 'uppercase' }}>File d'attente</span>
          </div>
          <div style={{
            position: 'absolute', top: '40%', left: '50%', transform: 'translate(-50%, -50%)',
            animation: 'thought-slide-in 0.4s ease-out 0.7s both',
          }}>
            <span style={{ fontSize: '10px', color: 'rgba(251,191,36,0.45)', letterSpacing: '2px', textTransform: 'uppercase', textShadow: '0 0 20px rgba(251,191,36,0.2)' }}>Traitement</span>
          </div>
          <div style={{
            position: 'absolute', top: '55%', right: '20%', transform: 'translate(50%, -50%)',
            animation: 'thought-slide-in 0.4s ease-out 0.8s both',
          }}>
            <span style={{ fontSize: '9px', color: 'rgba(34,197,94,0.4)', letterSpacing: '2px', textTransform: 'uppercase' }}>Termin&eacute;</span>
          </div>

          {/* Hover tooltip on cube */}
          {workerHoveredTask && (
            <div data-testid="worker-hover-tooltip" style={{
              position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
              pointerEvents: 'none', animation: 'thought-slide-in 0.15s ease-out',
              zIndex: 25,
            }}>
              <div style={{
                padding: '12px 18px', borderRadius: '14px', minWidth: '200px',
                background: 'rgba(2,8,16,0.92)', backdropFilter: 'blur(16px)',
                border: `1px solid ${workerHoveredTask.status === 'failed' ? 'rgba(239,68,68,0.25)' : 'rgba(34,197,94,0.2)'}`,
                boxShadow: '0 4px 30px rgba(0,0,0,0.6)',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                  <span style={{
                    width: '8px', height: '8px', borderRadius: '50%',
                    backgroundColor: ({
                      ringover_sync: '#60a5fa', email_sync: '#34d399', teams_sync: '#22d3ee',
                      scan_duplicates: '#f97316', crm_sync: '#fbbf24',
                    })[workerHoveredTask.type] || '#8b5cf6',
                    boxShadow: `0 0 6px ${({
                      ringover_sync: '#60a5fa', email_sync: '#34d399', teams_sync: '#22d3ee',
                      scan_duplicates: '#f97316', crm_sync: '#fbbf24',
                    })[workerHoveredTask.type] || '#8b5cf6'}`,
                  }} />
                  <span style={{ fontSize: '13px', fontWeight: 700, color: 'white', letterSpacing: '0.5px' }}>
                    {({
                      ringover_sync: 'Ringover Sync', email_sync: 'Email Sync', teams_sync: 'Teams Sync',
                      scan_duplicates: 'Scan Doublons', crm_sync: 'CRM Sync', whatsapp_sync: 'WhatsApp Sync',
                    })[workerHoveredTask.type] || workerHoveredTask.type}
                  </span>
                </div>
                <div style={{ display: 'flex', gap: '12px', marginBottom: '6px' }}>
                  <span style={{ fontSize: '10px', color: 'rgba(255,255,255,0.4)' }}>
                    {workerHoveredTask.items_processed || 0} contacts
                  </span>
                  <span style={{ fontSize: '10px', color: '#22c55e' }}>
                    {workerHoveredTask.items_created || 0} m&eacute;moires
                  </span>
                </div>
                {workerHoveredTask.status === 'failed' && workerHoveredTask.error && (
                  <div style={{
                    padding: '4px 8px', borderRadius: '6px',
                    background: 'rgba(239,68,68,0.08)',
                    marginBottom: '6px',
                  }}>
                    <div style={{ fontSize: '9px', color: 'rgba(239,68,68,0.6)', lineHeight: 1.4 }}>
                      {(workerHoveredTask.error || '').slice(0, 80)}
                    </div>
                  </div>
                )}
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <span style={{
                    width: '5px', height: '5px', borderRadius: '50%',
                    backgroundColor: workerHoveredTask.status === 'failed' ? '#ef4444' : '#22c55e',
                  }} />
                  <span style={{
                    fontSize: '10px', fontWeight: 600,
                    color: workerHoveredTask.status === 'failed' ? '#ef4444' : '#22c55e',
                  }}>
                    {workerHoveredTask.status === 'failed' ? 'Echou\u00e9' : workerHoveredTask.status === 'completed' ? 'Termin\u00e9' : 'En cours'}
                  </span>
                  {workerHoveredTask.started_at && (
                    <span style={{ fontSize: '9px', color: 'rgba(255,255,255,0.2)' }}>
                      {(() => {
                        const diff = Date.now() - new Date(workerHoveredTask.started_at).getTime();
                        const mins = Math.floor(diff / 60000);
                        if (mins < 60) return `il y a ${mins}min`;
                        const hours = Math.floor(mins / 60);
                        if (hours < 24) return `il y a ${hours}h`;
                        return `il y a ${Math.floor(hours / 24)}j`;
                      })()}
                    </span>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── NETWORK MODE OVERLAY — "Réseau Neuronal des Contacts" ── */}
      {networkMode && networkData && (
        <div data-testid="network-overlay" style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 20 }}>
          {/* Back button */}
          <button
            data-testid="network-back-btn"
            onClick={() => { exitNetwork(); }}
            style={{
              position: 'absolute', top: '16px', left: '16px', pointerEvents: 'auto',
              display: 'flex', alignItems: 'center', gap: '8px',
              padding: '8px 18px', borderRadius: '9999px',
              background: 'rgba(0,0,0,0.7)', border: '1px solid rgba(192,132,252,0.3)',
              color: '#d8b4fe', cursor: 'pointer', fontSize: '12px', fontWeight: 600,
              backdropFilter: 'blur(8px)', transition: 'all 0.2s',
              letterSpacing: '0.5px',
            }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(192,132,252,0.6)'; e.currentTarget.style.background = 'rgba(139,92,246,0.15)'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(192,132,252,0.3)'; e.currentTarget.style.background = 'rgba(0,0,0,0.7)'; }}
          >
            &larr; Retour au cerveau
          </button>

          {/* Title */}
          <div style={{
            position: 'absolute', top: '16px', left: '50%', transform: 'translateX(-50%)',
            textAlign: 'center', animation: 'thought-slide-in 0.6s ease-out',
          }}>
            <div style={{
              fontSize: '16px', fontWeight: 700, color: 'white', letterSpacing: '3px',
              textTransform: 'uppercase',
              textShadow: '0 0 30px rgba(192,132,252,0.4), 0 0 60px rgba(192,132,252,0.15)',
            }}>
              R&eacute;seau Neuronal
            </div>
            <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.35)', letterSpacing: '1px', marginTop: '4px' }}>
              {networkData.diagnostic?.total_clusters || 0} clusters &middot; {networkData.diagnostic?.connected || 0} contacts connect&eacute;s
            </div>
          </div>

          {/* Diagnostic stats (top-right) */}
          <div style={{
            position: 'absolute', top: '16px', right: '16px',
            display: 'flex', flexDirection: 'column', gap: '4px',
            animation: 'thought-slide-in 0.5s ease-out 0.2s both',
          }}>
            <div style={{
              padding: '8px 14px', borderRadius: '12px',
              background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(8px)',
              border: '1px solid rgba(192,132,252,0.15)',
            }}>
              <div style={{ fontSize: '9px', color: 'rgba(255,255,255,0.25)', letterSpacing: '1.5px', textTransform: 'uppercase', marginBottom: '6px' }}>
                Diagnostic
              </div>
              <div style={{ display: 'flex', gap: '16px' }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '18px', fontWeight: 700, color: '#c084fc' }}>
                    {networkData.diagnostic?.connected_pct || 0}%
                  </div>
                  <div style={{ fontSize: '8px', color: 'rgba(255,255,255,0.3)' }}>connect&eacute;s</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '18px', fontWeight: 700, color: '#60a5fa' }}>
                    {networkData.diagnostic?.avg_cluster_size || 0}
                  </div>
                  <div style={{ fontSize: '8px', color: 'rgba(255,255,255,0.3)' }}>moy/cluster</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '18px', fontWeight: 700, color: '#34d399' }}>
                    {networkData.diagnostic?.isolated || 0}
                  </div>
                  <div style={{ fontSize: '8px', color: 'rgba(255,255,255,0.3)' }}>isol&eacute;s</div>
                </div>
              </div>
              {networkData.diagnostic?.hub_name && (
                <div style={{ marginTop: '6px', paddingTop: '6px', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                  <div style={{ fontSize: '9px', color: 'rgba(255,255,255,0.25)' }}>Hub principal</div>
                  <div style={{ fontSize: '11px', color: '#fbbf24', fontWeight: 600 }}>
                    {networkData.diagnostic.hub_name} &middot; {networkData.diagnostic.hub_interactions} interactions
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Cluster list (bottom-left) */}
          <div style={{
            position: 'absolute', bottom: '24px', left: '16px',
            display: 'flex', flexDirection: 'column', gap: '3px', pointerEvents: 'auto',
            maxHeight: '240px', overflowY: 'auto',
            animation: 'thought-slide-in 0.5s ease-out 0.3s both',
          }}>
            <div style={{ fontSize: '9px', color: 'rgba(255,255,255,0.25)', letterSpacing: '1.5px', textTransform: 'uppercase', marginBottom: '2px' }}>
              Top clusters &middot; cliquer pour d&eacute;tailler
            </div>
            {(networkData.clusters || []).slice(0, 12).map((cl, i) => {
              const typeColors = { crm_note: '#fbbf24', call: '#60a5fa', email: '#34d399', sms: '#22d3ee', whatsapp: '#a78bfa', document: '#f97316' };
              const tc = typeColors[cl.dominant_type] || '#8b5cf6';
              return (
                <div
                  key={cl.key || i}
                  data-testid={`network-cluster-${i}`}
                  onClick={() => enterClusterDetail(cl)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '6px',
                    padding: '4px 10px', borderRadius: '6px',
                    background: networkHoveredCluster?.key === cl.key ? 'rgba(192,132,252,0.15)' : 'rgba(0,0,0,0.4)',
                    border: networkHoveredCluster?.key === cl.key ? '1px solid rgba(192,132,252,0.3)' : '1px solid transparent',
                    cursor: 'pointer', transition: 'all 0.15s',
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = 'rgba(192,132,252,0.12)'}
                  onMouseLeave={e => e.currentTarget.style.background = networkHoveredCluster?.key === cl.key ? 'rgba(192,132,252,0.15)' : 'rgba(0,0,0,0.4)'}
                >
                  <span style={{
                    width: '6px', height: '6px', borderRadius: '50%',
                    backgroundColor: tc, boxShadow: `0 0 4px ${tc}`,
                    opacity: 0.4 + (1 - i / 12) * 0.6,
                  }} />
                  <span style={{ fontSize: '10px', color: `rgba(255,255,255,${0.35 + (1 - i / 12) * 0.4})`, fontWeight: i < 3 ? 600 : 400 }}>
                    {cl.label}
                  </span>
                  <span style={{ fontSize: '9px', color: 'rgba(255,255,255,0.2)' }}>{cl.count}</span>
                </div>
              );
            })}
          </div>

          {/* Type Legend (bottom-right) */}
          <div style={{
            position: 'absolute', bottom: '24px', right: '16px',
            display: 'flex', flexDirection: 'column', gap: '4px',
            animation: 'thought-slide-in 0.5s ease-out 0.2s both',
          }}>
            <div style={{ fontSize: '9px', color: 'rgba(255,255,255,0.25)', letterSpacing: '1.5px', textTransform: 'uppercase', marginBottom: '2px' }}>
              Couleur dominante
            </div>
            {[
              { label: 'Appels', color: '#60a5fa' },
              { label: 'Emails', color: '#34d399' },
              { label: 'CRM', color: '#fbbf24' },
              { label: 'SMS', color: '#22d3ee' },
              { label: 'WhatsApp', color: '#a78bfa' },
              { label: 'Documents', color: '#f97316' },
            ].map(item => (
              <div key={item.label} style={{
                display: 'flex', alignItems: 'center', gap: '6px',
                padding: '3px 10px', borderRadius: '8px',
                background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)',
              }}>
                <span style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: item.color, boxShadow: `0 0 6px ${item.color}` }} />
                <span style={{ fontSize: '10px', color: 'rgba(255,255,255,0.55)', fontWeight: 500 }}>{item.label}</span>
              </div>
            ))}
          </div>

          {/* Cluster hover tooltip (near cursor) */}
          {networkHoveredCluster && !networkClusterDetail && (
            <div data-testid="network-hover-tooltip" style={{
              position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
              pointerEvents: 'none', animation: 'thought-slide-in 0.2s ease-out',
            }}>
              <div style={{
                padding: '10px 18px', borderRadius: '14px',
                background: 'rgba(2,8,16,0.9)', backdropFilter: 'blur(16px)',
                border: '1px solid rgba(192,132,252,0.2)',
                boxShadow: '0 4px 30px rgba(0,0,0,0.5)',
                textAlign: 'center',
              }}>
                <div style={{ fontSize: '13px', fontWeight: 700, color: 'white', letterSpacing: '0.5px' }}>
                  {networkHoveredCluster.label}
                </div>
                <div style={{ display: 'flex', gap: '12px', marginTop: '6px', justifyContent: 'center' }}>
                  <span style={{ fontSize: '10px', color: '#c084fc' }}>{networkHoveredCluster.count} contacts</span>
                  <span style={{ fontSize: '10px', color: '#60a5fa' }}>{networkHoveredCluster.total_memories} m&eacute;moires</span>
                </div>
                {networkHoveredCluster.top_contacts && networkHoveredCluster.top_contacts.length > 0 && (
                  <div style={{ marginTop: '6px', paddingTop: '6px', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                    {networkHoveredCluster.top_contacts.slice(0, 3).map((n, i) => (
                      <div key={i} style={{ fontSize: '9px', color: 'rgba(255,255,255,0.35)' }}>{n}</div>
                    ))}
                  </div>
                )}
                <div style={{ fontSize: '8px', color: 'rgba(255,255,255,0.15)', marginTop: '4px' }}>
                  Cliquer pour explorer
                </div>
              </div>
            </div>
          )}

          {/* Cluster detail panel (side panel) */}
          {networkClusterDetail && (
            <div data-testid="network-cluster-detail" style={{
              position: 'absolute', top: '60px', right: '16px', bottom: '24px',
              width: '320px', pointerEvents: 'auto',
              animation: 'thought-slide-in 0.3s ease-out',
              display: 'flex', flexDirection: 'column',
            }}>
              <div style={{
                flex: 1, borderRadius: '16px', overflow: 'hidden',
                background: 'rgba(2,8,16,0.92)', backdropFilter: 'blur(20px)',
                border: '1px solid rgba(192,132,252,0.15)',
                boxShadow: '0 8px 40px rgba(0,0,0,0.5)',
                display: 'flex', flexDirection: 'column',
              }}>
                {/* Header */}
                <div style={{
                  padding: '14px 16px', borderBottom: '1px solid rgba(255,255,255,0.06)',
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                }}>
                  <div>
                    <div style={{ fontSize: '14px', fontWeight: 700, color: 'white', letterSpacing: '0.5px' }}>
                      {networkClusterDetail.cluster?.label || 'Cluster'}
                    </div>
                    <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.3)', marginTop: '2px' }}>
                      {networkClusterDetail.contacts?.length || 0} contacts dans ce cluster
                    </div>
                  </div>
                  <span
                    onClick={() => setNetworkClusterDetail(null)}
                    style={{ fontSize: '16px', color: 'rgba(255,255,255,0.3)', cursor: 'pointer', lineHeight: 1 }}
                  >&times;</span>
                </div>

                {/* Contacts list */}
                <div style={{ flex: 1, overflowY: 'auto', padding: '8px 0' }}>
                  {(networkClusterDetail.contacts || []).map((contact, i) => {
                    const typeColors = { crm_note: '#fbbf24', call: '#60a5fa', email: '#34d399', sms: '#22d3ee', whatsapp: '#a78bfa', unknown: '#8b5cf6' };
                    const tc = typeColors[contact.dominant_type] || '#8b5cf6';
                    return (
                      <div
                        key={contact.id || i}
                        data-testid={`network-contact-${i}`}
                        style={{
                          padding: '8px 16px',
                          borderBottom: '1px solid rgba(255,255,255,0.03)',
                          transition: 'background 0.15s',
                        }}
                        onMouseEnter={e => e.currentTarget.style.background = 'rgba(192,132,252,0.06)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span style={{ width: '7px', height: '7px', borderRadius: '50%', backgroundColor: tc, boxShadow: `0 0 6px ${tc}` }} />
                            <span style={{ fontSize: '11px', fontWeight: 600, color: 'rgba(255,255,255,0.8)' }}>
                              {contact.name || 'Inconnu'}
                            </span>
                          </div>
                          <span style={{ fontSize: '9px', color: 'rgba(255,255,255,0.2)' }}>
                            {contact.total_memories} mem.
                          </span>
                        </div>
                        {contact.phone && (
                          <div style={{ fontSize: '9px', color: 'rgba(255,255,255,0.2)', marginLeft: '15px', marginTop: '2px' }}>
                            {contact.phone}
                          </div>
                        )}
                        {contact.type_stats && Object.keys(contact.type_stats).length > 0 && (
                          <div style={{ display: 'flex', gap: '6px', marginLeft: '15px', marginTop: '3px', flexWrap: 'wrap' }}>
                            {Object.entries(contact.type_stats).map(([type, count]) => {
                              const labels = { call: 'Appels', email: 'Emails', crm_note: 'CRM', sms: 'SMS', whatsapp: 'WA' };
                              return (
                                <span key={type} style={{ fontSize: '8px', color: typeColors[type] || '#888', opacity: 0.6 }}>
                                  {labels[type] || type}: {count}
                                </span>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Footer with links info */}
                {networkClusterDetail.links && networkClusterDetail.links.length > 0 && (
                  <div style={{
                    padding: '10px 16px', borderTop: '1px solid rgba(255,255,255,0.06)',
                    fontSize: '9px', color: 'rgba(255,255,255,0.2)',
                  }}>
                    {networkClusterDetail.links.length} connexions intra-cluster d&eacute;tect&eacute;es
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── IRM COGNITIVE: Clone Selector Modal ── */}
      {irmCloneSelector && (
        <div data-testid="irm-clone-selector" style={{
          position: 'absolute', inset: 0, zIndex: 30,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(8px)',
        }}>
          <div style={{
            padding: '24px 32px', borderRadius: '20px',
            background: 'rgba(2,8,16,0.95)', border: '1px solid rgba(192,132,252,0.25)',
            boxShadow: '0 8px 60px rgba(0,0,0,0.6)',
            minWidth: '280px', maxWidth: '360px',
            animation: 'thought-slide-in 0.3s ease-out',
          }}>
            <div style={{ fontSize: '14px', fontWeight: 700, color: 'white', letterSpacing: '2px', textTransform: 'uppercase', marginBottom: '4px', textAlign: 'center' }}>
              IRM Cognitive
            </div>
            <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.3)', textAlign: 'center', marginBottom: '16px' }}>
              Choisissez un clone pour observer sa pens&eacute;e
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {irmClones.map(clone => (
                <button
                  key={clone.id}
                  data-testid={`irm-clone-${clone.name}`}
                  onClick={() => enterIrm(clone.id)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '10px',
                    padding: '10px 16px', borderRadius: '12px',
                    background: 'rgba(139,92,246,0.06)', border: '1px solid rgba(192,132,252,0.15)',
                    color: '#d8b4fe', cursor: 'pointer', fontSize: '13px', fontWeight: 600,
                    transition: 'all 0.2s', pointerEvents: 'auto',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = 'rgba(139,92,246,0.15)'; e.currentTarget.style.borderColor = 'rgba(192,132,252,0.4)'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'rgba(139,92,246,0.06)'; e.currentTarget.style.borderColor = 'rgba(192,132,252,0.15)'; }}
                >
                  <span style={{ width: '28px', height: '28px', borderRadius: '50%', background: 'rgba(192,132,252,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: 700, color: '#c084fc' }}>
                    {(clone.name || '?')[0]}
                  </span>
                  <span>{clone.name}</span>
                </button>
              ))}
            </div>
            <button
              onClick={() => setIrmCloneSelector(false)}
              style={{
                marginTop: '12px', width: '100%', padding: '8px',
                borderRadius: '8px', background: 'transparent', border: '1px solid rgba(255,255,255,0.1)',
                color: 'rgba(255,255,255,0.3)', cursor: 'pointer', fontSize: '11px',
                pointerEvents: 'auto',
              }}
            >
              Annuler
            </button>
          </div>
        </div>
      )}

      {/* ── IRM COGNITIVE: Main Overlay ── */}
      {irmMode && irmData && (
        <div data-testid="irm-overlay" style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 20 }}>
          {/* Back button */}
          <button
            data-testid="irm-back-btn"
            onClick={exitIrm}
            style={{
              position: 'absolute', top: '16px', left: '16px', pointerEvents: 'auto',
              display: 'flex', alignItems: 'center', gap: '8px',
              padding: '8px 18px', borderRadius: '9999px',
              background: 'rgba(0,0,0,0.7)', border: '1px solid rgba(192,132,252,0.3)',
              color: '#d8b4fe', cursor: 'pointer', fontSize: '12px', fontWeight: 600,
              backdropFilter: 'blur(8px)', transition: 'all 0.2s', letterSpacing: '0.5px',
            }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(192,132,252,0.6)'; e.currentTarget.style.background = 'rgba(139,92,246,0.15)'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(192,132,252,0.3)'; e.currentTarget.style.background = 'rgba(0,0,0,0.7)'; }}
          >
            &larr; Retour au cerveau
          </button>

          {/* Title + Clone name + LIVE badge */}
          <div style={{
            position: 'absolute', top: '16px', left: '50%', transform: 'translateX(-50%)',
            textAlign: 'center', animation: 'thought-slide-in 0.6s ease-out',
            transition: 'all 0.5s ease',
          }}>
            <div style={{
              fontSize: irmPresMode ? '18px' : '14px', fontWeight: 700, color: 'white',
              letterSpacing: irmPresMode ? '6px' : '4px', textTransform: 'uppercase',
              textShadow: irmPresMode
                ? '0 0 40px rgba(139,92,246,0.6), 0 0 80px rgba(139,92,246,0.2)'
                : '0 0 30px rgba(139,92,246,0.4), 0 0 60px rgba(139,92,246,0.12)',
              transition: 'all 0.5s ease',
            }}>
              IRM Cognitive
              {irmAsking && (
                <span style={{ marginLeft: '10px', fontSize: '9px', color: '#ef4444', letterSpacing: '1px', animation: 'pulse-dot 1.5s ease-in-out infinite' }}>
                  &#9679; LIVE
                </span>
              )}
            </div>
            <div style={{ fontSize: irmPresMode ? '14px' : '12px', color: '#c084fc', fontWeight: 600, marginTop: '4px', transition: 'all 0.5s ease' }}>
              {irmData.clone?.name}
            </div>
            <div style={{ fontSize: '9px', color: 'rgba(255,255,255,0.2)', marginTop: '2px' }}>
              {irmAsking
                ? <span style={{ color: 'rgba(192,132,252,0.5)' }}>{irmData.clone?.name} analyse...</span>
                : irmResponse && !irmResponse.error
                  ? <span style={{ color: 'rgba(52,211,153,0.5)' }}>{irmData.clone?.name} a mobilis&eacute; {irmResponse.sources_count || 0} source(s)</span>
                  : <>{irmData.clone?.total_conversations} pens&eacute;es analys&eacute;es &middot; {Object.keys(irmData.capabilities || {}).length} capacit&eacute;s</>
              }
            </div>
          </div>

          {/* Presentation mode toggle button (top-right corner) */}
          <button
            data-testid="irm-pres-toggle"
            onClick={() => setIrmPresMode(p => !p)}
            title={irmPresMode ? 'Mode Analyste' : 'Mode Présentation'}
            style={{
              position: 'absolute', top: '16px', right: '16px', pointerEvents: 'auto',
              width: '38px', height: '38px', borderRadius: '50%',
              background: irmPresMode ? 'rgba(139,92,246,0.25)' : 'rgba(0,0,0,0.5)',
              border: irmPresMode ? '1px solid rgba(192,132,252,0.5)' : '1px solid rgba(255,255,255,0.1)',
              color: irmPresMode ? '#d8b4fe' : 'rgba(255,255,255,0.3)',
              cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '16px', transition: 'all 0.3s ease',
              backdropFilter: 'blur(8px)', zIndex: 5,
              boxShadow: irmPresMode ? '0 0 20px rgba(139,92,246,0.2)' : 'none',
            }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(192,132,252,0.6)'; e.currentTarget.style.color = '#d8b4fe'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = irmPresMode ? 'rgba(192,132,252,0.5)' : 'rgba(255,255,255,0.1)'; e.currentTarget.style.color = irmPresMode ? '#d8b4fe' : 'rgba(255,255,255,0.3)'; }}
          >
            {irmPresMode ? (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="3" width="20" height="14" rx="2" /><line x1="8" y1="21" x2="16" y2="21" /><line x1="12" y1="17" x2="12" y2="21" /></svg>
            ) : (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>
            )}
          </button>

          {/* Clone switcher (top-left, below back) — hidden in presentation mode */}
          {!irmPresMode && (
          <div style={{
            position: 'absolute', top: '56px', left: '16px',
            animation: 'thought-slide-in 0.5s ease-out 0.2s both',
          }}>
            {irmClones.filter(c => c.id !== irmSelectedClone).map(clone => (
              <button
                key={clone.id}
                data-testid={`irm-switch-${clone.name}`}
                onClick={() => switchIrmClone(clone.id)}
                style={{
                  display: 'flex', alignItems: 'center', gap: '6px',
                  padding: '5px 12px', borderRadius: '9999px', marginBottom: '4px',
                  background: 'rgba(0,0,0,0.5)', border: '1px solid rgba(192,132,252,0.12)',
                  color: 'rgba(255,255,255,0.4)', cursor: 'pointer', fontSize: '10px', fontWeight: 500,
                  transition: 'all 0.2s', pointerEvents: 'auto',
                }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(192,132,252,0.35)'; e.currentTarget.style.color = '#d8b4fe'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(192,132,252,0.12)'; e.currentTarget.style.color = 'rgba(255,255,255,0.4)'; }}
              >
                <span style={{ width: '16px', height: '16px', borderRadius: '50%', background: 'rgba(192,132,252,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '8px', fontWeight: 700, color: '#c084fc' }}>
                  {(clone.name || '?')[0]}
                </span>
                {clone.name}
              </button>
            ))}
          </div>
          )}

          {/* Behaviour panel (top-right) — glassmorphism — hidden in presentation mode */}
          {!irmPresMode && (
          <div style={{
            position: 'absolute', top: '16px', right: '56px',
            animation: 'thought-slide-in 0.5s ease-out 0.3s both',
          }}>
            <div style={{
              padding: '14px 18px', borderRadius: '16px',
              background: 'rgba(5,2,18,0.65)', backdropFilter: 'blur(16px)',
              border: '1px solid rgba(139,92,246,0.2)',
              boxShadow: '0 0 40px rgba(139,92,246,0.06), inset 0 0 30px rgba(139,92,246,0.03)',
              maxWidth: '230px',
            }}>
              <div style={{ fontSize: '8px', color: 'rgba(255,255,255,0.2)', letterSpacing: '2px', textTransform: 'uppercase', marginBottom: '10px' }}>
                ADN du Clone
              </div>
              {irmData.behaviour?.style?.tone && (
                <div style={{ marginBottom: '8px' }}>
                  <div style={{ fontSize: '8px', color: 'rgba(255,255,255,0.25)', marginBottom: '4px' }}>Ton</div>
                  <div style={{ height: '4px', borderRadius: '2px', background: 'linear-gradient(90deg, #3b82f6, #8b5cf6, #fbbf24)', position: 'relative' }}>
                    <div style={{ position: 'absolute', top: '-3px', left: irmData.behaviour.style.tone === 'professionnel' ? '50%' : irmData.behaviour.style.tone === 'chaleureux' ? '75%' : '30%', width: '10px', height: '10px', borderRadius: '50%', background: '#fff', border: '2px solid #8b5cf6', transform: 'translateX(-50%)' }} />
                  </div>
                  <div style={{ fontSize: '9px', color: '#d8b4fe', fontWeight: 600, marginTop: '6px', textAlign: 'center' }}>{irmData.behaviour.style.tone}</div>
                </div>
              )}
              {irmData.behaviour?.style?.formality && (
                <div style={{ fontSize: '9px', color: '#60a5fa', marginBottom: '6px', padding: '3px 8px', borderRadius: '6px', background: 'rgba(96,165,250,0.08)', border: '1px solid rgba(96,165,250,0.12)', display: 'inline-block' }}>
                  {irmData.behaviour.style.formality}
                </div>
              )}
              {irmData.behaviour?.signature_phrases?.length > 0 && (
                <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid rgba(255,255,255,0.04)' }}>
                  <div style={{ fontSize: '7px', color: 'rgba(255,255,255,0.15)', letterSpacing: '1px', marginBottom: '5px', textTransform: 'uppercase' }}>Expressions signature</div>
                  {irmData.behaviour.signature_phrases.slice(0, 3).map((p, i) => (
                    <div key={i} style={{
                      fontSize: '9px', color: 'rgba(255,255,255,0.4)', fontStyle: 'italic',
                      padding: '4px 8px', marginBottom: '3px', borderRadius: '8px',
                      background: 'rgba(139,92,246,0.06)', borderLeft: '2px solid rgba(192,132,252,0.2)',
                    }}>"{p}"</div>
                  ))}
                </div>
              )}
              {irmData.behaviour?.patterns?.length > 0 && (
                <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid rgba(255,255,255,0.04)' }}>
                  <div style={{ fontSize: '7px', color: 'rgba(255,255,255,0.15)', letterSpacing: '1px', marginBottom: '5px', textTransform: 'uppercase' }}>Patterns</div>
                  {irmData.behaviour.patterns.slice(0, 3).map((p, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                      <div style={{ width: '28px', height: '28px', borderRadius: '50%', position: 'relative', flexShrink: 0 }}>
                        <svg width="28" height="28" viewBox="0 0 28 28"><circle cx="14" cy="14" r="11" fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="2.5" /><circle cx="14" cy="14" r="11" fill="none" stroke="#34d399" strokeWidth="2.5" strokeDasharray={`${p.confidence * 69} 69`} strokeLinecap="round" transform="rotate(-90 14 14)" /></svg>
                      </div>
                      <span style={{ fontSize: '8px', color: 'rgba(255,255,255,0.35)', lineHeight: 1.3 }}>{p.pattern}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          )}

          {/* Capabilities — Radar chart (bottom-right) — hidden in presentation mode */}
          {!irmPresMode && (
          <div style={{
            position: 'absolute', bottom: '24px', right: '16px',
            animation: 'thought-slide-in 0.5s ease-out 0.4s both',
          }}>
            <div style={{
              padding: '14px', borderRadius: '16px',
              background: 'rgba(5,2,18,0.65)', backdropFilter: 'blur(16px)',
              border: '1px solid rgba(139,92,246,0.2)',
              boxShadow: '0 0 40px rgba(139,92,246,0.06), inset 0 0 30px rgba(139,92,246,0.03)',
              width: '200px',
            }}>
              <div style={{ fontSize: '8px', color: 'rgba(255,255,255,0.2)', letterSpacing: '2px', textTransform: 'uppercase', marginBottom: '8px', textAlign: 'center' }}>
                Empreinte Cognitive
              </div>
              {(() => {
                const entries = Object.entries(irmData.capabilities || {});
                if (entries.length === 0) return null;
                const cx = 90, cy = 90, maxR = 70;
                const familyColors = { comprehension: '#60a5fa', analysis: '#22d3ee', personality: '#fbbf24', detection: '#34d399', collaboration: '#a78bfa' };
                const points = entries.map(([, cap], i) => {
                  const angle = (i / entries.length) * Math.PI * 2 - Math.PI / 2;
                  const r = Math.max(cap.weight || 0, 0.05) * maxR;
                  return { x: cx + r * Math.cos(angle), y: cy + r * Math.sin(angle), angle, cap, fullR: maxR };
                });
                const polygon = points.map(p => `${p.x},${p.y}`).join(' ');
                return (
                  <svg width="180" height="180" viewBox="0 0 180 180" style={{ display: 'block', margin: '0 auto' }}>
                    {/* Grid rings */}
                    {[0.25, 0.5, 0.75, 1].map(r => (
                      <circle key={r} cx={cx} cy={cy} r={maxR * r} fill="none" stroke="rgba(255,255,255,0.04)" strokeWidth="0.5" />
                    ))}
                    {/* Axis lines */}
                    {entries.map(([, cap], i) => {
                      const angle = (i / entries.length) * Math.PI * 2 - Math.PI / 2;
                      return <line key={i} x1={cx} y1={cy} x2={cx + maxR * Math.cos(angle)} y2={cy + maxR * Math.sin(angle)} stroke="rgba(255,255,255,0.05)" strokeWidth="0.5" />;
                    })}
                    {/* Filled polygon */}
                    <polygon points={polygon} fill="rgba(139,92,246,0.12)" stroke="rgba(192,132,252,0.5)" strokeWidth="1.5" strokeLinejoin="round" />
                    {/* Dots on vertices */}
                    {points.map((p, i) => {
                      const c = familyColors[entries[i][1].family] || '#8b5cf6';
                      return <circle key={i} cx={p.x} cy={p.y} r="3" fill={c} opacity="0.8" />;
                    })}
                    {/* Labels */}
                    {entries.map(([key, cap], i) => {
                      const angle = (i / entries.length) * Math.PI * 2 - Math.PI / 2;
                      const lx = cx + (maxR + 12) * Math.cos(angle);
                      const ly = cy + (maxR + 12) * Math.sin(angle);
                      const c = familyColors[cap.family] || '#8b5cf6';
                      const anchor = Math.abs(Math.cos(angle)) < 0.3 ? 'middle' : Math.cos(angle) > 0 ? 'start' : 'end';
                      return <text key={key} x={lx} y={ly} fill={c} fontSize="5.5" fontWeight="600" textAnchor={anchor} dominantBaseline="middle" opacity="0.7">{(cap.label || key).slice(0, 14)}</text>;
                    })}
                  </svg>
                );
              })()}
            </div>
          </div>
          )}

          {/* Thought replay (bottom-left, compact) — hidden in presentation mode */}
          {!irmPresMode && irmReplayIdx >= 0 && irmData.recent_thoughts?.[irmReplayIdx] && (
            <div style={{
              position: 'absolute', bottom: '20px', left: '16px',
              animation: 'thought-slide-in 0.3s ease-out', pointerEvents: 'none',
              maxWidth: '300px', width: '25%', minWidth: '220px',
            }}>
              <div style={{
                padding: '10px 14px', borderRadius: '14px',
                background: 'rgba(5,2,18,0.7)', backdropFilter: 'blur(12px)',
                border: irmData.recent_thoughts[irmReplayIdx]?.live
                  ? '1px solid rgba(239,68,68,0.25)' : '1px solid rgba(255,255,255,0.06)',
                boxShadow: irmData.recent_thoughts[irmReplayIdx]?.live
                  ? '0 0 25px rgba(239,68,68,0.08)' : '0 4px 20px rgba(0,0,0,0.3)',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '5px' }}>
                  <span style={{ fontSize: '7px', letterSpacing: '1.5px', textTransform: 'uppercase',
                    color: irmData.recent_thoughts[irmReplayIdx]?.live ? '#ef4444' : 'rgba(255,255,255,0.15)',
                    fontWeight: irmData.recent_thoughts[irmReplayIdx]?.live ? 700 : 500,
                  }}>
                    {irmData.recent_thoughts[irmReplayIdx]?.live
                      ? <>&#9679; LIVE</>
                      : <>REPLAY {irmReplayIdx + 1}/{irmData.recent_thoughts.length}</>
                    }
                  </span>
                  <span style={{ fontSize: '7px', color: 'rgba(255,255,255,0.1)' }}>
                    {irmData.recent_thoughts[irmReplayIdx].response_time_ms}ms
                  </span>
                </div>
                <div style={{ fontSize: '10px', color: irmData.recent_thoughts[irmReplayIdx]?.live ? '#f0abfc' : '#c084fc', fontWeight: 600, marginBottom: '3px', lineHeight: 1.3 }}>
                  {irmData.recent_thoughts[irmReplayIdx].message?.slice(0, 80)}{irmData.recent_thoughts[irmReplayIdx].message?.length > 80 ? '...' : ''}
                </div>
                <div style={{ fontSize: '9px', color: 'rgba(255,255,255,0.25)', fontStyle: 'italic', lineHeight: 1.3 }}>
                  {irmData.recent_thoughts[irmReplayIdx].response_preview?.slice(0, 100)}
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '3px', marginTop: '5px' }}>
                  {(irmData.recent_thoughts[irmReplayIdx].activated || []).slice(0, 5).map(cap => {
                    const familyColors = { comprehension: '#60a5fa', analysis: '#22d3ee', personality: '#fbbf24', detection: '#34d399', collaboration: '#a78bfa' };
                    const capData = irmData.capabilities?.[cap] || {};
                    const c = familyColors[capData.family] || '#8b5cf6';
                    return (
                      <span key={cap} style={{
                        fontSize: '6px', padding: '1px 5px', borderRadius: '3px',
                        background: `${c}12`, border: `1px solid ${c}25`, color: c,
                        fontWeight: 600, letterSpacing: '0.3px',
                      }}>
                        {capData.label || cap}
                      </span>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* ── Holographic response (presentation mode only) ── */}
          {irmPresMode && irmResponse && (
            <div data-testid="irm-pres-response" style={{
              position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
              maxWidth: '520px', width: '60%', pointerEvents: 'auto',
              animation: 'thought-slide-in 0.5s ease-out',
            }}>
              <div style={{
                padding: '20px 26px', borderRadius: '20px',
                background: 'rgba(5,2,18,0.6)', backdropFilter: 'blur(20px)',
                border: irmResponse.error ? '1px solid rgba(239,68,68,0.2)' : '1px solid rgba(139,92,246,0.3)',
                boxShadow: irmResponse.error
                  ? '0 0 40px rgba(239,68,68,0.08)'
                  : '0 0 60px rgba(139,92,246,0.15), 0 0 120px rgba(139,92,246,0.05)',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                  <div style={{ fontSize: '11px', color: '#c084fc', fontWeight: 600, flex: 1 }}>
                    {irmResponse.question}
                  </div>
                  <button
                    data-testid="irm-close-response"
                    onClick={() => { setIrmResponse(null); setIrmTypewriterText(''); if (irmTypewriterRef.current) clearInterval(irmTypewriterRef.current); }}
                    style={{
                      background: 'none', border: 'none', color: 'rgba(255,255,255,0.3)',
                      cursor: 'pointer', fontSize: '18px', lineHeight: 1, padding: '0 4px',
                      flexShrink: 0, transition: 'color 0.2s',
                    }}
                    onMouseEnter={e => e.currentTarget.style.color = 'rgba(255,255,255,0.6)'}
                    onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.3)'}
                  >
                    &times;
                  </button>
                </div>
                <div style={{
                  fontSize: '13px', color: irmResponse.error ? '#f87171' : 'rgba(255,255,255,0.65)',
                  lineHeight: 1.7, whiteSpace: 'pre-wrap', maxHeight: '250px', overflowY: 'auto',
                }}>
                  {irmTypewriterText || irmResponse.response}
                  {irmTypewriterText && irmTypewriterText.length < (irmResponse.response || '').length && (
                    <span style={{ opacity: 0.5, animation: 'pulse-dot 0.8s ease-in-out infinite' }}>|</span>
                  )}
                </div>
                {irmResponse.sources_count > 0 && (
                  <div style={{ fontSize: '8px', color: 'rgba(255,255,255,0.12)', marginTop: '10px', borderTop: '1px solid rgba(255,255,255,0.04)', paddingTop: '6px' }}>
                    {irmResponse.sources_count} source(s) utilis&eacute;e(s)
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── IRM Interactive Chat (bottom-center) ── */}
          <div data-testid="irm-chat-zone" style={{
            position: 'absolute', bottom: '20px', left: '50%', transform: 'translateX(-50%)',
            maxWidth: '480px', width: '40%', minWidth: '340px', pointerEvents: 'auto',
            animation: 'thought-slide-in 0.6s ease-out 0.6s both',
          }}>
            {/* Suggested questions */}
            {!irmResponse && !irmAsking && irmData.suggested_questions?.length > 0 && (
              <div data-testid="irm-suggested-questions" style={{ display: 'flex', gap: '6px', justifyContent: 'center', flexWrap: 'wrap', marginBottom: '10px' }}>
                {irmData.suggested_questions.map((q, i) => (
                  <button
                    key={i}
                    data-testid={`irm-suggested-q-${i}`}
                    onClick={() => askIrmQuestion(q.text)}
                    style={{
                      padding: '7px 16px', borderRadius: '20px',
                      background: 'rgba(139,92,246,0.08)', border: '1px solid rgba(192,132,252,0.18)',
                      color: '#d8b4fe', fontSize: '10px', fontWeight: 600,
                      cursor: 'pointer', transition: 'all 0.3s ease',
                      backdropFilter: 'blur(8px)', letterSpacing: '0.3px',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.background = 'rgba(139,92,246,0.2)'; e.currentTarget.style.borderColor = 'rgba(192,132,252,0.45)'; e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 4px 20px rgba(139,92,246,0.15)'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'rgba(139,92,246,0.08)'; e.currentTarget.style.borderColor = 'rgba(192,132,252,0.18)'; e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}
                  >
                    {q.label} &rarr;
                  </button>
                ))}
              </div>
            )}

            {/* Thinking indicator — with pulsing rings */}
            {irmAsking && (
              <div data-testid="irm-thinking" style={{
                padding: '14px 20px', borderRadius: '16px',
                background: 'rgba(5,2,18,0.85)', backdropFilter: 'blur(16px)',
                border: '1px solid rgba(139,92,246,0.3)',
                marginBottom: '10px', textAlign: 'center',
                boxShadow: '0 0 50px rgba(139,92,246,0.12)',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
                  <div style={{ position: 'relative', width: '20px', height: '20px' }}>
                    <div style={{ position: 'absolute', inset: 0, borderRadius: '50%', border: '2px solid rgba(192,132,252,0.3)', animation: 'irm-ring-pulse 1.5s ease-out infinite' }} />
                    <div style={{ position: 'absolute', inset: '4px', borderRadius: '50%', border: '1.5px solid rgba(192,132,252,0.5)', animation: 'irm-ring-pulse 1.5s ease-out 0.3s infinite' }} />
                    <div style={{ position: 'absolute', inset: '8px', borderRadius: '50%', background: '#c084fc' }} />
                  </div>
                  <span style={{ color: '#d8b4fe', fontSize: '12px', fontWeight: 600, letterSpacing: '0.5px' }}>Le clone r&eacute;fl&eacute;chit</span>
                </div>
                <div style={{ fontSize: '9px', color: 'rgba(255,255,255,0.2)', marginTop: '6px' }}>
                  Observez les neurones s'activer en temps r&eacute;el...
                </div>
              </div>
            )}

            {/* Response display — holographic in pres mode, card otherwise */}
            {irmResponse && !irmPresMode && (
              <div data-testid="irm-chat-response" style={{
                padding: '14px 18px', borderRadius: '16px',
                background: 'rgba(5,2,18,0.85)', backdropFilter: 'blur(16px)',
                border: irmResponse.error ? '1px solid rgba(239,68,68,0.25)' : '1px solid rgba(139,92,246,0.25)',
                boxShadow: irmResponse.error ? '0 0 30px rgba(239,68,68,0.06)' : '0 0 40px rgba(139,92,246,0.1)',
                marginBottom: '10px', maxHeight: '180px', overflowY: 'auto',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                  <div style={{ fontSize: '10px', color: '#c084fc', fontWeight: 600, flex: 1, marginRight: '8px' }}>
                    {irmResponse.question}
                  </div>
                  <button
                    data-testid="irm-close-response"
                    onClick={() => { setIrmResponse(null); if (irmTypewriterRef.current) clearInterval(irmTypewriterRef.current); }}
                    style={{
                      background: 'none', border: 'none', color: 'rgba(255,255,255,0.25)',
                      cursor: 'pointer', fontSize: '16px', lineHeight: 1, padding: '0 2px',
                      flexShrink: 0, transition: 'color 0.2s',
                    }}
                    onMouseEnter={e => e.currentTarget.style.color = 'rgba(255,255,255,0.6)'}
                    onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.25)'}
                  >
                    &times;
                  </button>
                </div>
                <div style={{
                  fontSize: '11px', color: irmResponse.error ? '#f87171' : 'rgba(255,255,255,0.55)',
                  lineHeight: 1.6, whiteSpace: 'pre-wrap',
                }}>
                  {irmResponse.response}
                </div>
                {irmResponse.sources_count > 0 && (
                  <div style={{ fontSize: '8px', color: 'rgba(255,255,255,0.12)', marginTop: '8px', borderTop: '1px solid rgba(255,255,255,0.04)', paddingTop: '6px' }}>
                    {irmResponse.sources_count} source(s) utilis&eacute;e(s)
                  </div>
                )}
              </div>
            )}

            {/* Input bar — terminal neural */}
            <form
              data-testid="irm-chat-form"
              onSubmit={(e) => { e.preventDefault(); askIrmQuestion(irmQuestion); }}
              style={{
                display: 'flex', gap: '8px', alignItems: 'center',
                padding: '10px 16px', borderRadius: '14px',
                background: 'rgba(5,2,18,0.75)', backdropFilter: 'blur(16px)',
                border: '1px solid rgba(139,92,246,0.15)',
                boxShadow: '0 4px 30px rgba(0,0,0,0.3), 0 0 20px rgba(139,92,246,0.04)',
                transition: 'border-color 0.3s, box-shadow 0.3s',
              }}
            >
              <input
                data-testid="irm-chat-input"
                type="text"
                value={irmQuestion}
                onChange={e => setIrmQuestion(e.target.value)}
                placeholder="Posez une question au clone..."
                disabled={irmAsking}
                style={{
                  flex: 1, background: 'transparent', border: 'none', outline: 'none',
                  color: 'white', fontSize: '12px', fontFamily: 'inherit',
                  opacity: irmAsking ? 0.3 : 1,
                }}
                onFocus={e => { e.target.closest('form').style.borderColor = 'rgba(139,92,246,0.35)'; e.target.closest('form').style.boxShadow = '0 4px 30px rgba(0,0,0,0.3), 0 0 30px rgba(139,92,246,0.08)'; }}
                onBlur={e => { e.target.closest('form').style.borderColor = 'rgba(139,92,246,0.15)'; e.target.closest('form').style.boxShadow = '0 4px 30px rgba(0,0,0,0.3), 0 0 20px rgba(139,92,246,0.04)'; }}
              />
              <button
                data-testid="irm-chat-submit"
                type="submit"
                disabled={irmAsking || !irmQuestion.trim()}
                style={{
                  padding: '7px 18px', borderRadius: '9999px',
                  background: irmAsking || !irmQuestion.trim() ? 'rgba(139,92,246,0.1)' : 'linear-gradient(135deg, rgba(139,92,246,0.5), rgba(192,132,252,0.4))',
                  border: '1px solid rgba(192,132,252,0.25)',
                  color: irmAsking || !irmQuestion.trim() ? 'rgba(255,255,255,0.2)' : '#fff',
                  fontSize: '11px', fontWeight: 600,
                  cursor: irmAsking || !irmQuestion.trim() ? 'not-allowed' : 'pointer',
                  transition: 'all 0.3s', flexShrink: 0,
                }}
              >
                {irmAsking ? '...' : 'Envoyer'}
              </button>
            </form>
          </div>

          {/* Neuron labels in 3D (projected) — in pres mode, only show activated caps */}
          {irmRef.current && Object.entries(irmRef.current.neuronMap || {}).map(([key, n]) => {
            const capData = irmData.capabilities?.[key] || {};
            if (!n || !n.pos || !sceneRef.current?.camera) return null;
            // In presentation mode, only show labels for currently activated capabilities
            if (irmPresMode && !irmActivatedCaps.includes(key)) return null;
            const vec = n.pos.clone().project(sceneRef.current.camera);
            if (vec.z > 1) return null;
            const canvas = canvasRef.current;
            if (!canvas) return null;
            const sx = (vec.x * 0.5 + 0.5) * canvas.clientWidth;
            const sy = (-vec.y * 0.5 + 0.5) * canvas.clientHeight;
            const familyColors = { comprehension: '#60a5fa', analysis: '#22d3ee', personality: '#fbbf24', detection: '#34d399', collaboration: '#a78bfa' };
            const c = familyColors[capData.family] || '#8b5cf6';
            const isActive = irmActivatedCaps.includes(key);
            return (
              <div key={key} style={{
                position: 'absolute', left: `${sx}px`, top: `${sy + 22}px`,
                transform: 'translate(-50%, 0)', pointerEvents: 'none',
                whiteSpace: 'nowrap', textAlign: 'center',
                transition: 'opacity 0.5s ease',
                opacity: isActive ? 1 : (irmPresMode ? 0 : 1),
              }}>
                <div style={{
                  fontSize: isActive ? '9px' : '8px', fontWeight: 600, color: c,
                  opacity: isActive ? 1 : (n.weight > 0.05 ? 0.6 + n.weight * 0.4 : 0.2),
                  textShadow: isActive ? `0 0 15px ${c}80, 0 0 30px ${c}40` : `0 0 10px ${c}40`,
                  letterSpacing: '0.5px',
                  transition: 'all 0.3s ease',
                }}>
                  {capData.label || key}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <style>{`
        @keyframes pulse-dot {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.4; transform: scale(0.7); }
        }
        @keyframes thought-slide-in {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes irm-ring-pulse {
          0% { transform: scale(1); opacity: 0.6; }
          100% { transform: scale(2); opacity: 0; }
        }
      `}</style>
    </div>
  );
}
